import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Alert, Form } from "react-bootstrap";
import i18n from "i18next";
import { getToken } from "../../../common/environment_config";
import { assetKey } from "../../../common/utils";
import { fetchIdealStudioFiles, fetchFileVersions, approveJob } from "../../../common/server_utils";
import { onJobsSettled } from "../../utils/jobSettled";
import AppModal from "../common/modal/AppModal";
import SceneAudioService from "../../services/sceneAudioService";
import HistoryTab from "../add_image_modal/HistoryTab";
import ImageTextSelector from "./ImageTextSelector";
import ImageRegionSelector from "./ImageRegionSelector";
import Lightbox from "./Lightbox";
import AnimationEditMode from "./AnimationEditMode";
import { belowImageBtnStyle } from "./merlinStyles";
import { pickAnimationApplyUrl } from "../../utils/pickAnimationApplyUrl.es6";
import { isFileTooLarge, fileTooLargeMessage } from "../../../common/upload_limits.es6";
import {
  supportedDurationsForProvider,
  defaultDurationForWindow,
} from "../../utils/animationDurationOptions.es6";
import {
  buildAnimateJobSettings,
  resolveAnimateDisplaySettings,
} from "../../utils/animateJobSettings.es6";
import { resolveBoxTiming } from "../../../common/resolveBoxTiming.es6";
import { getSceneDurationSec } from "../../../common/sceneTimeline.es6";
import {
  AI_BLOCK,
  classifyAiProviderError,
  mapAiProviderErrorMessage,
} from "../../../common/ai_provider_errors.es6";
import {
  getPluginImageUrl,
  extractContentImageUrls,
  extractBackgroundImageUrl,
} from "../../utils/collectSceneImages.es6";
import {
  LuInfo,
  LuArrowLeft,
  LuArrowRight,
  LuClapperboard,
  LuImageOff,
  LuEraser,
  LuRemoveFormatting,
  LuLanguages,
  LuCopy,
  LuWandSparkles,
  LuPencil,
  LuScanLine,
  LuZoomIn,
  LuReplace,
  LuLayoutTemplate,
  LuImage,
  LuVideo,
  LuLoaderCircle,
  LuCircleX,
  LuCheck,
  LuX,
  LuRefreshCw,
  LuHandCoins,
  LuHistory,
  LuLayers,
  LuScrollText,
  LuLibrary,
  LuScanText,
  LuMessagesSquare,
  LuChevronDown,
  LuSparkles,
  LuTimer,
  LuRepeat,
  LuScissors,
  LuPlay,
} from "react-icons/lu";
import { startInlineEditSession } from "../ai-chat/inlineEditSession";
import { openInDirector } from "../carousel/audio/video-director/openInDirector";
import { isCreditRefusal } from "../../utils/insufficientBalance";
// Leaf module (no imports): area_ops/areaOpsSubmit imports this file, so the
// batch label helpers must not come through areaOpsBatch.js.
import { AREA_OPS_BATCH_OPERATION, formatAreaOpsBatchLabel } from "./area_ops/areaOpsBatchLabel";

// Module-level set — survives component remounts, prevents duplicate polling
const activePollingJobs = new Set();

// Backend (gemini-animation-provider-v2) prefixes RAI blocks with a stable
// machine code (other providers use free text — see ai_provider_errors.es6)
// so we can show a localized message instead of Google's raw (English,
// support-code) reason. Anything else falls through to the raw backend
// message (already localized, e.g. the maintenance notice) or the generic
// fallback.
function mapAnimateErrorMessage(raw) {
  const kind = classifyAiProviderError(raw);
  if (kind === AI_BLOCK.COPYRIGHT) {
    return i18n.t("AnimationCopyrightBlocked");
  }
  if (kind === AI_BLOCK.SAFETY) {
    return i18n.t("AnimationSafetyBlocked");
  }
  return raw || i18n.t("ImageAnimationFailedGenericMessage");
}

// Image-editing endpoints return the result URL in different fields depending on
// the operation: translate / change-image-text / edit put it in
// `result.images[].url` or `result.url`, generate-similar uses
// `result.details.generatedImages[].url`. Collect them in the same priority the
// rest of the app uses (see useSessionImageJobs + imagePromptLibraryService) so
// the Result panel always finds a URL — otherwise the edited image never shows.
export function extractEditedImageUrls(payload) {
  if (!payload || typeof payload !== "object") return [];
  const result = (payload.result && typeof payload.result === "object" && payload.result) || {};
  const metadata =
    (payload.metadata && typeof payload.metadata === "object" && payload.metadata) || {};
  const urls = [];
  const pushFromArray = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const item of arr) {
      if (typeof item === "string") urls.push(item);
      else if (item && typeof item === "object" && typeof item.url === "string")
        urls.push(item.url);
    }
  };
  pushFromArray(result.images);
  pushFromArray(result.details?.generatedImages);
  if (typeof result.url === "string") urls.push(result.url);
  if (typeof result.imageUrl === "string") urls.push(result.imageUrl);
  pushFromArray(metadata.generatedImages);
  if (typeof metadata.imageUrl === "string") urls.push(metadata.imageUrl);
  if (typeof payload.resultUrl === "string") urls.push(payload.resultUrl);
  const seen = new Set();
  const out = [];
  for (const u of urls) {
    const v = typeof u === "string" ? u.trim() : "";
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

const PROVIDER_OPTIONS = [
  { value: "pixverse", label: "Pixverse", disabled: false },
  { value: "grok", label: "Grok", disabled: false },
  { value: "gemini-fast", label: "Gemini Fast", disabled: false },
  { value: "gemini", label: "Gemini", disabled: false },
  // fal.ai image-to-video providers. Duration options come from the shared
  // supportedDurationsForProvider util (kling 5/10/15, ltx 6/8/10, seedance
  // 5/8/12, hailuo fixed-length → no duration chips), so the dropdown here
  // auto-adjusts per provider without any extra wiring.
  { value: "kling", label: "Kling", disabled: false },
  { value: "ltx", label: "LTX", disabled: false },
  { value: "seedance", label: "Seedance", disabled: false },
  { value: "hailuo", label: "Hailuo", disabled: false },
];

// Metadata pill for the already-animated-box collapsed settings summary.
const summaryChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  borderRadius: 999,
  border: "1px solid #e2e8f0",
  background: "#fff",
  color: "#475569",
  fontSize: 11,
  fontWeight: 600,
};

// Ghost chip for the compact animate settings toolbar (provider / süre /
// döngü), rendered inline in the Prompt label row. Hover/focus styles live
// in styleTag (.animChip rules).
const animChipStyle = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "5px 8px",
  borderRadius: 7,
  border: "none",
  background: "transparent",
  color: "#334155",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  userSelect: "none",
};

// Transparent native <select> stretched over the whole chip — the entire
// icon+label+caret row is the hit area and opens the native dropdown (no
// custom popup, no new dependency, no popup-over-popup).
const chipSelectOverlayStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  border: "none",
};

// Subtle divider between chip groups.
const chipDividerStyle = {
  width: 1,
  height: 16,
  background: "#e2e8f0",
  margin: "0 4px",
  flex: "none",
};

const LANGUAGE_OPTIONS = [
  { value: "Turkish", labelKey: "Turkish" },
  { value: "English", labelKey: "English" },
  { value: "Spanish", labelKey: "Spanish" },
  { value: "French", labelKey: "French" },
  { value: "German", labelKey: "German" },
  { value: "Azerbaijani", labelKey: "Azerbaijani" },
  { value: "Arabic", labelKey: "Arabic" },
  { value: "Russian", labelKey: "Russian" },
  { value: "Japanese", labelKey: "Japanese" },
  { value: "Portuguese", labelKey: "Portuguese" },
];

const LOCATION_OPTIONS = [
  { value: "top-left", labelKey: "Top Left" },
  { value: "top-center", labelKey: "Top Center" },
  { value: "top-right", labelKey: "Top Right" },
  { value: "middle-left", labelKey: "Middle Left" },
  { value: "center", labelKey: "Center" },
  { value: "middle-right", labelKey: "Middle Right" },
  { value: "bottom-left", labelKey: "Bottom Left" },
  { value: "bottom-center", labelKey: "Bottom Center" },
  { value: "bottom-right", labelKey: "Bottom Right" },
  { value: "custom", labelKey: "Custom" },
];

const STATUS_COLORS = {
  pending: "#ffc107",
  processing: "#17a2b8",
  completed: "#28a745",
  failed: "#dc3545",
};

const ASPECT_RATIO_OPTIONS = [
  {
    value: "1:1",
    label: "1:1",
    style: { width: "36px", height: "36px" },
    prompt:
      "Use generative fill to outpaint this image into a 1:1 aspect ratio. Keep the original central content exactly as it is without any distortion or scaling. Seamlessly extend the surrounding environment to fill the entire square frame. Ensure the newly generated parts blend perfectly with the original lighting and textures. Aspect ratio: 1:1",
  },
  {
    value: "4:3",
    label: "4:3",
    style: { width: "48px", height: "36px" },
    prompt:
      "Use generative fill to outpaint this image into a 4:3 aspect ratio. Keep the original central content exactly as it is without any distortion or scaling. Seamlessly extend the left and right sides of the environment to fill the wider horizontal frame. Ensure the newly generated parts blend perfectly with the original lighting and textures. Aspect ratio: 4:3",
  },
  {
    value: "4:5",
    label: "4:5",
    style: { width: "36px", height: "45px" },
    prompt:
      "Use generative fill to outpaint this image into a 4:5 aspect ratio. Keep the original central content exactly as it is without any distortion or scaling. Seamlessly extend the top and bottom of the environment to fill the taller vertical frame. Ensure the newly generated parts blend perfectly with the original lighting and textures. Aspect ratio: 4:5",
  },
  {
    value: "16:9",
    label: "16:9",
    style: { width: "56px", height: "32px" },
    prompt:
      "Use generative fill to outpaint this image into a 16:9 aspect ratio. Keep the original central content exactly as it is without any distortion or scaling. Seamlessly extend the left and right environment to fill the entire widescreen frame. Ensure the newly generated parts blend perfectly with the original lighting and textures. Aspect ratio: 16:9",
  },
  {
    value: "9:16",
    label: "9:16",
    style: { width: "32px", height: "56px" },
    prompt:
      "Use generative fill to outpaint this image into a 9:16 aspect ratio. Keep the original central content exactly as it is without any distortion or scaling. Seamlessly extend the top and bottom environment to fill the entire vertical frame. Ensure the newly generated parts blend perfectly with the original lighting and textures. Aspect ratio: 9:16",
  },
];

// Sıralama = AI Wizard menü kartlarının görünüm sırası (MerlinFeaturesPanel bu
// diziyi olduğu gibi map'ler). İlk 5 slot istenen sıraya sabitlendi:
// 1) Görseli Düzenle 2) Metni Ayır (extract-text) 3) Metni Değiştir (change-image-text)
// 4) Objeyi Referansla Değiştir 5) Aidea Akıllı 6) Görseli Canlandır — geri kalanlar özgün sıralarını korur.
/**
 * ORİJİNAL / SONUÇ önizleme kutularının ebadı.
 *
 * Yükseklik artık "formdan artan yer" değil, panelin genişliğinden türeyen sabit
 * bir oran. Sebebi: her feature'ın formu farklı yükseklikte (prompt textarea'sı
 * olan "Görseli Düzenle" vs hiç input'u olmayan "Metni Ayır") ve esnek düzende
 * bu fark doğrudan görsel kutusuna yansıyıp aynı modalı feature başına başka
 * ebatta gösteriyordu. Oran sabit + iki panel aynı grid kolonunda olduğu için
 * yükseklikler hem birbirine hem tüm feature'lara eşit.
 *
 * maxHeight kısa ekranlarda taşmayı önlüyor; devreye girdiğinde iki panele de
 * aynı şekilde uygulandığı için eşitlik korunuyor. Ebadı değiştirmek isteyen
 * TEK yer burası.
 */
const PANE_ASPECT_RATIO = "4 / 3";

const paneBoxSizing = {
  flex: "none",
  aspectRatio: PANE_ASPECT_RATIO,
  maxHeight: "38vh",
  // Taban yükseklik. aspectRatio yükseklik ÜRETİR ama yalnızca genişlik
  // definite olduğunda; kutu flex-kolon + grid-item zincirinin içinde olduğu
  // için zincirin bir yerinde ölçü indefinite kaldığında yükseklik 0a çöküyor
  // ve panelin yerinde sadece altındaki "Ön izleme" düğmesi kalıyor
  // (bildirilen bug). minHeight zincirden BAĞIMSIZ taban veriyor.
  minHeight: 160,
};

/**
 * Özellik formu bölgesinin görünür yükseklik sınırı.
 *
 * Neden açık bir sayı: bölgeye `flex: 1 1 auto + minHeight: 0 + overflowY: auto`
 * vermek ancak ATASININ yüksekliği definite ise kaydırma üretir. Modal "içeriği
 * kadar uzun" olsun diye .modal-content `max-height + flex: 0 1 auto` ile
 * içeriğe göre boyutlanıyor — bu da yüksekliği INDEFINITE yapıyor, dolayısıyla
 * flex tabanlı kaydırma hiç devreye girmiyordu: form büyüyüp üstteki panelleri
 * yukarı itiyordu ("Metni Çevir"e satır eklendikçe görülen bug).
 *
 * max-height indefinite zincire ihtiyaç duymuyor: sınıra dayanınca bölge kendi
 * içinde kayıyor, paneller ve footer yerinde kalıyor. Kısa formlarda sınır hiç
 * devreye girmediği için modal yine içeriği kadar kısa açılıyor.
 */
const FEATURE_FORM_MAX_HEIGHT = "min(40vh, 380px)";

/**
 * sideLayout'ta (bkz. bodyContent) sol kolondaki tek panel (ORİJİNAL ya da
 * SONUÇ, sekmeyle). İkisi aynı ölçüde → sekme değişince kutu zıplamıyor.
 * maxHeight: kısa ekranda panel küçülsün, kolonu taşırmasın.
 */
const sidePaneSizing = {
  flex: "none",
  aspectRatio: "16 / 10",
  minHeight: 180,
  maxHeight: "44vh",
};

/**
 * sideLayout grid'inin tavanı: ekran − dialog marjları (24) − başlık (~52) −
 * footer (~66) − gövde padding'i (~36) − pay. Aşılınca sağ form kolonu kayar.
 */
const SIDE_LAYOUT_MAX_HEIGHT = "calc(100vh - 200px)";

export const IMAGE_EDIT_FEATURES = [
  {
    id: "generate-image-from-image",
    name: "Edit Image",
    endpoint: "api/image-editing/generate-image-from-image",
    icon: LuWandSparkles,
    description: "Edit your image using AI",
    requiresPrompt: true,
    promptPlaceholder: "EditImagePromptPlaceholder",
    modelLabel: "gemini",
  },
  {
    id: "extract-text",
    name: "ExtractText",
    icon: LuScanText,
    endpoint: "api/image-editing/extract-text",
    description: "Separate the baked-in text into editable text boxes",
    modelLabel: "florence-2-large",
    // Runs through the modal like the other features (no prompt needed): submit
    // fires the extract-text job, the modal polls + previews the cleaned
    // background, and "Accept" applies the result (swap bg + create one editable
    // BasicText box per detected region). No dispatchDirect / auto-apply.
  },
  {
    id: "change-image-text",
    name: "Change Image Text",
    endpoint: "api/image-editing/change-image-text",
    icon: LuPencil,
    description: "Change text content within the image",
    requiresTextChange: true,
    modelLabel: "gemini",
  },
  {
    id: "replace-product",
    name: "Replace Product",
    endpoint: "api/image-editing/replace-product",
    icon: LuReplace,
    description: "Replace a garment or product using a reference image",
    requiresReferenceImage: true,
    requiresRegion: true,
    modelLabel: "gemini",
  },
  {
    id: "aidea-smart-elements",
    name: "aideaSmartElements",
    icon: LuLayers,
    description: "Decompose this image into editable layers",
    // Kendi HTTP endpoint'i yok: iş middleware'in magic-element-engine'ine bir
    // Redux thunk üzerinden gidiyor (common/aideaSmartElements.es6). Modal
    // ARTIK atlanmıyor — diğer feature'lar gibi açılıyor, "Başlat" thunk'ı
    // tetikleyip kapanıyor; ilerleme canvas kutusunun üzerinde gösteriliyor.
    //
    // dispatchDirect bayrağı korunuyor ama anlamı daraldı: yalnızca
    // AIImageGenerateTab (Görsel Ekle modalı) bu feature'ı listesinden ELEMEK
    // için kullanıyor — orada bir canvas kutusu yok, thunk'ın ihtiyacı olan
    // boxId de yok.
    dispatchDirect: true,
    // Kredi kesiyor: middleware magic-element-engine artık extract-text ile aynı
    // akışı yapıyor — decompose öncesi pre-check, iş bitince processor'da düşüm,
    // fiyat price_lists('edit','seedream-v5-pro-layerize') satırından. Bu yüzden
    // CREDIT_ENDPOINTS'te var ve modalda kredi butonu gösteriliyor.
  },
  {
    id: "animate",
    name: "Animate Image",
    endpoint: `api/animation/v2`,
    icon: LuClapperboard,
    description: "Animate your static image using AI",
    requiresPrompt: true,
    // animate'te kullanıcı zaten provider'ı seçtiği için modelLabel verilmiyor
  },
  {
    id: "remove-background",
    name: "Remove Background",
    endpoint: "api/image-editing/remove-background",
    icon: LuImageOff,
    description: "Remove background from the image",
    modelLabel: "fal-ai",
  },
  {
    id: "remove-object",
    name: "Remove Object",
    endpoint: "api/image-editing/remove-object",
    icon: LuEraser,
    description: "Remove specific objects from the image",
    requiresPrompt: true,
    promptPlaceholder: "RemoveObjectPromptPlaceholder",
    modelLabel: "gemini",
  },
  {
    id: "remove-text",
    name: "Remove Text",
    endpoint: "api/image-editing/remove-text",
    icon: LuRemoveFormatting,
    description: "Remove text from the image",
    modelLabel: "fal-ai",
  },
  {
    id: "translate",
    name: "Translate Text",
    endpoint: "api/image-editing/translate-image-text",
    icon: LuLanguages,
    description: "Translate text within the image",
    requiresTargetLanguage: true,
    modelLabel: "gemini",
  },
  {
    id: "generate-similar",
    name: "Generate Similar",
    endpoint: "api/image-editing/generate-similar",
    icon: LuCopy,
    description: "Generate similar variations of the image",
    requiresCount: true,
    modelLabel: "fal-ai",
  },
  {
    id: "resize",
    name: "Resize Image",
    endpoint: "api/image-editing/aspect-change",
    icon: LuScanLine,
    description: "Change the aspect ratio of the image using AI",
    requiresAspectRatio: true,
    modelLabel: "gemini",
  },
  {
    id: "upscale",
    name: "Upscale Image",
    endpoint: "api/image-editing/upscale-image",
    icon: LuZoomIn,
    description: "Increase image resolution using AI",
    requiresScaleFactor: true,
    modelLabel: "aidea",
  },
  {
    id: "generate-from-reference",
    name: "Generate Similar Infographic",
    endpoint: "api/image-editing/generate-from-reference",
    icon: LuLayoutTemplate,
    description: "Generate a similar infographic based on this image",
    requiresPrompt: true,
    promptPlaceholder: "GenerateFromReferencePromptPlaceholder",
    modelLabel: "gemini",
  },
  // HIDDEN (temporarily): replace-background feature. Kept in code, removed from
  // the feature menu per request. Restore by uncommenting.
  // {
  //   id: "replace-background",
  //   name: "Replace Background",
  //   endpoint: "api/image-editing/replace-background",
  //   icon: LuReplace,
  //   description:
  //     "Replace the place/setting behind the character with a reference photo",
  //   requiresReferenceImage: true,
  //   modelLabel: "gemini",
  // },
];

export function ImageAnimatorModal({
  visible,
  onClose,
  imageUrl,
  boxId,
  navId,
  sceneIndex,
  onAcceptAnimation,
  onAddToScene,
  embed = false,
  initialFeatureId,
  initialSettings,
  onInsufficientBalance,
  // "Aidea Akıllı Öğeler": kendi endpoint'i olmadığı için submit bir HTTP
  // isteği değil, bu callback üzerinden Redux thunk'ı tetikliyor. Verilmezse
  // feature yine açılır ama "Başlat" hata gösterir.
  onDecomposeSmartElements,
  // Task 37 ASK 4: set only when this modal was opened from AnimatorPanel's
  // per-card action ({ panel: "animator", navId }) -- renders a Back control
  // that closes the popup and reopens the panel on the same scene. Only the
  // EditorShortcuts.jsx mount site currently ever passes this (see its own
  // comment); the EditorApp.jsx timeline mount leaves it undefined, so no
  // Back control ever renders there.
  returnTo,
}) {
  const [selectedFeature, setSelectedFeature] = useState(
    IMAGE_EDIT_FEATURES.find((f) => f.id === initialFeatureId) ||
      IMAGE_EDIT_FEATURES.find((f) => f.id === "animate")
  );
  const [prompt, setPrompt] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("Turkish");
  const [count, setCount] = useState(2);
  const [provider, setProvider] = useState("gemini");
  const [loop, setLoop] = useState(false);
  // "Transparan" toggle — force a transparent (alpha) output. Sends
  // transparent:true to POST /api/animation/v2; the worker then runs Bria
  // video matting to cut the subject out of its background. Default off.
  const [transparent, setTransparent] = useState(false);
  const [duration, setDuration] = useState(null); // null = not selected, 5 | 10 | 15
  // ── Kredi tahmini ("Görseli Canlandır") ────────────────────────────────────
  // Görsel Ekle modalıyla aynı desen: butona basınca /credit endpoint'ine istek
  // atılır (job başlatmaz, düşüm yapmaz), sonuç popover'da gösterilir. Şimdilik
  // yalnızca animate özelliğinde görünür; diğer özellikler kendi /credit
  // endpoint'leri açıldıkça aynı yapıya eklenecek.
  const [creditInfo, setCreditInfo] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditPopoverOpen, setCreditPopoverOpen] = useState(false);
  const [creditError, setCreditError] = useState(null);
  const creditBtnRef = useRef(null);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState("16:9");
  const [scaleFactor, setScaleFactor] = useState(2);
  const [textChanges, setTextChanges] = useState([
    { originalText: "", newText: "", location: "", customLocation: "" },
  ]);
  // remove-text & translate scope: "all" (whole image — fal.ai/gemini full pass)
  // or "region" (only the listed targetTexts — gemini).
  const [editMode, setEditMode] = useState("all");
  // Target texts for region mode (remove-text / translate).
  //
  // Entries are objects, not bare strings, so remove-text can carry the same
  // location hint "Metni Değiştir" sends: the same word can appear more than
  // once in an image, and without a location the model has to guess which
  // occurrence to erase. Translate keeps sending plain strings (its backend
  // has no location concept), so only the `text` field is read there.
  const [targetTexts, setTargetTexts] = useState([{ text: "", location: "", customLocation: "" }]);
  /** A target's location as the API should see it: "" when unset. */
  const targetLocationOf = (t) => (t.location === "custom" ? t.customLocation : t.location) || "";
  /**
   * "+ Ekle" ile satır/kart eklenen listeler ("Metin Çevir" hedef metinleri ve
   * "Metni Değiştir" değişiklik kartları) için ortak odaklama mekanizması.
   *
   * Aynı anda tek feature formu render edildiği için TEK ref yeterli — hangi
   * liste görünürse o bağlanıyor. Eklenen eleman, kaydırılabilir form
   * bölgesinin altında görünmez kalabiliyor; son input'a odaklanmak tarayıcıyı
   * onu görünür alana kaydırmaya zorluyor, ayrıca kullanıcı doğrudan yazmaya
   * başlayabiliyor.
   *
   * Not: listelerin KENDİ overflow'u yok (bir zamanlar vardı) — form bölgesi
   * kayıyor, iç içe iki scrollbar istenmiyor.
   */
  const addableListRef = useRef(null);
  const [focusLastAddedInput, setFocusLastAddedInput] = useState(false);
  useEffect(() => {
    if (!focusLastAddedInput) return;
    setFocusLastAddedInput(false);
    const el = addableListRef.current;
    if (!el) return;
    // Yeni eleman DOM'a girdikten SONRA çalışır (bu effect state değişiminden
    // sonra tetiklenir).
    const inputs = el.querySelectorAll("input, textarea");
    const last = inputs[inputs.length - 1];
    if (last) last.focus({ preventScroll: false });
  }, [targetTexts, textChanges, focusLastAddedInput]);
  // Index of the targetTexts entry being picked via the region OCR overlay
  // (null = closed).
  const [targetTextSelectIdx, setTargetTextSelectIdx] = useState(null);
  // Index of the text-change entry whose original text is being picked from the
  // image via the PowerToys-style region OCR overlay (null = closed).
  const [textSelectEntryIdx, setTextSelectEntryIdx] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replayKeys, setReplayKeys] = useState({});
  const [sceneNarrationText, setSceneNarrationText] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  // "Sahne Metnini Getir" button (Prompt row) — mirrors
  // InfographicSceneModal.handleFetchSceneText's loading flag.
  const [fetchingSceneText, setFetchingSceneText] = useState(false);

  // reference_id versiyon ailesi — "Geçmiş" açılır/kapanır panel. LAZY: her modal
  // açılışında OTOMATİK fetch YOK (DB'yi yormasın). Sadece kullanıcı panele
  // tıklayıp açınca bir kez çekilir; içeride spinner döner.
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  // "Geçmiş" artık modalın SOLUNDA kalıcı bir versiyon rayı (eski tam-genişlik
  // üst panel değil). versionsOpen = ray GENİŞLEMİŞ mi (varsayılan açık); rayın
  // kendi katla okuyla daraltılır. Ray yalnız gerçek geçmiş varken görünür.
  const [versionsOpen, setVersionsOpen] = useState(true);
  // Ray filtre: tümü / animasyonlar / düzenlemeler (eski panelden geri getirildi).
  const [historyFilter, setHistoryFilter] = useState("all");
  // Versiyonların hangi url için yüklendiği — aynı görselde tekrar açınca yeniden
  // istek atmamak, farklı görsele geçince önbelleği geçersiz kılmak için.
  const [versionsLoadedFor, setVersionsLoadedFor] = useState(null);
  // Aktif (seçili) versiyonun url'i. Tıklayınca optimistik olarak hemen taşınır
  // ki tick beklemeden doğru karta gitsin; box url'i (imageUrl prop) gerçekten
  // değişince onunla senkronlanır. imageUrl'e doğrudan bağlamıyoruz çünkü prop
  // güncellemesi gecikebilir / normalize edilip birebir eşleşmeyebilir.
  const [activeVersionUrl, setActiveVersionUrl] = useState(imageUrl);
  // Geçmişte tıklanan ama henüz uygulanmamış versiyon. Kart tıklaması artık
  // direkt uygulamıyor; sadece bunu seçili işaretliyor. Panelin sağ altındaki
  // "Onayla" butonuna basınca gerçekten uygulanır.
  const [pendingVersionUrl, setPendingVersionUrl] = useState(null);
  const prevImageUrlRef = useRef(imageUrl);
  useEffect(() => {
    if (imageUrl !== prevImageUrlRef.current) {
      prevImageUrlRef.current = imageUrl;
      setActiveVersionUrl(imageUrl);
      setPendingVersionUrl(null);
    }
  }, [imageUrl]);

  // ── Mode B ("animation edit") — replaces the modal body in place (no
  // popup-over-popup). editVersion = the normalized version being viewed.
  const [modalMode, setModalMode] = useState("generate"); // "generate" | "edit"
  const [editVersion, setEditVersion] = useState(null);
  // Animate wizard: the author asked to go back to "Ayarla" after a job
  // already completed (Yeniden üret / clicking the step badge). wizardStep is
  // otherwise derived from job status, which would pin them on "Üret"
  // forever once a result exists. Cleared on submit, dismiss and reopen.
  const [reconfiguring, setReconfiguring] = useState(false);
  // advancedOpen: lifted here (not local state inside AnimationEditMode) so it
  // survives the Sahneden/Kütüphaneden picker roundtrip, which swaps the modal
  // body away from AnimationEditMode (unmounting it) and back. Reset to false
  // only on Mode B entry/exit (enterEditMode/exitEditMode below), never on a
  // picker open/close.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [extendPrompt, setExtendPrompt] = useState("");
  const [extendProvider, setExtendProvider] = useState("gemini-fast");
  // Optional continuation-reference image (extend chunk-1 source). Declared
  // here so S3's runExtend passthrough and S4's picker UI share one value;
  // S2 itself never sets it (no picker yet), so extend behavior is unchanged
  // until S4 lands.
  const [referenceImage, setReferenceImage] = useState(null); // { url, name? } | null
  // S4 — continuation reference-image picker state (Mode B only). Mirrors the
  // shape of the replace-product widget's own pickingLibrary/libraryFiles/
  // pickingReference state, under new names — that feature's state is untouched.
  const [pickingExtendLibrary, setPickingExtendLibrary] = useState(false);
  const [extendLibraryFiles, setExtendLibraryFiles] = useState([]);
  const [extendLibraryLoading, setExtendLibraryLoading] = useState(false);
  const [extendLibraryError, setExtendLibraryError] = useState(null);
  const [pickingExtendScene, setPickingExtendScene] = useState(false);
  const [extendReferenceUploading, setExtendReferenceUploading] = useState(false);
  const [extendReferenceError, setExtendReferenceError] = useState(null);
  // Frame-scrub row (below the LEFT preview, Phase-2 slice): grabbing a still
  // frame from editVersion.mp4Url at a chosen time and setting it as the
  // continuation reference image — shares referenceImage/extendReferenceError
  // with the S4 picker above, own busy flag only.
  const [frameGrabbing, setFrameGrabbing] = useState(false);

  const versionRowToEditVersion = (v, idx) => ({
    displayUrl: v.fileVirtualPath,
    mp4Url: v.mp4Url || null,
    durationSec: typeof v.durationSec === "number" ? v.durationSec : null,
    loop: !!v.loop,
    provider: v.provider || null,
    prompt: v.prompt || "",
    sourceFileId: v.id,
    sourceJobId: null,
    badge: typeof idx === "number" && idx >= 0 ? `v${idx + 1}` : null,
  });

  // A family row matches the box's current url either via the static
  // deliverable (fileVirtualPath) or, for video-source boxes, the mp4 itself
  // — GET /ideal-studio-files/versions rows carry mp4Url alongside
  // fileVirtualPath (the webp/gif deliverable), so an mp4-source box's
  // imageUrl never equals fileVirtualPath.
  const rowMatchesUrl = (v, url) =>
    !!url &&
    (assetKey(v.fileVirtualPath) === assetKey(url) ||
      (!!v.mp4Url && assetKey(v.mp4Url) === assetKey(url)));

  const liveJobToEditVersion = (j) => {
    if (!j || j.featureId !== "animate" || j.status !== "completed" || !j.result) return null;
    return {
      displayUrl: pickAnimationApplyUrl({
        webpUrl: j.result.webpUrl,
        gifUrl: j.result.gifUrl,
        mp4Url: j.result.mp4Url,
        fallbackUrl: j.result.url,
      }),
      mp4Url: j.result.mp4Url || null,
      durationSec: typeof j.result.durationSeconds === "number" ? j.result.durationSeconds : null,
      loop: typeof j.loop === "boolean" ? j.loop : false,
      provider: j.result.provider || null,
      prompt: prompt, // best-effort/informational-only, never rendered in edit mode
      sourceFileId: null,
      sourceJobId: j.id,
      badge: null,
    };
  };

  const enterEditMode = (version) => {
    if (!version) return;
    setEditVersion(version);
    setExtendPrompt("");
    setExtendProvider(
      ["gemini", "gemini-fast"].includes(version.provider) ? version.provider : "gemini-fast"
    );
    setModalMode("edit");
    setVersionsOpen(false);
    setAdvancedOpen(false);
  };

  // "Önizle ve Düzenle": an mp4 result is edited in the Video Director (simple mode); no mp4 → the legacy edit mode.
  const previewAndEdit = (version) => {
    if (!version?.mp4Url) {
      enterEditMode(version);
      return;
    }
    openInDirector({
      importUrl: version.mp4Url,
      promptProfile: "animate",
      simple: true,
      targetBoxId: boxId,
      loop: version.loop,
    });
    onClose?.();
  };

  const exitEditMode = () => {
    setModalMode("generate");
    setEditVersion(null);
    setReferenceImage(null);
    setAdvancedOpen(false);
  };

  // urlOverride: Onayla omits it (defaults to displayUrl — the picked
  // webp/gif/mp4 deliverable). The rail's "Sahneye MP4 olarak ekle" button
  // passes editVersion.mp4Url explicitly instead — same apply path (approve +
  // cleanup + close), different url. applyAnimationResult (both
  // onAcceptAnimation mount sites) already box-swaps to EnrichedPlayer
  // whenever the applied url is a video, so no extra branching is needed here.
  const acceptEditVersion = (urlOverride) => {
    if (!editVersion || isExtending) return;
    const applyUrl = urlOverride || editVersion.displayUrl;
    if (editVersion.sourceJobId && job?.id === editVersion.sourceJobId && job?.result) {
      handleAcceptResult(applyUrl); // live result → existing approve path (approveJob + cleanup + close);
      return; // handleAcceptResult(imageUrl) already accepts this override.
    }
    // Version-row source → same contract as the history Onayla bar (:2400-2415)
    // versions yalnız ray açıldıysa yüklü (fetch-on-expand) → rayı hiç açmadan
    // kabul edilen sürüm için video effect'inin çektiği currentVersionRow'a düş.
    const row =
      versions.find((x) => x.id === editVersion.sourceFileId) ||
      (currentVersionRow?.id === editVersion.sourceFileId ? currentVersionRow : null);
    if (row) applyVersionSettings(row);
    setActiveVersionUrl(applyUrl);
    if (onAcceptAnimation) onAcceptAnimation({ url: applyUrl, boxId, loop: editVersion.loop });
    // Sürümün job'u henüz onaylanmamışsa widget'ta "Onay Bekliyor" kalmasın.
    const versionJobId = row?.jobId || editVersion.sourceJobId;
    if (versionJobId) approveJob(versionJobId);
    onClose?.();
  };

  // Canlandırılmış kaynak (video mp4/webm veya animasyonlu webp/gif): kutunun url'i
  // statik olmayan bir çıktıysa modal, versions API'sinden bu çıktının ÜRETİLDİĞİ
  // orijinal fotoyu çekip Original panelinde gösterir; üretim o orijinale koşar.
  const isVideoSource = /\.(mp4|webm|mov|m4v|webp|gif)(\?|#|$)/i.test(imageUrl || "");
  // <video> yalnızca gerçek video uzantıları için; webp/gif <img> ile render edilir.
  const isPlayableVideoExt = /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(imageUrl || "");
  const [videoSourceImageUrl, setVideoSourceImageUrl] = useState(null);
  // The version-family row for the box's CURRENT url (imageUrl) — same lookup
  // the auto-fill below already does. Kept in state (not just applied to the
  // form) so extend/convert can act on a previously-generated animation even
  // when there's no live `job` in this modal session — see
  // versionRowToEditVersion() below.
  const [currentVersionRow, setCurrentVersionRow] = useState(null);
  // currentVersionRow'un aile listesindeki sırası ("vN" rozeti). versions yalnız
  // Geçmiş rayı açılınca yüklendiği için rozet ona bakamaz; bu effect'in kendi
  // listesinden gelir.
  const [currentVersionIdx, setCurrentVersionIdx] = useState(-1);
  // Video/animasyon kaynağının GERÇEK versiyon geçmişi var mı? (orijinal + çıktı,
  // ya da düzenlemeler → length > 1). Dışarıdan elle eklenen bir webp'in job
  // zinciri olmadığından fetchFileVersions yalnız kendisini (≤1) döndürür →
  // "Geçmiş" paneli hiç gösterilmez. Bu effect zaten videoSourceImageUrl için
  // fetch ediyor; sonucu tekrar kullanıyoruz (ek istek yok).
  const [hasVersionHistory, setHasVersionHistory] = useState(false);
  useEffect(() => {
    if (!visible || !isVideoSource || !imageUrl) {
      setVideoSourceImageUrl(null);
      setCurrentVersionRow(null);
      return undefined;
    }
    let cancelled = false;
    fetchFileVersions(imageUrl)
      .then((list) => {
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        // hasVersionHistory ARTIK burada set edilmiyor — aşağıdaki "ray için
        // fetch-on-open" effect'i tüm görsel türleri için tek sahip.
        // Yalnız GERÇEK (video olmayan) orijinali seç; yoksa null. Eskiden
        // "|| arr[0]" webp'in kendisini orijinal sanıp Original panelinde aynı
        // fotoğrafı gösteriyordu — dışarıdan eklenen, zinciri olmayan webp'te.
        const original = arr.find(
          (v) =>
            v.fileVirtualPath && !/\.(mp4|webm|mov|m4v|webp|gif)(\?|#|$)/i.test(v.fileVirtualPath)
        );
        setVideoSourceImageUrl(original?.fileVirtualPath || null);
        // OTOMATİK DOLDUR: açılan çıktının (imageUrl) versiyonunu bul, üretim
        // ayarlarını (prompt/provider/süre/döngü/format) forma getir. Kullanıcı
        // panel açmadan da mevcut animasyonun ayarlarını görür.
        const current = arr.find((v) => rowMatchesUrl(v, imageUrl));
        setCurrentVersionRow(current || null);
        setCurrentVersionIdx(current ? arr.indexOf(current) : -1);
        // Yalnız GERÇEK geçmişi/orijinali olan (original var) kaynakta ayarları
        // forma getir. Geçmişi olmayan webp'te applyVersionSettings →
        // resetAnimateDefaults loop'u localStorage değerinden sonra sıfırlayıp
        // "Loop→Play Once" sıçraması yapıyordu; onu atla → seçim sabit kalsın.
        if (current && original) applyVersionSettings(current);
      })
      .catch(() => {
        setVideoSourceImageUrl(null);
        setCurrentVersionRow(null);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, isVideoSource, imageUrl]);

  // Video/animasyon modunda feature'ı animate'e sabitle (kalıcı iş / farklı feature bulaşmasın).
  useEffect(() => {
    if (isVideoSource) {
      setSelectedFeature(IMAGE_EDIT_FEATURES.find((f) => f.id === "animate"));
    }
  }, [isVideoSource]);

  // Original panelinde gösterilecek url. Sol rayda bir versiyona tıklamak
  // (pendingVersionUrl) ORİJİNAL önizlemeyi CANLI değiştirir — kullanıcı seçtiği
  // versiyonu sağda görür, sahneye ise ancak "Onayla" ile uygular. (Eski tasarımda
  // panel gövdeyi kapladığı için pending önizlemeyi etkilemiyordu; ray artık
  // yan yana olduğundan etkiliyor.) mp4/webm gibi gerçek videolar <img> ile
  // gösterilemez → onlarda pending'i yansıtma, kaynak foto/imageUrl kalsın.
  const pendingPreviewUrl =
    pendingVersionUrl && !/\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(pendingVersionUrl)
      ? pendingVersionUrl
      : null;
  const originalDisplayUrl = pendingPreviewUrl || (isVideoSource ? videoSourceImageUrl : imageUrl);
  // Düzenleme/üretim KAYNAĞI: geçmişten seçili (pending) versiyon varsa O; yoksa
  // sahnedeki (active); o da yoksa orijinal. Böylece geçmişten bir versiyon
  // seçip prompt yazınca üretim EN BAŞTAKİ orijinale değil, seçili versiyona koşar.
  const editSourceUrl = pendingVersionUrl || activeVersionUrl || imageUrl;
  // Farklı bir görsele geçildiyse (yeni url mevcut ailede yok) önbelleği sıfırla
  // → panel bir sonraki açılışta o görsel için yeniden yükler. Aile içinde versiyon
  // değiştirmede (url ailede var) önbellek korunur, tekrar istek atılmaz.
  useEffect(() => {
    if (
      versionsLoadedFor &&
      versionsLoadedFor !== imageUrl &&
      !versions.some((v) => rowMatchesUrl(v, imageUrl))
    ) {
      setVersions([]);
      setVersionsLoadedFor(null);
    }
  }, [imageUrl]);

  // AnimatorPanel (mounted separately in EditorApp, may not be open right
  // now) writes new version-history entries server-side when the user applies a
  // batch. Force the next toggleVersions() open to refetch instead of serving
  // this stale per-url cache.
  useEffect(() => {
    const handler = () => setVersionsLoadedFor(null);
    window.addEventListener("bulkAnimateApplied", handler);
    return () => window.removeEventListener("bulkAnimateApplied", handler);
  }, []);

  // Sol rayı yalnızca genişlet/daralt (fetch aşağıdaki effect'te, ray açılınca).
  const toggleVersions = () => setVersionsOpen((v) => !v);

  // Ray açık/kapalı. Modal her açılışta KAPALI rayla gelir (kullanıcı isteği);
  // kapalıyken yalnız "aç" oku görünür — versiyon listesi (ve yüklenirken dönen
  // spinner) sadece ray açılınca render edilir (RAIL_COLLAPSED_W).
  // Sıfırlama RENDER sırasında (prop değişince state ayarlama kalıbı): effect'te
  // yapılınca önceki oturumda açık bırakılan ray ilk karede açık çiziliyor,
  // sonra modal giriş animasyonunun ortasında daralıyordu.
  const [railOpen, setRailOpen] = useState(false);
  const [railPrevVisible, setRailPrevVisible] = useState(visible);
  if (visible !== railPrevVisible) {
    setRailPrevVisible(visible);
    if (visible) setRailOpen(false);
  }
  // Modal genişliği/sağ boşluğu YALNIZ ray aç/kapa sırasında animasyonlu
  // (imageAnimatorModal--railAnim). "Sahneyi gör", AI sohbet yanaşması, pencere
  // boyutu gibi diğer değişimlerde anında uygulanır — yoksa içerik genişliği
  // anında, dialog 0.25s'de değişip ray içeriğin üstüne biniyor/başlık kayıyordu.
  const [railAnimating, setRailAnimating] = useState(false);
  // Kapanış animasyonu sürerken (react-bootstrap onExit → onExited) true:
  // imageAnimatorModal--exiting sınıfı pageModal'ın yukarı kaydırmasını
  // kapatıyor, modal yerinde sönüyor.
  const [exiting, setExiting] = useState(false);
  const railAnimTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(railAnimTimerRef.current), []);
  const toggleRail = () => {
    setRailOpen((prev) => !prev);
    setRailAnimating(true);
    clearTimeout(railAnimTimerRef.current);
    railAnimTimerRef.current = setTimeout(() => setRailAnimating(false), 300);
  };

  // Yanıt gelene kadar görsel değiştiyse eski görselin listesi yazılmasın.
  const latestImageUrlRef = useRef(imageUrl);
  latestImageUrlRef.current = imageUrl;
  // Ray için fetch-on-EXPAND: versiyon ailesi modal açılınca DEĞİL, kullanıcı
  // rayı okla ilk açtığında yüklenir (ray kapalı gelir; açılışta istek + rayda
  // dönen spinner modalın ilk anını ağırlaştırıyordu). Yüklenirken spinner
  // rayın içinde döner. hasVersionHistory'nin TEK sahibi burası (video effect'i
  // set etmiyor). Cache: aynı url için tekrar istek atılmaz.
  useEffect(() => {
    if (!visible || !imageUrl || !railOpen) return;
    if (versionsLoading) return;
    if (versionsLoadedFor === imageUrl) return;
    const urlForThisLoad = imageUrl;
    setVersionsLoading(true);
    // Fetch boş/hatalı dönse bile rayda EN AZ mevcut görsel ("Orijinal" kartı)
    // görünsün — kullanıcı ilk düzenlemeden önce de rayı görür. Gerçek aile
    // gelince (≥1) tohum yerine o kullanılır.
    const seed = [{ fileVirtualPath: imageUrl, id: "current" }];
    fetchFileVersions(imageUrl)
      .then((list) => {
        if (latestImageUrlRef.current !== urlForThisLoad) return;
        const arr = Array.isArray(list) ? list : [];
        setVersions(arr.length > 0 ? arr : seed);
        setVersionsLoadedFor(urlForThisLoad);
        setHasVersionHistory(arr.length > 1);
      })
      .catch(() => {
        if (latestImageUrlRef.current !== urlForThisLoad) return;
        setVersions(seed);
        // Hata da "yüklendi" sayılır: yoksa railLoading (versionsLoadedFor !==
        // imageUrl) true kalır ve açık rayda spinner sonsuza kadar döner.
        setVersionsLoadedFor(urlForThisLoad);
        setHasVersionHistory(false);
      })
      .finally(() => setVersionsLoading(false));
    // versionsLoadedFor/versionsLoading da bağımlılık: ray açıkken önbellek
    // sıfırlanınca (bulkAnimateApplied) ya da görsel yükleme sırasında
    // değişince yeniden yüklensin — yoksa spinner takılı kalıyordu. Üstteki
    // guard'lar döngüyü engeller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, imageUrl, railOpen, versionsLoadedFor, versionsLoading]);

  // Geçmiş rayı modalın KENDİ kutusunun (.modal-content) içine, sol kenara
  // yapışık basılır — eskiden document.body'de sabit (fixed) ayrı bir paneldi ve
  // dar ekranda modalın üstüne biniyordu. Portal hedefi LAYOUT effect'te bulunur:
  // react-bootstrap modal DOM'u aynı commit'te eklenmiş olur ve buradaki
  // setState tarayıcı boyamadan ÖNCE yeniden render ettirir → ray modalın ilk
  // karesinde hazır (useEffect'te bir kare rayısız çizilip sonra içerik
  // yerine oturuyor, açılışta kasılma gibi görünüyordu). Kapanışta hedef
  // silinmez: ray, modal sönerken onunla birlikte kaybolsun.
  // Hedef bu ÖRNEĞE özel sınıfla aranır (AppModal contentClassName): editörde
  // birden fazla ImageAnimatorModal var; genel ".imageAnimatorModal
  // .modal-content" araması, öteki örnek kapanırken DOM'daysa rayı onun içine
  // koyuyordu. Embed'de AppModal yok → arama da yok.
  const railContentClass = `imgAnimContent-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [modalContentEl, setModalContentEl] = useState(null);
  useLayoutEffect(() => {
    if (!visible || embed) return undefined;
    let raf;
    let tries = 0;
    const find = () => {
      const el = document.querySelector(`.${railContentClass}`);
      if (el) setModalContentEl(el);
      else if (tries++ < 30) raf = requestAnimationFrame(find);
    };
    find();
    return () => cancelAnimationFrame(raf);
  }, [visible, embed, railContentClass]);

  // Rayın başlık ve alt şeridi, modalın başlığı (.app-modal__header) ve
  // footer'ıyla (.modal-footer) AYNI boyda olsun diye ikisi ölçülür — boyları
  // feature'a/duruma göre değiştiği için sabit sayı tutmuyor. .modal-content'in
  // boyu değişince (footer gelip gidince, içerik büyüyünce) yeniden ölçülür.
  const [railChrome, setRailChrome] = useState({ head: 0, foot: 0 });
  useLayoutEffect(() => {
    if (!modalContentEl) return undefined;
    const measure = () => {
      const head = modalContentEl.querySelector(":scope > .app-modal__header");
      const foot = modalContentEl.querySelector(".modal-footer:not([hidden])");
      const next = { head: head ? head.offsetHeight : 0, foot: foot ? foot.offsetHeight : 0 };
      setRailChrome((prev) => (prev.head === next.head && prev.foot === next.foot ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    // .modal-content max-height'e dayanınca başlık/footer boyu değişse de
    // kendi boyu değişmez → başlık ve footer ayrıca izlenir; footer gelip
    // gidince (MutationObserver) izleme yeniden kurulur.
    const ro = new ResizeObserver(measure);
    const observeAll = () => {
      ro.disconnect();
      ro.observe(modalContentEl);
      const head = modalContentEl.querySelector(":scope > .app-modal__header");
      const foot = modalContentEl.querySelector(".modal-footer");
      if (head) ro.observe(head);
      if (foot) ro.observe(foot);
      measure();
    };
    observeAll();
    const mo = new MutationObserver(observeAll);
    mo.observe(modalContentEl, { childList: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [modalContentEl]);

  // Versiyonun etiketi = ürettiği feature'ın wizard'daki ADI (i18n.t(name)) —
  // "Görseli Canlandır", "Arka Planı Kaldır", "Obje Değiştir"… Böylece etiketler
  // feature listesindeki isimlerle birebir aynı. operation/jobType → feature id.
  const OP_TO_FEATURE_ID = {
    "remove-background": "remove-background",
    "chroma-key-remove-background": "remove-background",
    "remove-object": "remove-object",
    "remove-text": "remove-text",
    "translate-text": "translate",
    // Toplu Alan İşlemleri's sequential versions carry the area-op type.
    translate: "translate",
    "change-image-text": "change-image-text",
    "generate-image-with-text": "change-image-text",
    "generate-similar": "generate-similar",
    "edit-image": "generate-image-from-image",
    edit: "generate-image-from-image",
    "aspect-change": "resize",
    upscale: "upscale",
    "upscale-image": "upscale",
    "replace-product": "replace-product",
    "change-object": "replace-product",
    "generate-from-reference": "generate-from-reference",
    "generate-with-style-reference": "generate-from-reference",
    "extract-text": "extract-text",
    "animate-extend": "animate",
    "animate-convert": "animate",
    // Görseli Düzenle v2 (area ops) — not in the wizard grid, named below.
    "edit-region": "edit-region",
    "add-object": "add-object",
    "add-logo": "add-logo",
  };
  // Area-ops-only features have no IMAGE_EDIT_FEATURES entry; these are their
  // i18n keys (same names AreaOpsModal shows).
  const AREA_OP_FEATURE_NAMES = {
    "edit-region": "Edit Region",
    "add-object": "Add Object",
    "add-logo": "Add Logo",
  };
  const JOBTYPE_TO_FEATURE_ID = {
    "image-animation": "animate",
    animation: "animate",
    "video-to-video": "animate",
    "image-editing": "generate-image-from-image",
    "training-aspect-conversion": "resize",
    "infographic-image-generate": "generate-from-reference",
    "infographic-images-generate": "generate-from-reference",
    "infographic-scene-generate": "generate-from-reference",
  };
  const featureNameById = (id) => {
    const f = IMAGE_EDIT_FEATURES.find((x) => x.id === id);
    if (f) return i18n.t(f.name);
    return AREA_OP_FEATURE_NAMES[id] ? i18n.t(AREA_OP_FEATURE_NAMES[id]) : null;
  };
  const versionOpLabel = (v, idx) => {
    if (idx === 0) return i18n.t("Original");
    // Toplu Düzenleme (area-ops-batch): just "Toplu Düzenleme".
    if (v.operation === AREA_OPS_BATCH_OPERATION) {
      return formatAreaOpsBatchLabel((k, o) => i18n.t(k, o));
    }
    // animate-extend: aynı işlem (Görseli Canlandır) ama uzatma olduğunu belirt.
    if (v.operation === "animate-extend") {
      return `${featureNameById("animate") || i18n.t("Animate Image")} (${i18n.t("Extended")})`;
    }
    // animate-convert: aynı işlem ama döngü yönünü belirt (mevcut Loop/Play
    // Once anahtarlarını yeniden kullanıyoruz — yeni bir çeviri gerekmiyor).
    if (v.operation === "animate-convert") {
      const loopLabel = v.loop ? i18n.t("Loop") : i18n.t("Play Once");
      return `${featureNameById("animate") || i18n.t("Animate Image")} (${loopLabel})`;
    }
    // 1) operation → feature adı (en granüler). Sondaki "-image" ekini soyarak da
    //    dene (ör. "upscale-image" → "upscale") — jobType image-editing generic'ine
    //    düşüp yanlış etiketlenmesini önler.
    const opFeat =
      v.operation &&
      (OP_TO_FEATURE_ID[v.operation] || OP_TO_FEATURE_ID[v.operation.replace(/-image$/, "")]);
    if (opFeat) return featureNameById(opFeat);
    // 2) jobType → feature adı
    const jobFeat = v.jobType && JOBTYPE_TO_FEATURE_ID[v.jobType];
    if (jobFeat) return featureNameById(jobFeat);
    // 3) Video/animasyon çıktısı → "Görseli Canlandır"
    const isVideo =
      /video/i.test(v.fileType || "") ||
      /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(v.fileVirtualPath || "");
    if (isVideo) return featureNameById("animate") || i18n.t("Animate");
    // 4) Feature'ı olmayan job türleri
    if (v.jobType === "text-to-image") return i18n.t("Text to Image");
    if (v.jobType === "image-to-avatar" || v.jobType === "ai-avatar") return i18n.t("Avatar");
    // 5) Eşlenmemiş ama dolu kod → düzgün yazım
    const raw = v.operation || v.jobType;
    if (raw) {
      return raw.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    }
    // 6) Kod yok → backend fileDescription'ı
    if (v.fileDescription) return v.fileDescription;
    // 7) Son çare
    return `${i18n.t("Version")} ${idx + 1}`;
  };

  // Bir versiyonu üreten feature'ın id'si (operation → jobType → video sezgisi).
  // Bulunamazsa null.
  const featureIdForVersion = (v) => {
    const opFeat =
      v.operation &&
      (OP_TO_FEATURE_ID[v.operation] || OP_TO_FEATURE_ID[v.operation.replace(/-image$/, "")]);
    if (opFeat) return opFeat;
    const jobFeat = v.jobType && JOBTYPE_TO_FEATURE_ID[v.jobType];
    if (jobFeat) return jobFeat;
    const isVideo =
      /video/i.test(v.fileType || "") ||
      /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(v.fileVirtualPath || "");
    // Animasyon çıktısı (webp/gif loop): backend fileType="animation" işaretler
    // ve/veya outputFormat doldurur; operation/jobType null gelebilir (bu yüzden
    // yukarıdaki eşlemelere düşmez). Statik webp yüklemesi fileType="image"
    // olduğundan buraya girmez.
    const isAnimation =
      /animation/i.test(v.fileType || "") || v.outputFormat === "webp" || v.outputFormat === "gif";
    if (isVideo || isAnimation) return "animate";
    return null;
  };

  // Geçmişten seçilen versiyonun ÜRETİM ayarlarını (prompt/provider/feature) forma
  // geri yükler — kullanıcı aynı ayarlarla yeniden üretebilsin. Backend sadece
  // prompt/provider/model + operation/jobType tutuyor; süre/oran/adet gelmez.
  // Animate ayarlarını (prompt/provider/süre/döngü) varsayılana çeker.
  // Sadece webp/mp4 (animate) versiyonlarında geri yükleniyor; onun dışındaki
  // her seçimde forma bulaşmış eski animate ayarları kalmasın diye sıfırlıyoruz.
  // Süre varsayılanı = öğenin sahne penceresine en yakın desteklenen süre
  // (pencere bilinmiyorsa Otomatik) — açılıştaki preselect ile aynı kural.
  const resetAnimateDefaults = () => {
    setPrompt("");
    setProvider("gemini");
    setDuration(defaultDurationForWindow("gemini", elementWindowSec));
    setLoop(false);
  };
  const applyVersionSettings = (v) => {
    if (!v) return;
    // Restore YALNIZCA "Görseli Canlandır" (animate) versiyonları için.
    // featureIdForVersion animate çıktısını (video + animation webp/gif) tanır;
    // elle yüklenmiş görsel / diğer feature'lar → null döner, varsayılana dönülür.
    // (operation/jobType null olan animate webp'i burada "job'suz" sanıp
    // sıfırlamıyoruz — loop gerçekten uygulanıyor.)
    if (featureIdForVersion(v) !== "animate") {
      resetAnimateDefaults();
      return;
    }
    if (typeof v.prompt === "string") setPrompt(v.prompt);
    // provider yalnızca modalın desteklediği listeden biriyse uygula.
    if (v.provider && PROVIDER_OPTIONS.some((o) => o.value === v.provider)) {
      setProvider(v.provider);
    }
    // Üretim süresi (backend job.metadata'dan gelir). provider ile uyumsuzsa
    // aşağıdaki duration-validation effect'i null'a çeker — sorun olmaz.
    if (typeof v.durationSec === "number" && Number.isFinite(v.durationSec)) {
      setDuration(Math.round(v.durationSec));
    }
    // Döngü (animate-v2 createPlaceholder metadata'sından).
    if (typeof v.loop === "boolean") setLoop(v.loop);
    // Feature'ı yalnızca non-video modda değiştir (video modu animate'e kilitli).
    if (!isVideoSource) {
      const fid = featureIdForVersion(v);
      const feat = fid && IMAGE_EDIT_FEATURES.find((f) => f.id === fid);
      if (feat) setSelectedFeature(feat);
    }
  };

  // replace-product: the reference garment/product image the user supplies
  // (uploaded file or picked from the activity history). Sent to the backend as
  // params.referenceImageUrl alongside the source (current) image.
  const [referenceImageUrl, setReferenceImageUrl] = useState("");
  const [referenceUploading, setReferenceUploading] = useState(false);
  const [referenceError, setReferenceError] = useState(null);
  // When true the body swaps to the history picker so the user can choose an
  // existing image as the reference (mirrors the showHistory browse view).
  const [pickingReference, setPickingReference] = useState(false);
  // When true the body swaps to the training library picker so the user can pick
  // an existing uploaded image (fetchIdealStudioFiles) as the reference.
  const [pickingLibrary, setPickingLibrary] = useState(false);
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState(null);
  // replace-product: OPTIONAL text note disambiguating which object inside the
  // selected region to replace (e.g. "the jacket"). Empty → replace whatever is
  // inside the region. Sent to the backend as params.targetObject.
  const [targetObject, setTargetObject] = useState("");
  // replace-product: when true the reference object is re-rendered to match the
  // source image style before insertion; when false (default) it is inserted
  // AS-IS. Sent to the backend as params.adaptStyle.
  const [adaptStyle, setAdaptStyle] = useState(false);
  // replace-product: REQUIRED normalized bounding box ({x,y,width,height} 0-1)
  // the user draws over the SOURCE image marking WHICH object to replace (mirrors
  // the translate/remove-text region selection). Drives the two-step pipeline.
  const [targetRegion, setTargetRegion] = useState(null);
  // When true the fullscreen region selector overlay is open.
  const [regionSelectorOpen, setRegionSelectorOpen] = useState(false);
  // Drag-over highlight for the reference-image drop zone.
  const [refDragActive, setRefDragActive] = useState(false);

  // Consolidated job state - all job-related data in one object
  const [job, setJob] = useState(null);
  // job = { id, status, result, featureId, progress }

  // İş tepsiden onaylanır/reddedilirse (modal açıkken ya da arka planda mount
  // kalmışken) bu modalın elindeki sonuç bayatlar — "Onayla" çubuğu zaten
  // sahneye uygulanmış görseli yeniden onaya sunar. Kalıcı kaydı JobWidgetHost
  // siler (clearSettledImageEditJobs); burada yalnız in-memory state sıfırlanır.
  const jobIdRef = useRef(null);
  jobIdRef.current = job?.id || null;
  useEffect(
    () =>
      onJobsSettled((jobIds) => {
        if (jobIdRef.current && jobIds.includes(jobIdRef.current)) setJob(null);
      }),
    []
  );

  // Animate-v2 "extend" feature: the box's on-screen window in the current
  // scene, start/end in seconds — falling back to the scene duration when the
  // box has no explicit end. Drives both the Otomatik duration picker and the
  // SONUÇ "Uzat" chip/buttons below, plus the already-animated-box summary
  // card's "Sahnede: start–end sn" chip. Reads straight from the store the way
  // sibling components do (e.g. BoxContextMenu.jsx) instead of prop-drilling
  // through every one of this modal's mount sites.
  const elementWindow = useSelector((s) => {
    const present = (s.undoGroup && s.undoGroup.present) || s;
    const box = boxId ? present.boxesById?.[boxId] : null;
    if (!box) return null;
    const legacyAnims = present.animationsById?.[boxId] || [];
    const { startFrame, endFrame: resolvedEnd } = resolveBoxTiming(box, legacyAnims, 30);
    let endFrame = resolvedEnd;
    if (typeof endFrame !== "number") {
      const sceneDurationSec = navId ? getSceneDurationSec(navId, present) : 0;
      if (sceneDurationSec > 0) endFrame = sceneDurationSec * 30;
    }
    if (typeof endFrame !== "number") return null;
    const start = typeof startFrame === "number" ? startFrame : 0;
    return { startSec: start / 30, endSec: endFrame / 30 };
  });
  const elementWindowSec = elementWindow
    ? Math.max(0, elementWindow.endSec - elementWindow.startSec)
    : null;

  // S4 — current scene's images, for Mode B's continuation-reference picker.
  // Mirrors KnollingView's per-box loop (collectSceneImages.es6's three
  // extractors), scoped to this box's own scene (navId) instead of the whole
  // training. Owner directive: the box's OWN current still image is pinned
  // first — pre-seeded into `out`/`seen` so any later box producing the same
  // url is deduped against it, not the other way around.
  const navItemsById = useSelector((s) => ((s.undoGroup && s.undoGroup.present) || s).navItemsById);
  const boxesById = useSelector((s) => ((s.undoGroup && s.undoGroup.present) || s).boxesById);
  const pluginToolbarsById = useSelector(
    (s) => ((s.undoGroup && s.undoGroup.present) || s).pluginToolbarsById
  );
  const extendSceneImages = useMemo(() => {
    const seen = new Set(originalDisplayUrl ? [originalDisplayUrl] : []);
    const out = originalDisplayUrl ? [{ url: originalDisplayUrl, name: i18n.t("Original") }] : [];
    const navBoxIds = navItemsById?.[navId]?.boxes || [];
    navBoxIds.forEach((id) => {
      const box = boxesById?.[id];
      const toolbar = pluginToolbarsById?.[id];
      if (!box) return;
      const pluginUrl = getPluginImageUrl(toolbar);
      if (pluginUrl && !seen.has(pluginUrl)) {
        seen.add(pluginUrl);
        out.push({ url: pluginUrl, name: toolbar?.state?.alt || "Image" });
      }
      extractContentImageUrls(box).forEach((u) => {
        if (!seen.has(u)) {
          seen.add(u);
          out.push({ url: u, name: "Content" });
        }
      });
      const bg = extractBackgroundImageUrl(box);
      if (bg && !seen.has(bg)) {
        seen.add(bg);
        out.push({ url: bg, name: "Background" });
      }
    });
    return out;
  }, [navId, boxesById, pluginToolbarsById, navItemsById, originalDisplayUrl]);

  // Extend/convert-in-flight flag (disables extend AND convert buttons) + the
  // jobId it started, so the completion watcher below only reacts to ITS OWN
  // job. On completion, also snaps the loop toggle to the RESULT's loop —
  // extend always yields play-once (job.loop was set to false at submit, see
  // submitAnimateDerivative), convert yields whatever loop it converted to.
  const [isExtending, setIsExtending] = useState(false);
  const extendJobIdRef = useRef(null);
  useEffect(() => {
    if (!isExtending || !extendJobIdRef.current) return;
    if (!job) {
      // checkJobStatus's 404 cleanup wiped the job (setJob(null)) — release
      // the rail instead of leaving it locked forever.
      setIsExtending(false);
      return;
    }
    if (job.id !== extendJobIdRef.current) return;
    if (job.status === "completed" || job.status === "failed") {
      if (job.status === "completed") {
        if (typeof job.loop === "boolean") setLoop(job.loop);
        if (modalMode === "edit") refreshEditVersionFromJob(job);
      }
      setIsExtending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExtending, job, modalMode]);

  // Swap the LEFT display to the just-finished derivative and refresh the
  // family so badge/sourceFileId/currentVersionRow stop being stale. S1 adds
  // `jobId` to the versions payload — match on that first (exact, no url
  // normalization concerns); fall back to the old url-guess ONLY when a
  // row's `jobId` is absent (rows created before S1 shipped, or MW not yet
  // redeployed).
  const refreshEditVersionFromJob = (doneJob) => {
    const live = liveJobToEditVersion(doneJob);
    if (live) setEditVersion(live);
    if (!imageUrl) return;
    fetchFileVersions(imageUrl)
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setVersions(arr);
        setVersionsLoadedFor(imageUrl);
        const resultUrl =
          doneJob?.result?.webpUrl || doneJob?.result?.gifUrl || doneJob?.result?.url;
        let idx = arr.findIndex((r) => r.jobId && r.jobId === doneJob?.id);
        if (idx < 0) {
          idx = arr.findIndex(
            (r) => !r.jobId && assetKey(r.fileVirtualPath) === assetKey(resultUrl)
          );
        }
        if (idx >= 0) {
          setEditVersion(versionRowToEditVersion(arr[idx], idx));
          setCurrentVersionRow(arr[idx]);
        }
        // idx < 0 ⇒ placeholder not patched yet — keep the live-shape
        // editVersion; sourceJobId still works for further derivatives.
      })
      .catch(() => {});
  };

  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [hoveredImage, setHoveredImage] = useState(null);
  // <video> element for the completed single-result preview — lets the Replay
  // button restart playback (currentTime=0 + play()) for mp4/webm results.
  const resultVideoRef = useRef(null);

  // Use ref to track current boxId to avoid closure issues in async polling
  const currentBoxIdRef = useRef(boxId);

  // Update ref whenever boxId changes
  useEffect(() => {
    currentBoxIdRef.current = boxId;
  }, [boxId]);

  // When the chat agent opens this modal pre-set (initialFeatureId +
  // initialSettings from the `editImage` tool), apply the requested operation +
  // its settings ONCE per open — on the false→true transition of `visible` — so
  // Aidea Wizard starts on the right edit. Anything the user changes afterward wins.
  const lastVisibleRef = useRef(false);
  useEffect(() => {
    const justOpened = visible && !lastVisibleRef.current;
    lastVisibleRef.current = visible;
    if (!justOpened) return;
    // Her açılışta geçmiş kapalı başlasın (Onayla ile kapatınca sıfırlamıyoruz;
    // flash olmaması için burada temizleniyor).
    setVersionsOpen(false);
    setPendingVersionUrl(null);
    // Mode B is never persisted across close/reopen (deliberate — see plan);
    // a derivative in flight keeps polling via the existing localStorage keys
    // and reopens into Mode A showing progress.
    setModalMode("generate");
    setEditVersion(null);
    setReconfiguring(false);
    setAdvancedOpen(false);
    setExtendPrompt("");
    setReferenceImage(null);
    setPickingExtendLibrary(false);
    setExtendLibraryFiles([]);
    setExtendLibraryLoading(false);
    setExtendLibraryError(null);
    setPickingExtendScene(false);
    setExtendReferenceUploading(false);
    setExtendReferenceError(null);
    if (initialFeatureId) {
      const f = IMAGE_EDIT_FEATURES.find((x) => x.id === initialFeatureId);
      if (f) setSelectedFeature(f);
    }
    const s = initialSettings || {};
    if (typeof s.prompt === "string" && s.prompt) setPrompt(s.prompt);
    if (s.targetLanguage) setTargetLanguage(s.targetLanguage);
    if (typeof s.count === "number") setCount(s.count);
    if (s.aspectRatio) setSelectedAspectRatio(s.aspectRatio);
    if (typeof s.scaleFactor === "number") setScaleFactor(s.scaleFactor);
    if (Array.isArray(s.textChanges) && s.textChanges.length) {
      setTextChanges(
        s.textChanges.map((t) => ({
          originalText: t.originalText || "",
          newText: t.newText || "",
          location: "",
          customLocation: "",
        }))
      );
    }
    if (s.mode === "all" || s.mode === "region") setEditMode(s.mode);
    if (Array.isArray(s.targetTexts) && s.targetTexts.length) {
      // Saved settings from before targets carried a location are plain
      // strings — accept both shapes so an older draft still restores.
      setTargetTexts(
        s.targetTexts.map((t) =>
          t && typeof t === "object"
            ? {
                text: String(t.text ?? ""),
                location: String(t.location ?? ""),
                customLocation: String(t.customLocation ?? ""),
              }
            : { text: String(t), location: "", customLocation: "" }
        )
      );
    }
    // Provider her açılışta "gemini"den başlar — önceki oturumdan kalan
    // (state hiç unmount olmadığından "son kullanılan" gibi davranan) provider
    // değeri kullanıcıyı şaşırtmasın. Aidea Wizard açıkça bir provider
    // istiyorsa (s.provider) o kazanır; aksi halde varsayılan gemini.
    setProvider(s.provider || "gemini");
    if (typeof s.durationSec === "number") {
      setDuration(s.durationSec);
    } else {
      // Süre PRESELECT: pencere hesaplanabiliyorsa açılışta Otomatik yerine
      // öğenin sahne penceresine en yakın desteklenen süre seçili gelir —
      // her provider, her döngü modu. Pencere yoksa Otomatik kalır.
      setDuration(defaultDurationForWindow(s.provider || "gemini", elementWindowSec));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialFeatureId, initialSettings]);

  // Re-derive duration if the currently selected value is not valid for the new
  // provider: preselect the new provider's nearest-allowed duration to the scene
  // window (null/Otomatik when no window). A manual "Otomatik" (null) stays put.
  useEffect(() => {
    const validDurations = supportedDurationsForProvider(provider);
    if (duration !== null && !validDurations.includes(duration)) {
      setDuration(defaultDurationForWindow(provider, elementWindowSec));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, duration]);

  useEffect(() => {
    let cancelled = false;
    if (!visible || selectedFeature.id !== "generate-from-reference" || !navId) {
      return undefined;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const trainingId = urlParams.get("id");
    if (!trainingId) return undefined;

    SceneAudioService.getSceneAudios({ trainingId })
      .then((data) => {
        if (cancelled) return;
        const scenes = data && Array.isArray(data.scenes) ? data.scenes : [];
        const scene = scenes.find((item) => item.navId === navId);
        const narrationAudio =
          scene?.audios?.find((audio) => audio.narrationText) || scene?.audios?.[0];
        setSceneNarrationText(narrationAudio?.narrationText || "");
      })
      .catch(() => {
        if (!cancelled) setSceneNarrationText("");
      });

    return () => {
      cancelled = true;
    };
  }, [visible, selectedFeature.id, navId]);

  // Blur active CKEditor instances when modal opens to prevent re-init conflicts
  useEffect(() => {
    if (visible && typeof CKEDITOR !== "undefined") {
      try {
        Object.keys(CKEDITOR.instances).forEach((id) => {
          const instance = CKEDITOR.instances[id];
          if (instance?.focusManager?.hasFocus) {
            instance.focusManager.blur(true);
          }
        });
      } catch (e) {
        // ignore
      }
    }
  }, [visible]);

  // Load persisted job result when modal opens
  useEffect(() => {
    if (visible && boxId) {
      console.log(`[useEffect] Loading job for boxId: ${boxId}`);
      const savedJob = localStorage.getItem(`image-edit-job-${boxId}`);

      const savedLoop = localStorage.getItem("animation-loop");
      if (savedLoop !== null) {
        setLoop(savedLoop === "true");
      }

      const savedTransparent = localStorage.getItem("animation-transparent");
      if (savedTransparent !== null) {
        setTransparent(savedTransparent === "true");
      }

      if (savedJob) {
        try {
          const jobData = JSON.parse(savedJob);
          console.log(`[useEffect] Found saved job:`, jobData);
          setJob({
            id: jobData.jobId,
            status: jobData.status,
            result: jobData.result,
            featureId: jobData.featureId,
            progress: jobData.progress || 0,
            error: jobData.error || null,
            settings: jobData.settings,
          });

          // Restore the feature that was used for this job — UNLESS the caller
          // explicitly requested a feature for this open (Aidea Wizard's
          // initialFeatureId). The user's current intent must win over a stale
          // persisted job; otherwise opening "Change Text" snaps back to the
          // previous "Translate" job that is still in localStorage.
          if (jobData.featureId && !initialFeatureId) {
            const feature = IMAGE_EDIT_FEATURES.find((f) => f.id === jobData.featureId);
            if (feature) {
              setSelectedFeature(feature);
            }
            // Animate: put the form back to what this job was submitted with
            // (the open-transition effect above has just reset provider/süre
            // to defaults) so "Yeniden üret" starts from the real settings.
            const s = jobData.featureId === "animate" ? jobData.settings : null;
            if (s) {
              if (typeof s.prompt === "string") setPrompt(s.prompt);
              if (s.provider && PROVIDER_OPTIONS.some((o) => o.value === s.provider)) {
                setProvider(s.provider);
              }
              if (typeof s.durationSec === "number") setDuration(s.durationSec);
              if (typeof s.loop === "boolean") setLoop(s.loop);
            }
          }

          // If job was still processing, resume polling (only if not already polling this job)
          if (jobData.status !== "completed" && jobData.status !== "failed") {
            if (!activePollingJobs.has(jobData.jobId)) {
              checkJobStatus(jobData.jobId, 0, boxId);
            }
          }
        } catch (err) {
          console.error("Failed to load saved job:", err);
        }
      } else {
        // No saved job for this box, clear any previous job states
        console.log(`[useEffect] No saved job for boxId: ${boxId}, clearing states`);
        setJob(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, boxId, initialFeatureId]);

  const checkJobStatus = async (jobId, attempt = 0, expectedBoxId = null) => {
    const maxAttempts = 150;
    const apiUrl = process.env.MW_API_URL;

    if (!jobId || attempt >= maxAttempts) {
      activePollingJobs.delete(jobId);
      return;
    }

    // Mark this job as actively polling (prevent duplicates)
    if (attempt === 0) {
      if (activePollingJobs.has(jobId)) return;
      activePollingJobs.add(jobId);
    }

    // Retrieve the boxId this job belongs to from localStorage
    const jobMetaKey = `image-edit-job-meta-${jobId}`;
    const jobMeta = localStorage.getItem(jobMetaKey);
    const jobMetaParsed = jobMeta ? JSON.parse(jobMeta) : null;
    const jobOwnerBoxId = jobMetaParsed ? jobMetaParsed.boxId : expectedBoxId;
    const isAnimateJob = jobMetaParsed?.featureId === "animate";

    // Use ref to get current boxId to avoid closure issues
    const currentBoxId = currentBoxIdRef.current;

    // If there is no boxId context (e.g. opened from AddImageModal), always update state.
    // Otherwise only update if this job belongs to the currently displayed photo.
    const shouldUpdateState = !currentBoxId || (jobOwnerBoxId && jobOwnerBoxId === currentBoxId);
    const targetBoxId = jobOwnerBoxId;

    // Debug logging to track polling behavior
    console.log(
      `[Job Polling] JobId: ${jobId}, Owner: ${jobOwnerBoxId}, Current: ${currentBoxId}, UpdateState: ${shouldUpdateState}`
    );

    try {
      // Use different status endpoints based on feature
      const statusUrl = isAnimateJob
        ? `${apiUrl}api/animation/v2/${jobId}`
        : `${apiUrl}api/image-editing/job/${jobId}`;

      const response = await fetch(statusUrl, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      // Handle 404 - Job not found (probably deleted from history)
      if (response.status === 404) {
        console.log(`[Job Cleanup] Job ${jobId} not found (404), cleaning up localStorage`);

        // Clear job metadata
        localStorage.removeItem(`image-edit-job-meta-${jobId}`);

        // Clear job data for this box (use targetBoxId if available, or currentBoxId)
        const boxIdToClean = targetBoxId || currentBoxId;
        if (boxIdToClean) {
          console.log(`[Job Cleanup] Removing localStorage for boxId: ${boxIdToClean}`);
          localStorage.removeItem(`image-edit-job-${boxIdToClean}`);
        }

        // If this job belongs to current photo, clear states
        if (shouldUpdateState) {
          console.log(`[Job Cleanup] Clearing states for current photo`);
          setJob(null);
        }

        // Stop polling
        return;
      }

      if (!response.ok) {
        throw new Error(i18n.t("Failed to check job status"));
      }

      const jobData = await response.json();
      const status = jobData?.status || jobData?.state || "unknown";
      // animate-v2 returns progress as 0..1; legacy image-editing returns 0..100
      let progress;
      if (isAnimateJob) {
        const raw = typeof jobData?.progress === "number" ? jobData.progress : 0;
        progress = Math.round(raw * 100);
        if (status === "completed") progress = 100;
      } else {
        progress = jobData?.progress || 0;
        if (status === "processing" && progress === 0) {
          progress = 50;
        } else if (status === "completed") {
          progress = 100;
        }
      }

      // Only update state if this job belongs to the current photo
      if (shouldUpdateState) {
        console.log(`[State Update] JobId: ${jobId}, Status: ${status}, Progress: ${progress}`);
        setJob((prev) => (prev ? { ...prev, status, progress } : null));
      } else {
        console.log(
          `[State Update Skipped] JobId: ${jobId}, Owner: ${jobOwnerBoxId}, Current: ${currentBoxId}`
        );
      }

      if (status === "completed") {
        let result;
        if (isAnimateJob) {
          const requestedFmt = jobMetaParsed?.outputFormat;
          const preferred =
            requestedFmt === "gif"
              ? jobData.gifUrl
              : requestedFmt === "mp4"
                ? jobData.mp4Url
                : requestedFmt === "webp"
                  ? jobData.webpUrl
                  : null;
          const url =
            preferred ||
            pickAnimationApplyUrl({
              webpUrl: jobData.webpUrl,
              gifUrl: jobData.gifUrl,
              mp4Url: jobData.mp4Url,
            });
          result = {
            url,
            blobName: url,
            sizeInBytes: null,
            durationSeconds: jobData.durationSec,
            provider: jobData.provider || "veo",
            mp4Url: jobData.mp4Url,
            webpUrl: jobData.webpUrl,
            gifUrl: jobData.gifUrl,
            reducedGifUrl: jobData.reducedGifUrl,
            thumbnailUrl: jobData.thumbnailUrl,
          };
        } else {
          const base = jobData.result && typeof jobData.result === "object" ? jobData.result : null;
          if (base) {
            // Keep the original result (incl. details.generatedImages for the
            // multi-image grid) but guarantee a top-level `url` so the single
            // result preview can render translate / change-text / edit output.
            const urls = extractEditedImageUrls(jobData);
            result = {
              ...base,
              url: base.url || urls[0] || null,
            };
          } else {
            result = {
              url: jobData.convertedVideoUrl,
              blobName: jobData.convertedVideoUrl,
              sizeInBytes: jobData.convertedVideoUrlSize,
              durationSeconds: jobData.executionTimeSeconds,
              provider: "veo",
            };
          }
        }

        if (shouldUpdateState) {
          setJob((prev) => (prev ? { ...prev, result, status, progress: 100 } : null));
        }
        // Save to localStorage so it persists across modal close/open
        if (targetBoxId) {
          console.log(`[LocalStorage Write] Completed - BoxId: ${targetBoxId}, JobId: ${jobId}`);
          const existingJob = localStorage.getItem(`image-edit-job-${targetBoxId}`);
          const existingParsed = existingJob ? JSON.parse(existingJob) : null;
          const featureId = existingParsed?.featureId ?? null;
          const settings = existingParsed?.settings;
          localStorage.setItem(
            `image-edit-job-${targetBoxId}`,
            JSON.stringify({
              jobId,
              status,
              result,
              progress: 100,
              featureId,
              settings,
            })
          );
        }
        activePollingJobs.delete(jobId);
        return;
      }

      if (status === "failed" || status === "cancelled") {
        const errorMessage = isAnimateJob
          ? status === "cancelled"
            ? i18n.t("Cancelled")
            : mapAnimateErrorMessage(jobData.errorMessage)
          : mapAiProviderErrorMessage(jobData.error || jobData.message) ||
            jobData.error ||
            jobData.message ||
            i18n.t("Job failed");

        // Update state with error message
        if (shouldUpdateState) {
          setJob((prev) => (prev ? { ...prev, status, progress, error: errorMessage } : null));
        }

        if (targetBoxId) {
          const existingJob = localStorage.getItem(`image-edit-job-${targetBoxId}`);
          const existingParsed = existingJob ? JSON.parse(existingJob) : null;
          const featureId = existingParsed?.featureId ?? null;
          const settings = existingParsed?.settings;
          localStorage.setItem(
            `image-edit-job-${targetBoxId}`,
            JSON.stringify({
              jobId,
              status,
              result: null,
              progress: progress,
              featureId,
              settings,
              error: errorMessage,
            })
          );
        }
        activePollingJobs.delete(jobId);
        return;
      }

      // Save progress during any non-terminal state (covers both legacy and animate-v2 statuses)
      const isTerminal = status === "completed" || status === "failed" || status === "cancelled";
      if (targetBoxId && !isTerminal) {
        const existingJob = localStorage.getItem(`image-edit-job-${targetBoxId}`);
        const existingParsed = existingJob ? JSON.parse(existingJob) : null;
        localStorage.setItem(
          `image-edit-job-${targetBoxId}`,
          JSON.stringify({
            jobId,
            status,
            result: null,
            progress: progress,
            featureId: existingParsed?.featureId ?? null,
            settings: existingParsed?.settings,
          })
        );
      }

      setTimeout(() => {
        checkJobStatus(jobId, attempt + 1, expectedBoxId);
      }, 4000);
    } catch (err) {
      console.error("Error checking image edit job status:", err);
      activePollingJobs.delete(jobId);
    }
  };

  // Shared submit for BOTH animate-v2 derivative ops — "extend" (POST
  // api/animation/v2/extend) and "convert" (POST api/animation/v2/convert) —
  // then reuse the exact SAME checkJobStatus polling the generate flow
  // already relies on; no separate polling path for either. Called from
  // Mode B's rail (source = the currently viewed editVersion). job.result is
  // intentionally PRESERVED (not nulled) while the derivative job is in
  // flight so the timing chip/loop label keep showing the pre-submit values
  // and the buttons render disabled instead of disappearing. loopOnComplete
  // is stamped onto the new job so the completion watcher above can snap the
  // loop toggle to match the result.
  const submitAnimateDerivative = async (path, body, { loopOnComplete } = {}) => {
    if (isExtending) return;
    setIsExtending(true);
    setError(null);
    try {
      const apiUrl = process.env.MW_API_URL;
      if (!apiUrl) throw new Error(i18n.t("API URL not configured"));
      const response = await fetch(`${apiUrl}${path}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        // Extend generates new provider footage, so /animation/v2/extend has a
        // balance pre-check like /animation/v2 does. Route it to the same global
        // popup handleSubmit uses instead of the inline red error strip, which
        // shows the raw "insufficient.balance" / "team.budget_exceeded" token with no amounts.
        // (Convert shares this helper but never bills — local ffmpeg only — so
        // it simply never hits this branch.)
        if (isCreditRefusal(errData)) {
          if (typeof onInsufficientBalance === "function") {
            onInsufficientBalance(errData);
            onClose?.();
          } else {
            setError(i18n.t("Insufficient balance") || "Yetersiz bakiye");
          }
          setIsExtending(false);
          return;
        }
        throw new Error(errData?.message || i18n.t("Failed to extend animation"));
      }
      const resultBody = await response.json();
      if (!resultBody?.jobId) throw new Error(i18n.t("Failed to extend animation"));

      extendJobIdRef.current = resultBody.jobId;
      setJob((prev) => ({
        ...(prev || {}),
        id: resultBody.jobId,
        status: "submitted",
        progress: 0,
        featureId: "animate",
        loop: loopOnComplete,
      }));

      localStorage.setItem(
        `image-edit-job-meta-${resultBody.jobId}`,
        JSON.stringify({
          boxId: boxId || null,
          featureId: "animate",
          outputFormat: "webp",
        })
      );
      if (boxId) {
        localStorage.setItem(
          `image-edit-job-${boxId}`,
          JSON.stringify({
            jobId: resultBody.jobId,
            status: "submitted",
            result: null,
            progress: 0,
            featureId: "animate",
          })
        );
      }

      checkJobStatus(resultBody.jobId, 0, boxId);
    } catch (err) {
      console.error("Extend/convert animation error:", err);
      setError(`${i18n.t("Failed to extend animation")}: ${err?.message || err?.toString() || ""}`);
      setIsExtending(false);
    }
  };

  // "once": one 8s continuation chunk. "to-target": enough chunks to reach
  // targetDurationSec MORE seconds of NEW footage (MW's own semantics — see
  // computeExtendChunkCount; the caller turns a desired TOTAL duration into a
  // delta before calling this, same as AnimationEditMode's own display math).
  // Output is always play-once (loopOnComplete: false) — gemini extend can't
  // preserve a loop. promptArg (Mode B textarea) is sent as the continuation
  // prompt — MW then skips its flash auto-prompt; providerArg switches the
  // chunk model between gemini/gemini-fast; referenceImageUrlArg (S4's
  // picker) seeds chunk 1 instead of the extracted last frame.
  const runExtend = ({
    sourceJobId,
    sourceFileId,
    mode,
    targetDurationSec,
    prompt: promptArg,
    provider: providerArg,
    referenceImageUrl: referenceImageUrlArg,
  }) => {
    const body = { mode };
    if (sourceJobId) body.sourceJobId = sourceJobId;
    if (sourceFileId != null) body.sourceFileId = sourceFileId;
    if (mode === "to-target" && typeof targetDurationSec === "number") {
      body.targetDurationSec = targetDurationSec;
    }
    if (promptArg && promptArg.trim()) body.prompt = promptArg.trim();
    if (providerArg) body.provider = providerArg;
    if (referenceImageUrlArg) body.referenceImageUrl = referenceImageUrlArg;
    return submitAnimateDerivative("api/animation/v2/extend", body, {
      loopOnComplete: false,
    });
  };

  // Pure ffmpeg re-encode between loop/play-once — works for any provider's
  // result. `loop` is the TARGET the caller wants (not the source's current
  // loop) — the caller (Mode B rail) picks it.
  const runConvert = ({ sourceJobId, sourceFileId, loop: targetLoop }) => {
    const body = { loop: !!targetLoop };
    if (sourceJobId) body.sourceJobId = sourceJobId;
    if (sourceFileId != null) body.sourceFileId = sourceFileId;
    return submitAnimateDerivative("api/animation/v2/convert", body, {
      loopOnComplete: !!targetLoop,
    });
  };

  // Upload a reference garment/product file to the cloud and store its URL.
  // Uses the image-editing /upload endpoint (multipart) which returns { url }.
  // Accepted reference image types. Anything else (mp3, pdf, …) is rejected
  // before upload with a clear warning.
  const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

  const handleReferenceUpload = useCallback(async (file) => {
    if (!file) return;
    setReferenceError(null);
    // 500 MB üst sınırı: aşan dosyayı kabul etme.
    if (isFileTooLarge(file)) {
      setReferenceError(fileTooLargeMessage());
      return;
    }
    // Only accept plain PNG/JPG/JPEG. Reject everything else (mp3, gif, …).
    const typeOk =
      ACCEPTED_IMAGE_TYPES.includes((file.type || "").toLowerCase()) ||
      /\.(png|jpe?g)$/i.test(file.name || "");
    if (!typeOk) {
      setReferenceError(i18n.t("ReplaceProduct.InvalidFileType"));
      return;
    }
    setReferenceUploading(true);
    try {
      const apiUrl = process.env.MW_API_URL;
      if (!apiUrl) {
        throw new Error(i18n.t("API URL not configured"));
      }
      const formData = new FormData();
      formData.append("image", file);
      const response = await fetch(`${apiUrl}api/image-editing/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.message || i18n.t("Failed to upload image"));
      }
      const data = await response.json();
      if (!data?.url) {
        throw new Error(i18n.t("Failed to upload image"));
      }
      setReferenceImageUrl(data.url);
    } catch (err) {
      console.error("Reference image upload error:", err);
      setReferenceError(err?.message || i18n.t("Failed to upload image"));
    } finally {
      setReferenceUploading(false);
    }
  }, []);

  // Open the training library picker and fetch the user's uploaded images
  // (api/ideal-studio/files). The chosen image becomes referenceImageUrl.
  const openLibraryPicker = useCallback(async () => {
    setPickingLibrary(true);
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const res = await fetchIdealStudioFiles({
        limit: 100,
        offset: 0,
        fileType: ["image", "image/gif", "image/jpeg", "image/png", "image/svg+xml", "image/webp"],
      });
      const files = (res && Array.isArray(res.files) ? res.files : [])
        .map((f) => ({ url: f.fileVirtualPath, name: f.fileName }))
        .filter((f) => f.url);
      setLibraryFiles(files);
    } catch (e) {
      console.error("Library images load error:", e);
      setLibraryError(i18n.t("files_load_error") || "Dosyalar yüklenemedi");
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  const handleSubmit = async () => {
    // "Aidea Akıllı Öğeler" tek istisna: arkasında REST endpoint değil bir Redux
    // thunk var. İlerleme/hata canvas kutusunun üzerinde gösterildiği için modal
    // hemen kapanıyor — burada poll edilecek bir jobId yok.
    if (selectedFeature.id === "aidea-smart-elements") {
      if (typeof onDecomposeSmartElements !== "function") {
        setError(i18n.t("aideaSmartElementsFailed"));
        return;
      }
      onDecomposeSmartElements(boxId);
      handleClose();
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setReconfiguring(false);
    const urlParams = new URLSearchParams(window.location.search);
    const trainingId = urlParams.get("id");

    let hadError = false;

    // Snapshot of what THIS animate request carries (provider/süre/döngü/
    // prompt) — pinned onto the job so the Üret step shows the submitted
    // values even after the form state is reset by a close/reopen.
    const animateSettings =
      selectedFeature.id === "animate"
        ? buildAnimateJobSettings({ provider, duration, loop, prompt, elementWindowSec })
        : undefined;

    try {
      const apiUrl = process.env.MW_API_URL;

      if (!apiUrl) {
        throw new Error(i18n.t("API URL not configured"));
      }

      let body = {
        imageUrl: editSourceUrl,
        idealStudioTrainingId: trainingId,
        navId,
        boxId,
        sceneIndex,
      };

      if (selectedFeature.id === "animate") {
        body = {
          // Video modunda kaynak bir mp4; canlandırma videonun ÜRETİLDİĞİ orijinal
          // fotoya koşmalı (video yeniden canlandırılamaz).
          imageUrl: isVideoSource ? videoSourceImageUrl || imageUrl : editSourceUrl,
          provider: provider,
          prompt: prompt,
          // Sunucu webp+gif+mp4'ün hepsini üretiyor; UI'daki format seçimi
          // kaldırıldı, istek bugüne kadarki varsayılan webp'i göndermeye devam ediyor.
          outputs: ["webp"],
          loop: loop,
          transparent: transparent,
          // Otomatik (artık yalnız ELLE seçilir — pencere biliniyorsa Süre
          // preselect ile dolu açılır): Bir Kez Oynat + bilinen pencere →
          // en yakın desteklenen süre; aksi halde eski davranış (gemini→4,
          // pixverse/grok→server varsayılanı). Kural buildAnimateJobSettings
          // içinde — gönderilen değer ile Üret adımında gösterilen değer aynı
          // kaynaktan gelsin diye.
          ...(animateSettings.durationSec !== null
            ? { durationSec: animateSettings.durationSec }
            : {}),
          trainingId: trainingId,
          boxId: boxId,
          navId: navId,
          sceneIndex: String(sceneIndex),
        };
      } else if (selectedFeature.id === "remove-object") {
        body.prompt = prompt;
        // Optional bounding box the user drew over the object to erase
        // (normalized {x,y,width,height} 0-1). Mirrors replace-product's
        // targetRegion. When present the backend can inpaint just that area.
        if (targetRegion) body.targetRegion = targetRegion;
      } else if (selectedFeature.id === "remove-text") {
        body.mode = editMode;
        if (editMode === "region") {
          // Objects, so each target carries its location hint. A target with
          // no location is sent as a bare string — the backend accepts both,
          // and this keeps the payload identical to before when unused.
          body.targetTexts = targetTexts
            .map((t) => ({ text: t.text.trim(), location: targetLocationOf(t).trim() }))
            .filter((t) => t.text)
            .map((t) => (t.location ? t : t.text));
        }
      } else if (selectedFeature.id === "translate") {
        body.targetLanguage = targetLanguage;
        body.mode = editMode;
        if (editMode === "region") {
          // Translate has no location concept server-side — plain strings.
          body.targetTexts = targetTexts.map((t) => t.text.trim()).filter(Boolean);
        }
      } else if (selectedFeature.id === "generate-similar") {
        body.count = count;
        body.provider = "gemini-nano";
      } else if (selectedFeature.id === "generate-image-from-image") {
        body = {
          imageUrl: editSourceUrl,
          prompt: prompt,
          idealStudioTrainingId: trainingId,
          sceneIndex: sceneIndex,
          boxId: boxId,
          navId: navId,
        };
      } else if (selectedFeature.id === "resize") {
        body = {
          imageUrl: editSourceUrl,
          aspectRatio: selectedAspectRatio,
          idealStudioTrainingId: trainingId,
          sceneIndex: sceneIndex,
          boxId: boxId,
          navId: navId,
        };
      } else if (selectedFeature.id === "upscale") {
        body = {
          imageUrl: editSourceUrl,
          scaleFactor: scaleFactor,
          idealStudioTrainingId: trainingId,
          sceneIndex: sceneIndex,
          boxId: boxId,
          navId: navId,
        };
      } else if (selectedFeature.id === "generate-from-reference") {
        body = {
          imageUrl: editSourceUrl,
          prompt: prompt,
          count: 1,
          provider: "gemini-nano",
          idealStudioTrainingId: trainingId,
          sceneIndex: sceneIndex,
          boxId: boxId,
          navId: navId,
          narration: sceneNarrationText,
        };
      } else if (selectedFeature.id === "replace-product") {
        body = {
          imageUrl: editSourceUrl,
          referenceImageUrl: referenceImageUrl,
          targetObject: targetObject,
          prompt: prompt,
          adaptStyle: adaptStyle,
          idealStudioTrainingId: trainingId,
          sceneIndex: sceneIndex,
          boxId: boxId,
          navId: navId,
        };
        // targetRegion is optional — only send it when the user drew a box.
        if (targetRegion) body.targetRegion = targetRegion;
      } else if (selectedFeature.id === "replace-background") {
        body = {
          imageUrl: editSourceUrl,
          referenceImageUrl: referenceImageUrl,
          prompt: prompt,
          idealStudioTrainingId: trainingId,
          sceneIndex: sceneIndex,
          boxId: boxId,
          navId: navId,
        };
        // targetRegion is optional — only send it when the user drew a box.
        if (targetRegion) body.targetRegion = targetRegion;
      } else if (selectedFeature.id === "change-image-text") {
        body = {
          imageUrl: editSourceUrl,
          idealStudioTrainingId: trainingId,
          sceneIndex: sceneIndex,
          boxId: boxId,
          navId: navId,
          textChanges: textChanges
            .filter((t) => t.originalText || t.newText)
            .map((t) => ({
              originalText: t.originalText,
              newText: t.newText,
              location: t.location === "custom" ? t.customLocation : t.location,
            })),
        };
      }

      // Use MW_API_URL for all features
      const fetchUrl = `${apiUrl}${selectedFeature.endpoint}`;

      const response = await fetch(fetchUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (isCreditRefusal(errData)) {
          if (typeof onInsufficientBalance === "function") {
            onInsufficientBalance(errData);
            onClose?.();
          }
          return;
        }
        throw new Error(errData?.message || i18n.t("Failed to submit animation job"));
      }

      try {
        const result = await response.json();
        if (result?.jobId) {
          console.log(
            `[Job Start] BoxId: ${boxId}, JobId: ${result.jobId}, Feature: ${selectedFeature.id}`
          );

          // Set consolidated job state. loop = the toggle value this job was
          // submitted with — liveJobToEditVersion() reads it back to
          // label the convert button once the job completes.
          setJob({
            id: result.jobId,
            status: result.status || "submitted",
            result: null,
            featureId: selectedFeature.id,
            progress: 0,
            loop: selectedFeature.id === "animate" ? loop : undefined,
            settings: animateSettings,
          });

          // Save loop preference
          localStorage.setItem("animation-loop", String(loop));

          // Save job metadata mapping (jobId -> featureId/boxId). checkJobStatus
          // reads this to pick the right status endpoint (animate vs
          // image-editing) — it must be written unconditionally, even when
          // there's no boxId yet (e.g. editing a freshly generated image in
          // AddImageModal that hasn't been placed on a scene). Previously this
          // was gated behind `if (boxId)`, so "Animate Image" jobs started
          // without a boxId silently polled the wrong endpoint and 404'd.
          localStorage.setItem(
            `image-edit-job-meta-${result.jobId}`,
            JSON.stringify({
              boxId: boxId || null,
              featureId: selectedFeature.id,
              outputFormat: selectedFeature.id === "animate" ? "webp" : undefined,
            })
          );

          if (boxId) {
            console.log(`[LocalStorage Init] BoxId: ${boxId}, JobId: ${result.jobId}`);
            // Save job status for this specific photo
            localStorage.setItem(
              `image-edit-job-${boxId}`,
              JSON.stringify({
                jobId: result.jobId,
                status: result.status || "submitted",
                result: null,
                progress: 0,
                featureId: selectedFeature.id,
                settings: animateSettings,
              })
            );
          }

          checkJobStatus(result.jobId, 0, boxId);
        }
      } catch (parseErr) {
        // Response body might be empty or non-JSON; ignore parsing errors for now
        console.warn("Could not parse image edit job response:", parseErr);
      }
    } catch (err) {
      console.error(`${selectedFeature.name} start error:`, err);
      const errorMessage = err?.message || err?.toString() || i18n.t("Unknown error");
      hadError = true;
      setError(`${i18n.t("Failed to start animation")}: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptResult = (imageUrl = null) => {
    // Surface every silent-failure mode as a toast so the user (and the
    // console) see exactly why the approve button "did nothing".
    if (!job?.result) {
      console.warn("[ImageAnimator] Approve clicked but job.result is empty", {
        jobId: job?.id,
        status: job?.status,
      });
      setError(i18n.t("Result not ready yet") || "Result not ready yet");
      return;
    }

    // Owner directive: animate output applies in place (webp/gif → same
    // HotspotImages box, url swap only) — mp4 only wins when the job has no
    // image-format output (legacy jobs from before the webp default). Same
    // rule as JobWidgetHost's own "Canlandır" approve path — both route
    // through pickAnimationApplyUrl so webp/gif never lose to mp4.
    const finalUrl =
      imageUrl ||
      pickAnimationApplyUrl({
        webpUrl: job.result.webpUrl,
        gifUrl: job.result.gifUrl,
        mp4Url: job.result.mp4Url,
        fallbackUrl: job.result.url,
      });
    if (!finalUrl) {
      console.warn("[ImageAnimator] Approve clicked but result has no URL", job.result);
      setError(i18n.t("Result URL missing") || "Result URL missing");
      return;
    }

    const animationData = {
      url: finalUrl,
      blobName: job.result.blobName,
      sizeInBytes: job.result.sizeInBytes,
      durationSeconds: job.result.durationSeconds,
      provider: job.result.provider,
      loop: typeof job.loop === "boolean" ? job.loop : undefined,
      // extract-text: parent (onAcceptAnimation) needs the detected regions to
      // create editable text boxes — a plain URL swap is not enough here.
      operation: job.result.operation || selectedFeature.id,
      textBoxes: job.result.textBoxes || job.result.details?.textBoxes,
    };

    // Pin the modal's own boxId on the payload so the parent handler can
    // resolve against the SAME box the user was looking at when they
    // opened the modal, not against this.props.box at click time (which
    // can drift if the user selected another box while the job ran).
    const pinnedBoxId = boxId;

    // Mark the job approved server-side (same route the job tray's own
    // Onayla calls) so a result accepted here — bypassing the tray
    // entirely — doesn't linger under "Onay Bekliyor". Every feature, not
    // just animate: remove-object/change-bg/extract-text/... jobs are
    // image_editing rows in the same tray. The middleware pushes
    // approved=true to the widget over the socket. Best-effort, never
    // blocks the apply below.
    if (job.id) approveJob(job.id);

    if (pinnedBoxId && onAcceptAnimation) {
      // Box already exists on canvas → update its URL (parent should
      // use animationData.boxId rather than its own this.props.box.id)
      onAcceptAnimation({ ...animationData, boxId: pinnedBoxId });
    } else if (onAddToScene) {
      // Box not on canvas yet → add it
      onAddToScene(finalUrl);
    } else {
      // Neither callback wired — fail loudly so the engineer who mounted
      // this modal knows their handler set is incomplete.
      console.error("[ImageAnimator] Approve clicked but no handler available", {
        boxId: pinnedBoxId,
        hasAccept: !!onAcceptAnimation,
        hasAddToScene: !!onAddToScene,
      });
      setError(
        i18n.t("No handler to apply result") || "No handler to apply result — please report this."
      );
      return;
    }

    // Clear localStorage
    if (boxId) {
      localStorage.removeItem(`image-edit-job-${boxId}`);
      if (job.id) {
        localStorage.removeItem(`image-edit-job-meta-${job.id}`);
      }
    }

    // Clear job state
    setJob(null);

    // Reset modal
    setPrompt("");
    setSelectedFeature(IMAGE_EDIT_FEATURES.find((f) => f.id === "animate"));
    setTargetLanguage("Turkish");
    setCount(2);
    setProvider("gemini");
    setError(null);

    onClose();
  };

  const handleDismiss = () => {
    // Store jobId before clearing it
    const currentJobId = job?.id;

    // Clear job state
    setJob(null);
    setReconfiguring(false);

    // Clear from localStorage
    if (boxId) {
      localStorage.removeItem(`image-edit-job-${boxId}`);
      // Also clear job metadata if jobId exists
      if (currentJobId) {
        localStorage.removeItem(`image-edit-job-meta-${currentJobId}`);
      }
    }
    localStorage.removeItem(`animation-job-${boxId}`);
  };

  const resetModal = () => {
    setPrompt("");
    setTargetLanguage("Turkish");
    setCount(2);
    setProvider("gemini");
    setLoop(false);
    setTextChanges([{ originalText: "", newText: "", location: "", customLocation: "" }]);
    setEditMode("all");
    setTargetTexts([{ text: "", location: "", customLocation: "" }]);
    setError(null);
    setReferenceImageUrl("");
    setReferenceError(null);
    setReferenceUploading(false);
    setPickingReference(false);
    setPickingLibrary(false);
    setLibraryFiles([]);
    setLibraryError(null);
    setTargetObject("");
    setTargetRegion(null);
    setRegionSelectorOpen(false);
    // Don't reset job states here - they should persist until accepted/dismissed
  };

  const handleClose = () => {
    // Only reset form fields if there's no pending job result
    if (!job?.result || job?.status !== "completed") {
      resetModal();
    }
    onClose();
  };

  // Task 37 ASK 4: reuses handleClose's existing close behaviour as-is (an
  // in-flight job keeps polling via checkJobStatus's module-level
  // activePollingJobs guard + localStorage, same as a plain Cancel/backdrop
  // close today -- nothing here changes that), then reopens AnimatorPanel on
  // the scene the user came from and asks it to refresh preflight so a
  // just-created animation shows immediately.
  const handleBackToPanel = () => {
    handleClose();
    if (returnTo?.navId) {
      window.dispatchEvent(
        new CustomEvent("aidea:openAnimatorPanel", { detail: { navId: returnTo.navId } })
      );
    }
  };

  // Top-bar "Chatbot ile düzenle": hand this image off to the Aidea Assistant
  // chat. Start an inline-edit session pinned to this box, close the modal
  // (no popup-over-popup), open the chat, and seed the composer so the user is
  // prompted to describe the edit. From there each chat edit runs + previews
  // inline; the canvas image is only swapped when the user clicks "Uygula".
  const handleOpenChatEdit = () => {
    if (!imageUrl) return;
    startInlineEditSession({ boxId, navId, workingUrl: imageUrl });
    const seed = i18n.t("ImageChatEdit.SeedPrompt", {
      defaultValue: "Bu görseli şöyle düzenlemek istiyorum: ",
    });
    // Stash so the composer can drain it even if it mounts AFTER the event
    // (the chat panel is opening on the same tick).
    if (typeof window !== "undefined") window.__aideaChatSeed = seed;
    handleClose();
    window.dispatchEvent(new CustomEvent("aidea:openAssistant"));
    window.dispatchEvent(new CustomEvent("aidea:seedChatInput", { detail: { text: seed } }));
  };

  // Mevcut sahnenin kayıtlı anlatım metnini scene-audios endpoint'inden çekip
  // animasyon promptuna doldurur — aynı kaynak/davranış
  // InfographicSceneModal.handleFetchSceneText ile birebir aynı (bkz. o
  // dosyadaki yorum).
  const handleFetchSceneText = async () => {
    setFetchingSceneText(true);
    try {
      const data = await SceneAudioService.getSceneAudios({});
      const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
      const scene = scenes.find((s) => s.navId === navId);
      const narrationAudio =
        scene?.audios?.find((a) => a.narrationText) || scene?.audios?.[0] || null;
      if (narrationAudio?.narrationText) {
        setPrompt((prev) =>
          prev && prev.trim()
            ? prev.replace(/\s+$/, "") + "\n" + narrationAudio.narrationText
            : narrationAudio.narrationText
        );
      }
    } catch (e) {
      console.warn("[ImageAnimatorModal] fetch scene narration failed", e);
    } finally {
      setFetchingSceneText(false);
    }
  };

  const isPromptMissing =
    selectedFeature.requiresPrompt &&
    selectedFeature.id !== "animate" &&
    !prompt.trim() &&
    // remove-object: a drawn region alone is enough — prompt becomes optional.
    !(selectedFeature.id === "remove-object" && targetRegion);

  const isTextChangeIncomplete =
    selectedFeature.id === "change-image-text" &&
    textChanges.some(
      (t) =>
        !t.originalText.trim() ||
        !t.location ||
        (t.location === "custom" && !t.customLocation.trim())
    );

  // Region mode (remove-text / translate) needs at least one non-empty target text.
  const isTargetTextsMissing =
    (selectedFeature.id === "remove-text" || selectedFeature.id === "translate") &&
    editMode === "region" &&
    !targetTexts.some((t) => t.text.trim());

  // replace-product cannot run without a reference object image AND a selected
  // region (the object the user marked on the source image). The text prompt is
  // an optional disambiguation. replace-background needs the reference image but
  // the region is optional (omit → replace the whole background).
  const isReferenceImageMissing = selectedFeature.requiresReferenceImage && !referenceImageUrl;
  const isTargetRegionMissing = selectedFeature.requiresRegion && !targetRegion;

  // Üretim uçuşta mı? POST cevabı geldikten (isSubmitting=false) sonra da job
  // terminal olana kadar devam ediyor. Eskiden disabled bu iki durumu birlikte
  // kontrol ederken etiket yalnızca isSubmitting’e bakıyordu; sonuç olarak buton
  // pasif kalmaya devam ederken yazı ~1 sn sonra "Başlat"a dönüyordu. Artık ikisi
  // de aynı bayrağı kullanıyor: buton pasif olduğu sürece "İşleniyor" yazar.
  const isJobInFlight = !!(
    job &&
    job.status &&
    job.status !== "completed" &&
    job.status !== "failed"
  );
  const isProcessing = isSubmitting || isJobInFlight;

  // sideLayout sol kolonu (bkz. bodyContent): ORİJİNAL/SONUÇ tek panelde,
  // "Görseli Canlandır"daki Döngü/Bir Kez Oynat gibi bir sekmeyle. Bu
  // özellik için üretim başlayınca ya da sonuç gelince SONUÇ'a kendiliğinden
  // geçer; kullanıcı istediği an ORİJİNAL'e dönebilir.
  const [sidePane, setSidePane] = useState("original");
  const sideHasJob =
    job?.featureId === selectedFeature.id &&
    (isJobInFlight || job?.status === "completed" || job?.status === "failed");
  useEffect(() => {
    if (isSubmitting || sideHasJob) setSidePane("result");
  }, [isSubmitting, sideHasJob]);

  // S256 redesign (animate feature only): derived — not stored — completed
  // flag and 1-2-3 wizard step (Ayarla/Üret/Düzenle), used to pick which
  // body layout renders below and to drive the step indicator. Mirrors the
  // exact isCompleted formula already computed twice inline further down in
  // the Result pane (job?.featureId === selectedFeature.id check), hoisted
  // here so it's available before bodyContent without duplicating job/modal
  // state. Every other feature ignores wizardStep entirely.
  const isCompletedForFeature =
    job?.status === "completed" && job?.featureId === selectedFeature.id;
  // reconfiguring only matters while a completed result is parked: it sends
  // the author back to Ayarla without discarding that result. A running job
  // always shows Üret (nothing to configure mid-flight).
  const wizardStep =
    modalMode === "edit" ? 3 : isProcessing ? 2 : isCompletedForFeature && !reconfiguring ? 2 : 1;

  // Settings the CURRENT animate job was submitted with (snapshot pinned on
  // the job by handleSubmit / restored from localStorage). The Üret step and
  // the result summary read these, never the live form — see
  // animateJobSettings.es6 for why the live form cannot be trusted there.
  const jobDisplaySettings = resolveAnimateDisplaySettings(job, {
    provider,
    duration,
    loop,
    prompt,
  });
  const jobDisplayProviderLabel =
    PROVIDER_OPTIONS.find((o) => o.value === jobDisplaySettings.provider)?.label ||
    jobDisplaySettings.provider;

  // Back to Ayarla with the form pre-filled from the parked job, so "change
  // one thing and regenerate" starts from what actually ran. The result stays
  // in `job` until the next submit replaces it (Sonuca dön brings it back).
  const startReconfigure = () => {
    if (isProcessing) return;
    if (modalMode === "edit") exitEditMode();
    if (job?.settings && job.featureId === "animate") {
      const s = job.settings;
      if (typeof s.prompt === "string") setPrompt(s.prompt);
      if (s.provider && PROVIDER_OPTIONS.some((o) => o.value === s.provider)) {
        setProvider(s.provider);
      }
      if (typeof s.durationSec === "number") setDuration(s.durationSec);
      if (typeof s.loop === "boolean") setLoop(s.loop);
    }
    setReconfiguring(true);
  };
  const showParkedResult = () => {
    if (modalMode === "edit") exitEditMode();
    setReconfiguring(false);
  };
  // Step badges the author may jump to from the current step (done steps
  // only; nothing is clickable while a job is running).
  const canJumpToStep = (step) => {
    if (isProcessing) return false;
    if (step === 1) return wizardStep > 1 && (isCompletedForFeature || modalMode === "edit");
    if (step === 2) return wizardStep !== 2 && isCompletedForFeature;
    return false;
  };
  const jumpToStep = (step) => {
    if (!canJumpToStep(step)) return;
    if (step === 1) startReconfigure();
    else if (step === 2) showParkedResult();
  };

  // Üret step's "elapsed" readout (S256 redesign). No new poll/interval: the
  // existing checkJobStatus loop already re-renders this component every 4s
  // while a job is in flight (setJob calls), which is enough fidelity for a
  // "genellikle 40-90 sn sürer" hint. Just a start-timestamp captured once
  // per processing run and cleared when it ends.
  const processingStartRef = useRef(null);
  useEffect(() => {
    if (isProcessing) {
      if (!processingStartRef.current) processingStartRef.current = Date.now();
    } else {
      processingStartRef.current = null;
    }
  }, [isProcessing]);

  // Görüntülenen/seçilen şey zaten bir animasyon çıktısı mı (kutu videosu VEYA
  // geçmişten pending seçilen video/webp/gif)? Öyleyse "Başlat" deaktif — üretilmiş
  // çıktı yeniden animate edilemez.
  const selectedIsAnimationOutput = /\.(mp4|webm|mov|m4v|webp|gif)(\?|#|$)/i.test(
    pendingVersionUrl || imageUrl || ""
  );

  // Zaten üretilmiş animasyon (webp/mp4) görüntülenirken animate inputları
  // (provider/süre/döngü/format/prompt) salt-okunur: yeniden üretilemez, sadece
  // gösterilir — "Başlat" gibi. isSubmitting sırasında da kilitli.
  const animateInputsLocked = isSubmitting || selectedIsAnimationOutput;

  // Already-animated box (mp4/webp/gif source with a resolved family row) —
  // same condition that already locks the feature to "animate" and the inputs
  // above via animateInputsLocked. The provider/Süre/Döngü/format card and the
  // Prompt textarea are dead weight here (inputs are read-only and
  // re-generation is disabled) — permanently collapsed behind a metadata
  // summary; Önizle ve Düzenle (Mode B) is the only way forward.
  const isAlreadyAnimatedBox = isVideoSource && !!currentVersionRow;
  const summaryProviderLabel =
    PROVIDER_OPTIONS.find((o) => o.value === provider)?.label || provider;
  const summaryDurationSec =
    typeof currentVersionRow?.durationSec === "number" ? currentVersionRow.durationSec : duration;
  const summaryPromptText = currentVersionRow?.prompt || prompt || "";

  // Kredi butonunun göründüğü işlemler ve tahmin endpoint'leri.
  // Backend eşlemesi: image-editing için tek route (:operation/credit) ve
  // image-edit-credit-map.ts; animate için /animation/v2/credit;
  // aidea-smart-elements için magic-element-engine'in kendi route'u
  // (/magic-element-engine/decompose/credit) — extract-text ile aynı sözleşme:
  // { requiredCredits, availableCredits, hasSufficientBalance, organizationId }.
  const CREDIT_ENDPOINTS = {
    animate: "api/animation/v2/credit",
    "remove-background": "api/image-editing/remove-background/credit",
    "remove-object": "api/image-editing/remove-object/credit",
    "remove-text": "api/image-editing/remove-text/credit",
    translate: "api/image-editing/translate-image-text/credit",
    "generate-similar": "api/image-editing/generate-similar/credit",
    "generate-image-from-image": "api/image-editing/generate-image-from-image/credit",
    "change-image-text": "api/image-editing/change-image-text/credit",
    resize: "api/image-editing/aspect-change/credit",
    upscale: "api/image-editing/upscale-image/credit",
    "generate-from-reference": "api/image-editing/generate-from-reference/credit",
    "replace-product": "api/image-editing/replace-product/credit",
    "extract-text": "api/image-editing/extract-text/credit",
    // Akıllı Öğeler: fiyat price_lists('edit','seedream-v5-pro-layerize')
    // satırından; gövde göndermeye gerek yok (buildCreditBody default {} döner),
    // endpoint her şeyi JWT'den türetir.
    "aidea-smart-elements": "api/magic-element-engine/decompose/credit",
  };
  const creditEndpoint = CREDIT_ENDPOINTS[selectedFeature.id];

  // Tahmin gövdesi: yalnızca FİYATI ETKİLEYEN alanlar. handleSubmit'in gerçek
  // gövdesiyle aynı değerleri kullanır, böylece buton üretimde kesilecek tutarı
  // gösterir (bkz. image-edit-credit-map.ts).
  const buildCreditBody = () => {
    switch (selectedFeature.id) {
      case "animate":
        // Süre seçilmediyse gönderme: backend Veo ailesinde 8s'e, diğerlerinde
        // kendi varsayılanına düşer — tahmin, üretimde işleyecek kuralla aynı.
        // transparent → popover'a Bria matting addon maliyeti de eklensin.
        return {
          provider,
          transparent,
          ...(duration !== null ? { durationSec: duration } : {}),
        };
      case "remove-text":
        // provider mode'a göre değişiyor (region → gemini-nano).
        return { mode: editMode };
      case "generate-similar":
        return { count };
      case "generate-from-reference":
        // handleSubmit bu işlemde sabit 1 gönderiyor.
        return { count: 1 };
      default:
        return {};
    }
  };

  // Butona tıklayınca aç/kapa. Açılışta backend'den tahmini maliyet + bakiye
  // çekilir; hiçbir job başlatılmaz, düşüm yapılmaz.
  const handleCreditCheck = async () => {
    if (!creditEndpoint) return;
    if (creditPopoverOpen) {
      setCreditPopoverOpen(false);
      return;
    }
    setCreditLoading(true);
    setCreditError(null);
    setCreditInfo(null);
    try {
      const apiUrl = process.env.MW_API_URL;
      const res = await fetch(`${apiUrl}${creditEndpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(buildCreditBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data?.message || i18n.t("CreditEstimate.Failed") || "Kredi sorgusu başarısız"
        );
      }
      setCreditInfo(data);
      setCreditPopoverOpen(true);
    } catch (err) {
      setCreditError(err?.message || String(err));
      setCreditPopoverOpen(true);
    } finally {
      setCreditLoading(false);
    }
  };

  // Provider/süre değişince eski tahmin yanlış olur — popover'ı kapat.
  useEffect(() => {
    setCreditPopoverOpen(false);
    setCreditInfo(null);
  }, [provider, duration, editMode, count, selectedFeature.id]);

  // Dışarı tıklayınca kapat (Görsel Ekle modalındaki davranışın aynısı).
  useEffect(() => {
    if (!creditPopoverOpen) return undefined;
    const onDocClick = (e) => {
      if (creditBtnRef.current && !creditBtnRef.current.contains(e.target)) {
        setCreditPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [creditPopoverOpen]);

  const footerContent = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: 12,
      }}
    >
      {/* Kredi tahmini — sol altta (Görsel Ekle modalıyla aynı yer). Footer
          düzenini burada kendimiz kuruyoruz: AppModal özel footer’ı
          .app-modal__footer sarmalayıcısı OLMADAN doğrudan Modal.Footer içine
          basıyor ve bu repodaki bootstrap sürümünde .modal-footer flex değil —
          bu yüzden justify-content/gap ve margin-right:auto işlemiyor, buton
          kendi satırında sağa yapışıyordu. */}
      {creditEndpoint && (
        <div ref={creditBtnRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={handleCreditCheck}
            disabled={creditLoading}
            title={i18n.t("CreditEstimate.Tooltip")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "9px 16px",
              borderRadius: "12px",
              border: `1px solid ${creditPopoverOpen ? "#c4b5fd" : "#dce3f3"}`,
              background: creditPopoverOpen ? "#eef2ff" : "#f8fafc",
              color: creditPopoverOpen ? "#7c3aed" : "#64748b",
              fontSize: "14px",
              fontWeight: 600,
              cursor: creditLoading ? "wait" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {creditLoading ? (
              <LuRefreshCw size={14} style={{ animation: "spin 0.8s linear infinite" }} />
            ) : (
              <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
            )}
            <span>
              {creditLoading ? i18n.t("CreditEstimate.Loading") : i18n.t("CreditEstimate.Button")}
            </span>
          </button>

          {creditPopoverOpen && (creditInfo || creditError) && (
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 10px)",
                left: 0,
                background: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                padding: "16px 20px",
                minWidth: "240px",
                boxShadow: "0 4px 24px rgba(15, 23, 42, 0.12)",
                zIndex: 3000075,
                fontSize: "13px",
                lineHeight: 1.6,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: -7,
                  left: 20,
                  width: 13,
                  height: 13,
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderTop: "none",
                  borderLeft: "none",
                  transform: "rotate(45deg)",
                }}
              />
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: 12,
                  fontSize: "14px",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
                {i18n.t("CreditEstimate.Title")}
              </div>

              {creditError ? (
                <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 600 }}>{creditError}</div>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ color: "#64748b" }}>{i18n.t("CreditEstimate.Cost")}</span>
                    <span style={{ fontWeight: 700, color: "#1e293b" }}>
                      {Number(creditInfo.requiredCredits).toFixed(2)}&nbsp;
                      <span style={{ color: "#8b95af", fontWeight: 400 }}>
                        {i18n.t("ImageHistory.Credits")}
                      </span>
                    </span>
                  </div>

                  {/* Faturalanan süre: Veo ailesi her zaman 8s, diğerleri seçilen süre */}
                  {creditInfo.billedDurationSeconds != null && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ color: "#64748b" }}>
                        {i18n.t("CreditEstimate.Duration") || "Süre"}
                      </span>
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>
                        {creditInfo.billedDurationSeconds}s
                      </span>
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 14,
                      paddingBottom: 14,
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>{i18n.t("CreditEstimate.Balance")}</span>
                    <span style={{ fontWeight: 700, color: "#1e293b" }}>
                      {Number(creditInfo.availableCredits).toFixed(2)}&nbsp;
                      <span style={{ color: "#8b95af", fontWeight: 400 }}>
                        {i18n.t("ImageHistory.Credits")}
                      </span>
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "7px 12px",
                      borderRadius: 8,
                      background: creditInfo.hasSufficientBalance
                        ? "rgba(124,58,237,0.08)"
                        : "rgba(239,68,68,0.08)",
                      color: creditInfo.hasSufficientBalance ? "#7c3aed" : "#ef4444",
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  >
                    {creditInfo.hasSufficientBalance
                      ? `✓ ${i18n.t("CreditEstimate.Sufficient")}`
                      : `✗ ${i18n.t("CreditEstimate.Insufficient")}`}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {/* Vazgeç + Başlat sağda; marginLeft:auto aradaki tüm boşluğu yiyor,
          böylece kredi butonu solda kalır. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>
        {/* Back on Ayarla with a finished result parked (Yeniden üret / step
            badge): let the author return to that result without having to
            regenerate. */}
        {selectedFeature.id === "animate" && reconfiguring && isCompletedForFeature && (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={showParkedResult}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {i18n.t("AnimEdit.BackToResult")} <LuArrowRight size={14} />
          </button>
        )}
        <button
          type="button"
          className="app-modal__btn app-modal__btn--ghost"
          onClick={handleClose}
        >
          {i18n.t("Cancel")}
        </button>
        {/* S256 redesign: once the animate feature's own job is completed (Sonuç
            step), the primary CTA becomes "Sahneye uygula" — the direct-apply
            path (handleAcceptResult) that already existed for the in-pane
            Accept button, just promoted to the footer per the mockup instead
            of requiring a trip through Önizle ve Düzenle. Every other
            feature/state keeps the exact Başlat/İşleniyor button unchanged.
            While the author is back on Ayarla (reconfiguring) the CTA is
            Başlat again — that is the whole point of going back. */}
        {selectedFeature.id === "animate" && isCompletedForFeature && !reconfiguring ? (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            onClick={() => handleAcceptResult()}
          >
            <LuCheck size={15} /> {i18n.t("AnimEdit.ApplyToScene")}
          </button>
        ) : (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            onClick={handleSubmit}
            disabled={
              !imageUrl ||
              // Kutu videosu VEYA geçmişten seçilen animasyon çıktısı → Başlat deaktif.
              // Üretilmiş çıktı yeniden canlandırılamaz; kullanıcı sadece geçmişten
              // seçip Onaylar.
              selectedIsAnimationOutput ||
              isProcessing ||
              isExtending ||
              selectedFeature.comingSoon ||
              isPromptMissing ||
              isTextChangeIncomplete ||
              isTargetTextsMissing ||
              isReferenceImageMissing ||
              isTargetRegionMissing ||
              referenceUploading
            }
          >
            {isProcessing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {i18n.t("Processing")}
              </>
            ) : selectedFeature.comingSoon ? (
              i18n.t("Yakında")
            ) : (
              i18n.t("Start")
            )}
          </button>
        )}
      </div>
    </div>
  );

  // Viewing the version already applied to the box (e.g. reopening the modal
  // on an already-animated image, or re-selecting the active row from
  // Geçmiş): applying it again writes the identical url → no visible change,
  // which reads as "Onayla did nothing". Render it as an inert "Applied"
  // state instead of a no-op confirm.
  const isEditVersionApplied = !!editVersion && editVersion.displayUrl === activeVersionUrl;
  // Rail's "Sahneye MP4 olarak ekle" applies editVersion.mp4Url, not
  // displayUrl — a separate comparison against the SAME activeVersionUrl
  // (which, when the box is video-source, already holds the box's current
  // mp4 — see isPlayableVideoExt/imageUrl above). Only meaningful for a
  // video-source box: an image-source box's activeVersionUrl is never an mp4.
  const isMp4VersionApplied =
    isPlayableVideoExt &&
    !!editVersion?.mp4Url &&
    assetKey(editVersion.mp4Url) === assetKey(activeVersionUrl);

  // Mode B footer: "Geri Dön" leaves edit mode and returns to the previous
  // step (the parked result, or Ayarla); "Vazgeç" closes the modal like it
  // does everywhere else. Before, the only exit was a "Vazgeç" that silently
  // acted as back — the author had no way to tell the two apart.
  const editFooterContent = (
    <>
      <button
        type="button"
        className="app-modal__btn app-modal__btn--ghost"
        onClick={exitEditMode}
        disabled={isExtending}
        style={{ marginRight: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <LuArrowLeft size={14} /> {i18n.t("AnimEdit.BackButton")}
      </button>
      <button type="button" className="app-modal__btn app-modal__btn--ghost" onClick={handleClose}>
        {i18n.t("Cancel")}
      </button>
      <button
        type="button"
        className="app-modal__btn app-modal__btn--primary"
        onClick={() => acceptEditVersion()}
        disabled={isExtending || !editVersion || isEditVersionApplied}
      >
        {isEditVersionApplied ? (
          <>
            <LuCheck size={12} /> {i18n.t("AnimEdit.Applied")}
          </>
        ) : (
          i18n.t("Confirm")
        )}
      </button>
    </>
  );

  // Shared scope UI for remove-text / translate: "all" vs "region" toggle plus,
  // in region mode, the list of target text strings (typed or picked via OCR).
  const renderRegionScope = () => {
    const isRemove = selectedFeature.id === "remove-text";
    return (
      <div style={{ marginBottom: "12px" }}>
        <Form.Label style={{ fontWeight: 600, color: "#333", fontSize: 13, display: "block" }}>
          {i18n.t("ImageEdit.Scope")}
        </Form.Label>
        <div style={{ display: "flex", gap: "8px", marginBottom: editMode === "region" ? 10 : 0 }}>
          {[
            {
              value: "all",
              labelKey: isRemove ? "ImageEdit.RemoveAllText" : "ImageEdit.TranslateAllText",
            },
            {
              value: "region",
              labelKey: isRemove
                ? "ImageEdit.RemoveSelectedText"
                : "ImageEdit.TranslateSelectedText",
            },
          ].map((opt) => {
            const isSelected = editMode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setEditMode(opt.value)}
                disabled={isSubmitting}
                style={{
                  flex: 1,
                  padding: "8px 10px",
                  border: `1.5px solid ${isSelected ? "#007bff" : "#e2e8f0"}`,
                  borderRadius: "8px",
                  backgroundColor: isSelected ? "#eef5ff" : "#fff",
                  color: isSelected ? "#007bff" : "#555",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                }}
              >
                {i18n.t(opt.labelKey)}
              </button>
            );
          })}
        </div>

        {editMode === "region" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <Form.Label style={{ fontWeight: 600, color: "#333", margin: 0, fontSize: 13 }}>
                {i18n.t("ImageEdit.TargetTexts")}
              </Form.Label>
              <button
                type="button"
                onClick={() => {
                  setTargetTexts([...targetTexts, { text: "", location: "", customLocation: "" }]);
                  setFocusLastAddedInput(true);
                }}
                disabled={isSubmitting}
                style={{
                  padding: "3px 10px",
                  border: "1.5px solid #007bff",
                  borderRadius: "6px",
                  backgroundColor: "#fff",
                  color: "#007bff",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + {i18n.t("Add")}
              </button>
            </div>
            <div ref={addableListRef}>
              {targetTexts.map((entry, idx) => (
                <div key={idx} style={{ marginBottom: "6px" }}>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <Form.Control
                      type="text"
                      placeholder={i18n.t("ImageEdit.TargetTextPlaceholder")}
                      value={entry.text}
                      onChange={(e) => {
                        const u = [...targetTexts];
                        u[idx] = { ...u[idx], text: e.target.value };
                        setTargetTexts(u);
                      }}
                      style={{
                        flex: 1,
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                        fontSize: "13px",
                      }}
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => setTargetTextSelectIdx(idx)}
                      disabled={isSubmitting || !imageUrl}
                      title={i18n.t("SelectText")}
                      style={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "0 10px",
                        border: "1.5px solid #007bff",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        color: "#007bff",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: isSubmitting || !imageUrl ? "not-allowed" : "pointer",
                        opacity: !imageUrl ? 0.5 : 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <LuScanLine size={14} />
                      {i18n.t("SelectText")}
                    </button>
                    {targetTexts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setTargetTexts(targetTexts.filter((_, i) => i !== idx))}
                        disabled={isSubmitting}
                        style={{
                          flexShrink: 0,
                          border: "none",
                          background: "none",
                          color: "#dc3545",
                          cursor: "pointer",
                          fontSize: "18px",
                          padding: "0 4px",
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {/* Location hint — remove-text only. Translate's endpoint has
                    no location parameter, so offering the control there would
                    collect input nothing consumes. */}
                  {isRemove && (
                    <>
                      <Form.Control
                        as="select"
                        value={entry.location}
                        onChange={(e) => {
                          const u = [...targetTexts];
                          u[idx] = { ...u[idx], location: e.target.value, customLocation: "" };
                          setTargetTexts(u);
                        }}
                        style={{
                          borderRadius: "6px",
                          border: "1px solid #ddd",
                          marginTop: "6px",
                          marginBottom: entry.location === "custom" ? "6px" : 0,
                          fontSize: "13px",
                        }}
                        disabled={isSubmitting}
                      >
                        {/* Optional, unlike "Metni Değiştir" where a location is
                          required: removal usually has one obvious target, and
                          forcing a pick would add a step to the common case. */}
                        <option value="">{i18n.t("SelectLocation")}</option>
                        {LOCATION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {i18n.t(opt.labelKey)}
                          </option>
                        ))}
                      </Form.Control>
                      {entry.location === "custom" && (
                        <Form.Control
                          type="text"
                          placeholder={i18n.t("EnterCustomLocationPlaceholder")}
                          value={entry.customLocation}
                          onChange={(e) => {
                            const u = [...targetTexts];
                            u[idx] = { ...u[idx], customLocation: e.target.value };
                            setTargetTexts(u);
                          }}
                          style={{
                            borderRadius: "6px",
                            border: "1px solid #ddd",
                            fontSize: "13px",
                          }}
                          disabled={isSubmitting}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  //test/
  // Mode B rail handlers. editSourceArg picks whichever of sourceJobId/
  // sourceFileId the currently-viewed version actually has (XOR, see the
  // editVersion shape) and forwards it as the derivative's source.
  const editSourceArg = (v) =>
    v.sourceJobId ? { sourceJobId: v.sourceJobId } : { sourceFileId: v.sourceFileId };

  const handleEditExtend = (mode, targetDurationSec) => {
    if (!editVersion || isExtending) return;
    // targetDurationSec arrives already converted to "new footage seconds" —
    // AnimationEditMode's own onExtend("to-target", fitTargetSec - animSec)
    // call site does that conversion; this handler just forwards it unchanged.
    runExtend({
      ...editSourceArg(editVersion),
      mode,
      targetDurationSec,
      prompt: extendPrompt,
      provider: extendProvider,
      referenceImageUrl: referenceImage?.url,
    });
  };

  const handleEditConvertFor = (v) => {
    if (!v || isExtending) return;
    runConvert({ ...editSourceArg(v), loop: !v.loop });
  };
  const handleEditConvert = () => handleEditConvertFor(editVersion);

  // S4 — continuation reference-image picker triggers. Copy the SHAPE of
  // openLibraryPicker/handleReferenceUpload (above, replace-product feature)
  // under new, Mode-B-scoped names; that feature's own state/functions are
  // untouched. ACCEPTED_IMAGE_TYPES is the same const declared above.
  // fileType: "image" (singular) — the real FileType enum value; none of
  // replace-product's other MIME-string entries ("image/gif" etc.) ever
  // match a real row, they're dead weight copied from a browser `accept`
  // list. Matches AddImageModal's LibraryImageTab.jsx (verified working).
  const openExtendLibraryPicker = useCallback(async () => {
    setPickingExtendLibrary(true);
    setExtendLibraryLoading(true);
    setExtendLibraryError(null);
    try {
      const res = await fetchIdealStudioFiles({
        limit: 100,
        offset: 0,
        fileType: "image",
      });
      const files = (res && Array.isArray(res.files) ? res.files : [])
        .map((f) => ({ url: f.fileVirtualPath, name: f.fileName }))
        .filter((f) => f.url);
      setExtendLibraryFiles(files);
    } catch (e) {
      console.error("Extend reference library load error:", e);
      setExtendLibraryError(i18n.t("files_load_error") || "Dosyalar yüklenemedi");
    } finally {
      setExtendLibraryLoading(false);
    }
  }, []);

  const handleExtendReferenceUpload = useCallback(async (file) => {
    if (!file) return;
    setExtendReferenceError(null);
    const typeOk =
      ACCEPTED_IMAGE_TYPES.includes((file.type || "").toLowerCase()) ||
      /\.(png|jpe?g)$/i.test(file.name || "");
    if (!typeOk) {
      setExtendReferenceError(i18n.t("ReplaceProduct.InvalidFileType"));
      return;
    }
    setExtendReferenceUploading(true);
    try {
      const apiUrl = process.env.MW_API_URL;
      if (!apiUrl) {
        throw new Error(i18n.t("API URL not configured"));
      }
      const formData = new FormData();
      formData.append("image", file);
      const response = await fetch(`${apiUrl}api/image-editing/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.message || i18n.t("Failed to upload image"));
      }
      const data = await response.json();
      if (!data?.url) {
        throw new Error(i18n.t("Failed to upload image"));
      }
      setReferenceImage({ url: data.url, name: file.name });
    } catch (err) {
      console.error("Extend reference upload error:", err);
      setExtendReferenceError(err?.message || i18n.t("Failed to upload image"));
    } finally {
      setExtendReferenceUploading(false);
    }
  }, []);

  // Frame-scrub row: grab a still frame from the currently-viewed version's
  // mp4Url at atSec and set it as the continuation reference image — same
  // MW auth/fetch pattern as handleExtendReferenceUpload, same target state.
  const handleUseFrameAsReference = useCallback(
    async (atSec) => {
      if (!editVersion) return;
      setExtendReferenceError(null);
      setFrameGrabbing(true);
      try {
        const apiUrl = process.env.MW_API_URL;
        if (!apiUrl) {
          throw new Error(i18n.t("API URL not configured"));
        }
        const response = await fetch(`${apiUrl}api/animation/v2/frame`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...editSourceArg(editVersion), atSec }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData?.message || i18n.t("AnimEdit.FrameExtractFailed"));
        }
        const data = await response.json();
        if (!data?.imageUrl) {
          throw new Error(i18n.t("AnimEdit.FrameExtractFailed"));
        }
        setReferenceImage({ url: data.imageUrl });
      } catch (err) {
        console.error("Frame extraction error:", err);
        setExtendReferenceError(err?.message || i18n.t("AnimEdit.FrameExtractFailed"));
      } finally {
        setFrameGrabbing(false);
      }
    },
    [editVersion]
  );

  // Ray verisi: fetch DAHA BİTMEDEN de mevcut görseli tek "Orijinal" kartı olarak
  // göster → ray, modalla AYNI ANDA gelir (ağ isteği beklenmez). Fetch dönünce
  // gerçek aile (versions) devralır. Böylece ilk açılışta gecikme olmaz.
  // Ray, imageUrl varsa HER ZAMAN görünür (boş/dolu fark etmez; yükseklik hep
  // modal boyunda). Data (versiyon ailesi) yüklenene kadar railLoading=true →
  // içeride spinner döner; yüklenince kartlar dizilir. Fetch boş dönerse mevcut
  // görsel tek "Orijinal" kartı olarak gösterilir.
  const railLoading = !!imageUrl && versionsLoadedFor !== imageUrl;
  const railVersions = !imageUrl
    ? []
    : railLoading
      ? []
      : versions.length > 0
        ? versions
        : [{ fileVirtualPath: imageUrl, id: "current" }];
  const showRail = !!imageUrl;
  // Ray, bodyContent'in içinde render ediliyor; history/kütüphane/referans/
  // extend seçicileri bodyContent'i HİÇ göstermiyor. Modal genişliği, sınıfı ve
  // ölçüleri rayın GERÇEKTEN ekranda olduğu duruma bağlı — yoksa o görünümlerde
  // solda boş bir şerit kalıyordu. (Koşul, AppModal children zinciriyle aynı.)
  const railShown =
    showRail &&
    !(
      (modalMode === "edit" && (pickingExtendLibrary || pickingExtendScene)) ||
      pickingLibrary ||
      pickingReference ||
      showHistory
    );
  // Ray genişlikleri. Açık = eski ayrı panelle aynı 290px; kapalı = yalnız
  // 30px "aç" oku + iç boşluk. Modal bu kadar genişler (bkz. maxWidth).
  const MODAL_BASE_W = 1000;
  const RAIL_OPEN_W = 290;
  const RAIL_COLLAPSED_W = 48;
  const railW = railShown ? (railOpen ? RAIL_OPEN_W : RAIL_COLLAPSED_W) : 0;
  // Dialog genişliği = SABİT içerik (min(taban, kap − 32 − açık ray); styleTag)
  // + ray. Kap = .modal'ın iç genişliği (100cqw; AI sohbet yanaşıkken dar).
  const railDialogClosedW = `min(${MODAL_BASE_W + RAIL_COLLAPSED_W}px, calc(100cqw - 32px - ${
    RAIL_OPEN_W - RAIL_COLLAPSED_W
  }px))`;
  const railDialogOpenW = `min(${MODAL_BASE_W + RAIL_OPEN_W}px, calc(100cqw - 32px))`;
  // Sağ boşluk: kapalıyken modal HER ekranda ortada. Açıkken sağ kenar yerinde
  // kalır (modal sola büyür); solda 16px'e kadar yer yetmezse (dar ekran) sağ
  // kenar ancak gereken kadar sağa kayar — içerik yine küçülmez, yalnız yana
  // kayar (styleTag'de margin-right geçişi).
  const railDialogClosedMr = `calc((100cqw - ${railDialogClosedW}) / 2)`;
  const railDialogMr = railOpen
    ? `max(16px, min(${railDialogClosedMr}, calc(100cqw - 16px - ${railDialogOpenW})))`
    : railDialogClosedMr;

  // Wrapper div (not Modal.Body itself — see bodyProps comment below for why)
  // that is a real flex column in BOTH modes, so the Geçmiş bar / settings
  // card / prompt (flex: "none") and the flexible media region (Mode B:
  // AnimationEditMode's wrapper; Mode A: the ORIJINAL/SONUÇ pane row) actually
  // participate in flex layout instead of just stacking as blocks — that's
  // what lets the media shrink first and keeps every control above the footer.
  // Mode A additionally gets overflowY:auto as a safety valve: if a tall
  // feature form's fixed content can't fit even after the media row hits its
  // minimum, it scrolls INSIDE this wrapper instead of clipping under the
  // footer. Mode B keeps the proven no-overflow layout untouched.
  //
  // sideLayout ("Objeyi Referansla Değiştir"): "Görseli Canlandır" Ayarla
  // adımıyla aynı dil — solda ORİJİNAL/SONUÇ alt alta, sağda form. Form uzun
  // (bölge + açıklama + stil + referans + ek yönerge) olduğu için alttaki
  // FEATURE_FORM_MAX_HEIGHT bölgesine sığmıyor, küçük ekranda "Ek yönerge"
  // footer'ın altında kalıyordu. Burada form sağ kolonun TAMAMINI alıp kendi
  // içinde kayıyor. Modal içeriği kadar uzun (Canlandır gibi); grid'in
  // maxHeight'i ekranı aşınca satır (minmax(0, 1fr)) kısalıyor ve sağ kolon
  // kayıyor. Sol kolonda tek panel + ORİJİNAL/SONUÇ sekmesi (sidePane).
  const sideLayout = !embed && selectedFeature.id === "replace-product";
  const sidePaneTabs = sideLayout ? (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "flex-start",
        gap: 2,
        padding: 3,
        marginBottom: 8,
        borderRadius: 10,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      {[
        { value: "original", label: i18n.t("Original") },
        { value: "result", label: i18n.t("Result") },
      ].map(({ value, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => setSidePane(value)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 14px",
            height: 28,
            borderRadius: 7,
            fontSize: 12,
            whiteSpace: "nowrap",
            border: "none",
            cursor: "pointer",
            ...(sidePane === value
              ? {
                  fontWeight: 700,
                  color: "#4f46e5",
                  background: "#ffffff",
                  boxShadow: "0 1px 3px rgba(15,23,42,0.10)",
                }
              : { fontWeight: 600, color: "#64748b", background: "transparent" }),
          }}
        >
          {label}
        </button>
      ))}
    </span>
  ) : null;
  const bodyContent = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        // Modal dalında burası KAYDIRMIYOR. Kaydırdığında taşan içerik tüm
        // gövdeyi hareket ettiriyordu: uzun bir özellik formunda (ör. "Metni
        // Değiştir"e çok satır eklenince) üstteki ORİJİNAL/SONUÇ panelleri de
        // yukarı kayıp ekrandan çıkıyor, kullanıcı kaydırma çubuğunu sahnenin
        // formunda değil modalın tamamında görüyordu.
        // overflow:hidden ile taşma baskısı aşağıdaki ESNEK bölgeye (özellik
        // formu, flex: 1 1 auto + overflowY: auto) iniyor: sabit ebatlı
        // paneller (flex:none) yerinde kalıyor, sadece form kayıyor.
        // Embed'in yükseklik zinciri yok — orada doğal akış + auto korunuyor.
        overflowY: embed ? "auto" : "hidden",
      }}
    >
      {/* S256 redesign: Ayarla/Üret/Düzenle step indicator, animate feature
          only — every other feature keeps today's headerless body. Purely
          presentational/derived from wizardStep, no state of its own. Hidden
          in embed (no modal chrome there) and while the Sürümler panel is
          open (that panel replaces this body's content, not a step within
          the wizard). */}
      {selectedFeature.id === "animate" && !embed && (
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flex: "none" }}
        >
          {[
            { step: 1, label: i18n.t("AnimEdit.StepSetup") },
            { step: 2, label: i18n.t("AnimEdit.StepGenerate") },
            { step: 3, label: i18n.t("AnimEdit.StepEdit") },
          ].map(({ step, label }, idx, arr) => {
            const done = wizardStep > step;
            const active = wizardStep === step;
            const clickable = canJumpToStep(step);
            // display:"contents" wrapper (not a fragment — this file has no
            // default React import, so shorthand <>...</> can't take the key
            // a .map() callback needs): lets the badge+label pair AND the
            // trailing connector line both act as direct flex children of
            // the row above, with no extra box in the layout.
            return (
              <span key={step} style={{ display: "contents" }}>
                <span
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => jumpToStep(step) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            jumpToStep(step);
                          }
                        }
                      : undefined
                  }
                  title={clickable ? label : undefined}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    flex: "none",
                    cursor: clickable ? "pointer" : "default",
                    borderRadius: 8,
                    padding: clickable ? "2px 6px 2px 2px" : 0,
                    margin: clickable ? "-2px -6px -2px -2px" : 0,
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      flex: "none",
                      ...(done
                        ? { background: "#eef2ff", border: "1px solid #c7d2fe", color: "#4f46e5" }
                        : active
                          ? { background: "#7c3aed", color: "#ffffff" }
                          : {
                              background: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              color: "#94a3b8",
                            }),
                    }}
                  >
                    {done ? <LuCheck size={12} /> : step}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: active ? 700 : 600,
                      color: active ? "#1e293b" : done ? "#475569" : "#94a3b8",
                    }}
                  >
                    {label}
                  </span>
                </span>
                {idx < arr.length - 1 && (
                  <span
                    style={{
                      flex: 1,
                      height: 1,
                      minWidth: 28,
                      background: done ? "#c7d2fe" : "#e2e8f0",
                    }}
                  />
                )}
              </span>
            );
          })}
        </div>
      )}

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          style={{ flex: "none", marginBottom: 10 }}
        >
          <strong>{i18n.t("Error")}:</strong> {error}
        </Alert>
      )}

      {/* ===== SOL VERSİYON RAYI — modalın İÇİNDE, .modal-content'in sol
          kenarına yapışık (createPortal → .modal-content). styleTag'deki
          .imageAnimatorModal--rail kuralları başlık/gövde/footer'ı rayın sağına, sağa
          hizalı sabit genişlikte oturtur; dar ekranda da modalın üstüne binmez.
          Açıkken tam liste + alt butonlar, kapalıyken YALNIZ "aç" oku (liste ve
          yükleme spinner'ı render edilmez). Bir versiyona tıklamak ORİJİNAL
          önizlemeyi (pendingVersionUrl) canlı değiştirir; sahneye alttaki buton
          uygular. ===== */}
      {showRail &&
        modalContentEl &&
        createPortal(
          <div
            className="imgRail"
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: railW,
              zIndex: 2,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              background: "#ffffff",
              borderRight: "1px solid #e2e8f0",
              overflow: "hidden",
              transition: "width 0.25s ease",
            }}
          >
            {/* İç kutu HEDEF genişlikte (ray kenarlığı hariç): dış kabuk 0.25s'de
                genişlerken içerik her karede yeniden dizilmez, sadece açığa çıkar
                (clip-reveal) — etiketler/butonlar titremiyordu. */}
            <div
              style={{
                flex: "1 1 auto",
                minHeight: 0,
                width: (railOpen ? RAIL_OPEN_W : RAIL_COLLAPSED_W) - 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Başlık satırı — modal başlığıyla (.app-modal__header) AYNI boy,
                iç boşluk ve alt çizgi; boy ölçülüp birebir verilir (railChrome).
                GEÇMİŞ solda (ORİJİNAL/SONUÇ bölüm etiketiyle aynı stil); ok = başlıktaki
                ikon butonlarıyla aynı .app-modal__toggle-btn. Kapalıyken yalnız ok. */}
              <div
                style={{
                  flex: "none",
                  boxSizing: "border-box",
                  height: railChrome.head || 53,
                  padding: railOpen ? "10px 16px" : "10px 0",
                  borderBottom: "1px solid #cbd5e1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: railOpen ? "space-between" : "center",
                  gap: 8,
                }}
              >
                {railOpen && (
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {i18n.t("AnimEdit.HistoryHeader", { defaultValue: "Geçmiş" })}
                  </span>
                )}
                {/* Chevron (‹/›) "geçmiş" olarak anlaşılmıyordu → iki durumda da
                    saat/geçmiş ikonu; açıkken header toggle'larıyla aynı --active. */}
                <button
                  type="button"
                  className={`app-modal__toggle-btn${railOpen ? " app-modal__toggle-btn--active" : ""}`}
                  onClick={toggleRail}
                  aria-expanded={railOpen}
                  title={
                    railOpen
                      ? i18n.t("AnimEdit.HistoryCollapse", { defaultValue: "Geçmişi kapat" })
                      : i18n.t("AnimEdit.HistoryExpand", { defaultValue: "Geçmişi aç" })
                  }
                >
                  <LuHistory size={16} />
                </button>
              </div>
              {railOpen && (
                <>
                  {/* Geçmiş satırları — küçük kare thumbnail + işlem adı + sağda →
                    (popup'a getir) / ✓ (seçili). Kart çizgi/zemin renkleri önizleme
                    panelleriyle, yazı Prompt etiketiyle aynı. OK'a tıklayınca
                    versiyon popup'a gelir; sahneye alttaki buton uygular. */}
                  <div
                    className="railScroll"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      overflowY: "auto",
                      overflowX: "hidden",
                      minHeight: 0,
                      flex: "1 1 auto",
                      padding: "16px 12px 16px 16px",
                    }}
                  >
                    {railLoading ? (
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#94a3b8",
                        }}
                      >
                        <LuLoaderCircle
                          size={24}
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                      </div>
                    ) : (
                      railVersions.map((v) => {
                        const idx = railVersions.indexOf(v);
                        const isApplied = rowMatchesUrl(v, activeVersionUrl);
                        const isVideo =
                          /video/i.test(v.fileType || "") ||
                          /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(v.fileVirtualPath || "");
                        // Seçili = önizlemede (pending) olan; yoksa sahnedeki (applied).
                        const isSelected = pendingVersionUrl
                          ? assetKey(v.fileVirtualPath) === assetKey(pendingVersionUrl)
                          : isApplied;
                        const label = versionOpLabel(v, idx);
                        // OK → bu versiyonu POPUP'ın içine uygula (ORİJİNAL önizleme +
                        // form ayarları). Canvas'a DEĞİL; sahneye alttaki buton ile.
                        const selectIntoPopup = () => {
                          const vIdx = versions.findIndex(
                            (x) => x.fileVirtualPath === v.fileVirtualPath
                          );
                          if (vIdx >= 0) applyVersionSettings(versions[vIdx]);
                          setPendingVersionUrl(v.fileVirtualPath);
                        };
                        return (
                          <div
                            key={v.id || idx}
                            className="railRow"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              flex: "none",
                              padding: 8,
                              borderRadius: 10,
                              background: isSelected ? "rgba(124,58,237,0.06)" : "#ffffff",
                              border: isSelected ? "1px solid #7c3aed" : "1px solid #e2e8f0",
                              // Seçili halde 2px görünen çerçeve — border 1px kalır ki
                              // seçim değişince kart boyu/konumu oynamasın.
                              boxShadow: isSelected ? "inset 0 0 0 1px #7c3aed" : "none",
                            }}
                          >
                            {/* küçük kare thumbnail — önizleme paneliyle aynı zemin/çizgi */}
                            <div
                              style={{
                                position: "relative",
                                width: 48,
                                height: 48,
                                flex: "none",
                                borderRadius: 8,
                                overflow: "hidden",
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              {isVideo ? (
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#64748b",
                                  }}
                                >
                                  <LuVideo size={18} />
                                </div>
                              ) : (
                                <img
                                  src={v.fileVirtualPath}
                                  alt={label}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              )}
                              {/* v sıra rozeti — thumbnail sol-üst köşe (marka moru) */}
                              <span
                                style={{
                                  position: "absolute",
                                  top: 2,
                                  left: 2,
                                  padding: "0 3px",
                                  height: 13,
                                  borderRadius: 3,
                                  fontSize: 8,
                                  fontWeight: 700,
                                  color: "#fff",
                                  background: "#7c3aed",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {`v${idx + 1}`}
                              </span>
                            </div>
                            {/* isim: işlem adı (Orijinal / Görseli Düzenle / ...) —
                                modal gövdesindeki "Prompt" etiketiyle aynı boyut/renk. */}
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#333",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {label}
                            </span>
                            {/* sağ: seçiliyse ✓, değilse → (tıklayınca bu versiyonu
                              popup'a getirir ve ✓ olur) */}
                            {isSelected ? (
                              <LuCheck size={18} style={{ color: "#22c55e", flex: "none" }} />
                            ) : (
                              <button
                                type="button"
                                className="railRowArrow"
                                title={i18n.t("AnimEdit.SendToPopup", {
                                  defaultValue: "Popup'a getir",
                                })}
                                onClick={selectIntoPopup}
                                style={{
                                  flex: "none",
                                  width: 32,
                                  height: 32,
                                  borderRadius: 8,
                                  border: "none",
                                  background: "#7c3aed",
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  padding: 0,
                                }}
                              >
                                <LuArrowRight size={16} />
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Alt: Vazgeç (seçimi sahnedekine döndür) + Sahneye uygula (asıl
                    canvas/sahne uygulaması burada). Modal footer'ıyla AYNI boy
                    (ölçülür), zemin, üst çizgi ve .app-modal__btn butonları; etiket
                    modal footer'ındaki aynı işlemin etiketi (AnimEdit.ApplyToScene) —
                    "Sahnedeki fotoğrafı güncelle" 14px butonda 290px raya sığmıyordu. */}
                  {(() => {
                    const selectedUrl = pendingVersionUrl || activeVersionUrl;
                    const changed = !!pendingVersionUrl && pendingVersionUrl !== activeVersionUrl;
                    const applyToScene = () => {
                      const url = selectedUrl;
                      const vIdx = versions.findIndex(
                        (x) => assetKey(x.fileVirtualPath) === assetKey(url)
                      );
                      if (vIdx >= 0) applyVersionSettings(versions[vIdx]);
                      setActiveVersionUrl(url);
                      setPendingVersionUrl(null);
                      if (onAcceptAnimation) onAcceptAnimation({ url, boxId });
                      if (vIdx >= 0 && versions[vIdx].jobId) approveJob(versions[vIdx].jobId);
                      onClose?.();
                    };
                    return (
                      <div
                        style={{
                          flex: "none",
                          boxSizing: "border-box",
                          height: railChrome.foot || undefined,
                          padding: "10px 16px",
                          borderTop: "1px solid #e2e8f0",
                          background: "#f8f9fa",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <button
                          type="button"
                          className="app-modal__btn app-modal__btn--ghost"
                          disabled={!pendingVersionUrl}
                          onClick={() => setPendingVersionUrl(null)}
                          style={{ flex: "none", paddingLeft: 14, paddingRight: 14 }}
                        >
                          {i18n.t("Cancel", { defaultValue: "Vazgeç" })}
                        </button>
                        <button
                          type="button"
                          className="app-modal__btn app-modal__btn--primary"
                          disabled={!changed}
                          onClick={applyToScene}
                          // Yan boşluk 22→14px: 290px rayda uzun dillerde de etiket sığsın.
                          style={{ flex: 1, minWidth: 0, paddingLeft: 14, paddingRight: 14 }}
                        >
                          <span
                            style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {i18n.t("AnimEdit.ApplyToScene", { defaultValue: "Sahneye uygula" })}
                          </span>
                        </button>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>,
          modalContentEl
        )}

      {modalMode === "edit" && editVersion ? (
        <div style={{ flex: 1, minHeight: 0 }}>
          <AnimationEditMode
            version={editVersion}
            elementWindow={elementWindow}
            elementWindowSec={elementWindowSec}
            isBusy={isExtending}
            jobProgress={isExtending ? (job?.progress ?? 0) : null}
            jobFailedMessage={
              job?.status === "failed" && extendJobIdRef.current === job?.id ? job?.error : null
            }
            advancedOpen={advancedOpen}
            setAdvancedOpen={setAdvancedOpen}
            hideAdvanced
            extendPrompt={extendPrompt}
            onExtendPromptChange={setExtendPrompt}
            extendProvider={extendProvider}
            onExtendProviderChange={setExtendProvider}
            onExtend={handleEditExtend}
            onConvert={handleEditConvert}
            onAddAsMp4={() => acceptEditVersion(editVersion?.mp4Url)}
            mp4Applied={isMp4VersionApplied}
            isVideoSourceBox={isPlayableVideoExt}
            onAddAsWebp={() => acceptEditVersion(editVersion?.displayUrl)}
            onOpenLightbox={setLightboxUrl}
            onDismissError={handleDismiss}
            replayTs={replayKeys.editMode || ""}
            onReplay={() => setReplayKeys((p) => ({ ...p, editMode: Date.now() }))}
            referenceImage={referenceImage}
            onPickReferenceLibrary={openExtendLibraryPicker}
            onPickReferenceScene={() => setPickingExtendScene(true)}
            onReferenceUpload={handleExtendReferenceUpload}
            onClearReference={() => setReferenceImage(null)}
            referenceUploading={extendReferenceUploading}
            referenceError={extendReferenceError}
            onUseFrameAsReference={handleUseFrameAsReference}
            frameGrabbing={frameGrabbing}
            embed={embed}
          />
        </div>
      ) : selectedFeature.id === "animate" && !isAlreadyAnimatedBox && wizardStep === 1 ? (
        // S256 redesign — Step 1 "Ayarla": 460px source image | form grid,
        // replacing the shared Original/Result 2-pane layout for this one
        // feature+state only. Every other feature/state falls through to
        // the untouched fragment below.
        (() => {
          const sectionLabelStyle = {
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            fontSize: 11,
            fontWeight: 700,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          };
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "460px minmax(0, 1fr)",
                gap: 16,
                alignItems: "start",
                flex: "1 1 auto",
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={sectionLabelStyle}>{i18n.t("AnimEdit.SourceImageLabel")}</span>
                <div
                  style={{
                    border: "2px dashed #e2e8f0",
                    borderRadius: 10,
                    background: "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    padding: 6,
                    minHeight: 260,
                    cursor: originalDisplayUrl ? "zoom-in" : "default",
                  }}
                  onClick={() => originalDisplayUrl && setLightboxUrl(originalDisplayUrl)}
                >
                  {originalDisplayUrl ? (
                    <img
                      src={originalDisplayUrl}
                      alt="Original"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: 6,
                      }}
                    />
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 13 }}>
                      {i18n.t("NoPreviewImage", { defaultValue: "No preview image available" })}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  {originalDisplayUrl && (
                    <button
                      type="button"
                      onClick={() => setLightboxUrl(originalDisplayUrl)}
                      style={belowImageBtnStyle}
                    >
                      <LuZoomIn size={12} /> {i18n.t("Preview")}
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={sectionLabelStyle}>
                      {i18n.t("AnimEdit.MotionDescriptionLabel")}
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#94a3b8",
                          textTransform: "none",
                          letterSpacing: 0,
                        }}
                      >
                        {i18n.t("AnimEdit.OptionalLabel")}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={handleFetchSceneText}
                      disabled={fetchingSceneText}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        color: "#7c3aed",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: fetchingSceneText ? "default" : "pointer",
                      }}
                    >
                      {fetchingSceneText ? i18n.t("Loading") : i18n.t("FetchSceneText")}
                    </button>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={i18n.t(
                      selectedFeature.promptPlaceholder || "EnterDescriptionPlaceholder"
                    )}
                    style={{
                      borderRadius: 8,
                      border: "1.5px solid #e2e8f0",
                      fontSize: 13,
                      resize: "vertical",
                      minHeight: 148,
                    }}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={sectionLabelStyle}>{i18n.t("AI Provider")}</span>
                    <span
                      className="animChip"
                      style={{
                        ...animChipStyle,
                        width: "100%",
                        justifyContent: "space-between",
                        height: 34,
                      }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <LuSparkles size={13} style={{ color: "#7c3aed", flex: "none" }} />
                        {PROVIDER_OPTIONS.find((o) => o.value === provider)?.label || provider}
                      </span>
                      <LuChevronDown size={12} style={{ color: "#94a3b8", flex: "none" }} />
                      <select
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                        aria-label={i18n.t("AI Provider")}
                        style={chipSelectOverlayStyle}
                      >
                        {PROVIDER_OPTIONS.filter((o) => !o.disabled).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={sectionLabelStyle}>{i18n.t("Duration")}</span>
                    <span
                      className="animChip"
                      style={{
                        ...animChipStyle,
                        width: "100%",
                        justifyContent: "space-between",
                        height: 34,
                      }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <LuTimer size={13} style={{ color: "#0284c7", flex: "none" }} />
                        {duration === null
                          ? i18n.t("Auto")
                          : i18n.t("DurationSecondsShort", { count: duration })}
                      </span>
                      <LuChevronDown size={12} style={{ color: "#94a3b8", flex: "none" }} />
                      <select
                        value={duration ?? ""}
                        onChange={(e) =>
                          setDuration(e.target.value === "" ? null : Number(e.target.value))
                        }
                        aria-label={i18n.t("Duration")}
                        style={chipSelectOverlayStyle}
                      >
                        <option value="">{i18n.t("Auto")}</option>
                        {supportedDurationsForProvider(provider).map((sec) => (
                          <option key={sec} value={sec}>
                            {i18n.t("DurationSecondsShort", { count: sec })}
                          </option>
                        ))}
                      </select>
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    border: "1px solid #e2e8f0",
                    borderRadius: 14,
                    padding: "10px 14px",
                  }}
                >
                  <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {i18n.t("AnimEdit.PlaybackLabel")}
                    </span>
                    <span style={{ fontSize: 11, color: "#6b7280" }}>
                      {i18n.t("AnimEdit.PlaybackHint")}
                    </span>
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      padding: 3,
                      borderRadius: 10,
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    {[
                      { value: true, label: i18n.t("Loop"), Icon: LuRepeat, iconSize: 12 },
                      { value: false, label: i18n.t("Play Once"), Icon: LuPlay, iconSize: 11 },
                    ].map(({ value, label, Icon, iconSize }) => (
                      <button
                        key={String(value)}
                        type="button"
                        onClick={() => {
                          setLoop(value);
                          localStorage.setItem("animation-loop", String(value));
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 5,
                          padding: "0 14px",
                          height: 30,
                          borderRadius: 7,
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          border: "none",
                          cursor: "pointer",
                          ...(loop === value
                            ? {
                                fontWeight: 700,
                                color: "#4f46e5",
                                background: "#ffffff",
                                boxShadow: "0 1px 3px rgba(15,23,42,0.10)",
                              }
                            : { fontWeight: 600, color: "#64748b", background: "transparent" }),
                        }}
                      >
                        <Icon size={iconSize} /> {label}
                      </button>
                    ))}
                  </span>
                </div>

                {elementWindow && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      border: "1px solid #c7d2fe",
                      borderRadius: 10,
                      background: "#eef2ff",
                      padding: "10px 12px",
                    }}
                  >
                    <LuInfo size={14} style={{ color: "#4f46e5", marginTop: 1, flex: "none" }} />
                    <span style={{ fontSize: 12, lineHeight: 1.5, color: "#3730a3" }}>
                      {i18n.t("AnimEdit.SetupSceneFitBanner", {
                        start: elementWindow.startSec.toFixed(1),
                        end: elementWindow.endSec.toFixed(1),
                        sec: (duration ?? elementWindowSec ?? 0).toFixed(1),
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })()
      ) : selectedFeature.id === "animate" && !isAlreadyAnimatedBox && wizardStep === 2 ? (
        // S256 redesign — Step 2 "Üret": in-progress (Uretim.html) and done
        // (Sonuc.html) sub-states, same feature+state gating as Step 1 above.
        (() => {
          const sectionLabelStyle = {
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            fontSize: 11,
            fontWeight: 700,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          };
          // Üret step reads the SUBMITTED settings (job.settings snapshot),
          // not the live form — the form may have been reset by a
          // close/reopen while this job was still running.
          const providerLabel = jobDisplayProviderLabel;
          const shownDuration = jobDisplaySettings.durationSec;
          const shownLoop = jobDisplaySettings.loop;
          const shownPrompt = jobDisplaySettings.prompt;
          const sourcePane = (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={sectionLabelStyle}>{i18n.t("AnimEdit.SourceImageLabel")}</span>
              <div
                style={{
                  border: "2px dashed #e2e8f0",
                  borderRadius: 10,
                  background: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  padding: 6,
                  minHeight: 260,
                }}
              >
                {originalDisplayUrl && (
                  <img
                    src={originalDisplayUrl}
                    alt="Original"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: 6,
                    }}
                  />
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                {originalDisplayUrl && (
                  <button
                    type="button"
                    onClick={() => setLightboxUrl(originalDisplayUrl)}
                    style={belowImageBtnStyle}
                  >
                    <LuZoomIn size={12} /> {i18n.t("Preview")}
                  </button>
                )}
              </div>
            </div>
          );

          if (isProcessing) {
            const elapsedSec = processingStartRef.current
              ? Math.max(0, Math.round((Date.now() - processingStartRef.current) / 1000))
              : 0;
            const elapsedLabel = `${Math.floor(elapsedSec / 60)}:${String(elapsedSec % 60).padStart(2, "0")}`;
            const progressPct = Math.min(100, Math.max(0, job?.progress || 0));
            return (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "460px minmax(0, 1fr)",
                  gap: 16,
                  alignItems: "start",
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflowY: "auto",
                }}
              >
                {sourcePane}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      background: "#ffffff",
                      padding: 16,
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                      boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          border: "2.5px solid #eef2ff",
                          borderTopColor: "#7c3aed",
                          display: "inline-block",
                          flex: "none",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", flex: 1 }}>
                        {i18n.t("AnimEdit.GeneratingTitle")}
                      </span>
                      {job?.progress != null && job.progress > 0 && (
                        <span style={{ fontSize: 16, fontWeight: 700, color: "#7c3aed" }}>
                          %{job.progress}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        position: "relative",
                        height: 6,
                        borderRadius: 999,
                        background: "#f1f5f9",
                        overflow: "hidden",
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          inset: 0,
                          right: `${100 - progressPct}%`,
                          background: "#7c3aed",
                          borderRadius: 999,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {i18n.t("AnimEdit.GeneratingHint")}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>
                        {i18n.t("AnimEdit.ElapsedTime", { time: elapsedLabel })}
                      </span>
                    </div>
                    <div style={{ height: 1, background: "#f1f5f9" }} />
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
                    >
                      <span style={summaryChipStyle}>{providerLabel}</span>
                      {shownDuration != null && (
                        <span style={summaryChipStyle}>
                          {i18n.t("DurationSecondsShort", { count: shownDuration })}
                        </span>
                      )}
                      <span style={summaryChipStyle}>
                        {i18n.t(shownLoop ? "Loop" : "Play Once")}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button type="button" onClick={handleClose} style={belowImageBtnStyle}>
                        <LuHistory size={13} /> {i18n.t("AnimEdit.ContinueInBackground")}
                      </button>
                      <button type="button" onClick={handleDismiss} style={belowImageBtnStyle}>
                        {i18n.t("AnimEdit.CancelJob")}
                      </button>
                    </div>
                  </div>
                  {shownPrompt && (
                    <div
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        background: "#f8fafc",
                        padding: "10px 12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <span style={sectionLabelStyle}>
                        {i18n.t("AnimEdit.MotionDescriptionLabel")}
                      </span>
                      <span style={{ fontSize: 12, lineHeight: 1.5, color: "#64748b" }}>
                        {shownPrompt}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          if (isCompletedForFeature && job?.result?.url) {
            const candidate = liveJobToEditVersion(job);
            const resultUrl = job.result.url;
            const isMp4 = resultUrl.endsWith(".mp4");
            const playbackLabel = i18n.t(shownLoop ? "Loop" : "Play Once");
            const durationSec =
              typeof job.result.durationSeconds === "number" ? job.result.durationSeconds : null;
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflowY: "auto",
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span style={sectionLabelStyle}>{i18n.t("Original")}</span>
                    <div
                      style={{
                        border: "2px dashed #e2e8f0",
                        borderRadius: 10,
                        background: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        padding: 6,
                        minHeight: 260,
                      }}
                    >
                      {originalDisplayUrl && (
                        <img
                          src={originalDisplayUrl}
                          alt="Original"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 6,
                          }}
                        />
                      )}
                    </div>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      {originalDisplayUrl && (
                        <button
                          type="button"
                          onClick={() => setLightboxUrl(originalDisplayUrl)}
                          style={belowImageBtnStyle}
                        >
                          <LuZoomIn size={12} /> {i18n.t("Preview")}
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span style={sectionLabelStyle}>{i18n.t("Result")}</span>
                    <div
                      style={{
                        border: "2px dashed #e2e8f0",
                        borderRadius: 10,
                        background: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        padding: 6,
                        minHeight: 260,
                        position: "relative",
                      }}
                    >
                      {isMp4 ? (
                        <video
                          ref={resultVideoRef}
                          src={resultUrl}
                          autoPlay
                          loop={shownLoop}
                          muted
                          playsInline
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 6,
                          }}
                        />
                      ) : (
                        <img
                          // Replay bumps replayKeys.single; a new URL is the only way
                          // to restart a finished play-once webp/gif.
                          src={
                            replayKeys.single
                              ? `${resultUrl}${resultUrl.includes("?") ? "&" : "?"}__t=${replayKeys.single}`
                              : resultUrl
                          }
                          alt="Result"
                          onClick={() => setLightboxUrl(resultUrl)}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 6,
                            cursor: "zoom-in",
                          }}
                        />
                      )}
                      <span
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#64748b",
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {playbackLabel}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (isMp4) {
                            const el = resultVideoRef.current;
                            if (el) {
                              el.currentTime = 0;
                              el.play().catch(() => {});
                            }
                          } else {
                            setReplayKeys((p) => ({ ...p, single: Date.now() }));
                          }
                        }}
                        style={belowImageBtnStyle}
                      >
                        <LuRefreshCw size={12} /> {i18n.t("Replay")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setLightboxUrl(resultUrl)}
                        style={belowImageBtnStyle}
                      >
                        <LuZoomIn size={12} /> {i18n.t("Preview")}
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    flex: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    border: "1px solid #e9ecef",
                    borderRadius: 10,
                    background: "#fafbfc",
                    padding: "11px 14px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "#dcfce7",
                      color: "#16a34a",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "none",
                    }}
                  >
                    <LuCheck size={15} />
                  </span>
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      flex: 1,
                      minWidth: 160,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                      {i18n.t("AnimEdit.ResultReadyTitle")}
                    </span>
                    <span style={{ fontSize: 11.5, color: "#64748b" }}>
                      {elementWindowSec != null
                        ? i18n.t("AnimEdit.ResultReadySummary", {
                            sec: durationSec != null ? durationSec.toFixed(1) : "?",
                            playback: playbackLabel,
                            provider: providerLabel,
                            windowSec: elementWindowSec.toFixed(1),
                          })
                        : i18n.t("AnimEdit.ResultReadySummaryNoWindow", {
                            sec: durationSec != null ? durationSec.toFixed(1) : "?",
                            playback: playbackLabel,
                            provider: providerLabel,
                          })}
                    </span>
                  </span>
                  {/* Back to Ayarla with the form pre-filled from this job —
                      the author can change prompt/provider/süre/döngü and
                      press Başlat. (Used to re-POST immediately with whatever
                      the form happened to hold.) */}
                  <button type="button" onClick={startReconfigure} style={belowImageBtnStyle}>
                    <LuRefreshCw size={13} /> {i18n.t("AnimEdit.Regenerate")}
                  </button>
                  <button
                    type="button"
                    disabled={isExtending || !candidate}
                    onClick={() => candidate && previewAndEdit(candidate)}
                    style={{
                      ...belowImageBtnStyle,
                      border: "1px solid #c7d2fe",
                      background: "#eef2ff",
                      color: "#4f46e5",
                      fontWeight: 700,
                    }}
                  >
                    <LuPencil size={13} /> {i18n.t("PreviewAndEdit")}
                  </button>
                </div>
              </div>
            );
          }

          // Failed job — restyled minimally to match the new card language,
          // same handleDismiss as before (no logic change).
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "460px minmax(0, 1fr)",
                gap: 16,
                alignItems: "start",
                flex: "1 1 auto",
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              {sourcePane}
              <div
                style={{
                  border: "1px solid #fca5a5",
                  borderRadius: 12,
                  background: "#fff5f5",
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  textAlign: "center",
                  minHeight: 260,
                }}
              >
                <LuCircleX size={26} color="#ef4444" />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#ef4444" }}>
                  {i18n.t("Failed")}
                </span>
                {job?.error && (
                  <span style={{ fontSize: 12, color: "#ef4444", lineHeight: 1.4 }}>
                    {job.error}
                  </span>
                )}
                {/* "Kapat" used to be the only exit and did NOT close the
                    modal — it dropped the failed job and fell back to Ayarla.
                    Say so, and offer a same-settings retry next to it. */}
                <div
                  style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}
                >
                  <button type="button" onClick={handleSubmit} style={belowImageBtnStyle}>
                    <LuRefreshCw size={12} /> {i18n.t("AnimEdit.Retry")}
                  </button>
                  <button type="button" onClick={handleDismiss} style={belowImageBtnStyle}>
                    <LuArrowLeft size={12} /> {i18n.t("AnimEdit.BackToSetup")}
                  </button>
                </div>
              </div>
            </div>
          );
        })()
      ) : (
        <div
          className={sideLayout ? "imgSideLayout" : undefined}
          style={
            sideLayout
              ? {
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  gridTemplateRows: "minmax(0, 1fr)",
                  gap: 16,
                  flex: "0 1 auto",
                  minHeight: 0,
                  // Ekran − (dialog marjı + başlık + footer + gövde padding'i).
                  // Modal yüksekliği içerikten geldiği için (indefinite) flex
                  // küçülmesi formu sınırlamıyor; tavanı grid'in kendisi koyuyor.
                  maxHeight: SIDE_LAYOUT_MAX_HEIGHT,
                }
              : { display: "contents" }
          }
        >
          {/* Image panels: Original | Result.
              Bu satır ESKİDEN tek esnek bölgeydi: form/prompt'tan artan yüksekliği
              ne kadarsa alıyordu. Sonucu, her feature'da farklı ebatta bir görsel
              önizlemeydi — "Görseli Düzenle" (prompt textarea'sı var) ile "Metni
              Ayır" (hiç input'u yok) aynı modalda apayrı boyutlarda açılıyordu.
              Ayrıca SONUÇ tarafının alt buton satırı ikinci satıra taşıdığında
              (Replay+Preview+Preview&Edit+Convert) fazlalık doğrudan o panelin
              kutusundan çıkıyor, ORİJİNAL ile eşitliği bozuyordu.
              Çözüm: kutular ARTIK SABİT — genişliğe bağlı bir en-boy oranından
              (aşağıdaki PANE_ASPECT_RATIO) türüyor. İki panel aynı grid kolon
              genişliğine sahip olduğu için yükseklikleri de inşaat gereği eşit,
              ve form ne kadar uzun olursa olsun ebat değişmiyor. Form sığmazsa
              bodyContent'in kendi overflowY:auto'su devreye giriyor.
              Embed'in yükseklik zinciri yok, kendi doğal akışını koruyor. */}
          <div
            style={
              sideLayout
                ? { display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }
                : {
                    marginBottom: "10px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    ...(embed ? {} : { flex: "none" }),
                  }
            }
          >
            <div
              style={
                embed
                  ? undefined
                  : sideLayout
                    ? {
                        display: sidePane === "original" ? "flex" : "none",
                        flexDirection: "column",
                      }
                    : { display: "flex", flexDirection: "column", minHeight: 0 }
              }
            >
              {sideLayout ? (
                sidePaneTabs
              ) : (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#64748b",
                    marginBottom: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {i18n.t("Original")}
                </div>
              )}
              <div
                style={{
                  border: "2px dashed #e2e8f0",
                  borderRadius: "10px",
                  backgroundColor: "#f8fafc",
                  // Modal: en-boy oranından türeyen SABİT yükseklik (bkz.
                  // paneBoxSizing). objectFit: contain görselin kırpılmadan
                  // letterbox olmasını sağlıyor, yani oran sabitken bile her
                  // görsel tam görünüyor.
                  // Embed keeps the old fixed cap (no definite height chain).
                  ...(embed
                    ? { height: "min(300px, 28vh)" }
                    : sideLayout
                      ? sidePaneSizing
                      : paneBoxSizing),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  padding: 6,
                  position: "relative",
                }}
                onClick={() => originalDisplayUrl && setLightboxUrl(originalDisplayUrl)}
              >
                {originalDisplayUrl ? (
                  <img
                    src={originalDisplayUrl}
                    alt="Original"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "6px",
                      cursor: "zoom-in",
                    }}
                  />
                ) : (
                  <span style={{ color: "#94a3b8", fontSize: 13 }}>
                    {i18n.t("NoPreviewImage", { defaultValue: "No preview image available" })}
                  </span>
                )}
              </div>
              {/* Preview below the image, in normal flow — not overlaid, so it's
                  visible without hovering (replaces the old always-on magnifier).
                  minHeight: 22 (the button's own rendered height) is reserved even
                  when there's no button — otherwise this pane's dashed box gets
                  ~28px more room than the Result pane's whenever Result has no
                  footer of its own (Mode A empty/processing), stretching taller
                  and breaking the ORIJINAL/SONUÇ height match. See the matching
                  reservation on the Result side below. */}
              <div
                style={{ display: "flex", justifyContent: "center", marginTop: 6, minHeight: 22 }}
              >
                {originalDisplayUrl && (
                  <button
                    onClick={() => setLightboxUrl(originalDisplayUrl)}
                    style={belowImageBtnStyle}
                  >
                    <LuZoomIn size={12} /> {i18n.t("Preview")}
                  </button>
                )}
              </div>
            </div>
            <div
              style={
                embed
                  ? undefined
                  : sideLayout
                    ? {
                        display: sidePane === "result" ? "flex" : "none",
                        flexDirection: "column",
                      }
                    : { display: "flex", flexDirection: "column", minHeight: 0 }
              }
            >
              {sideLayout ? (
                sidePaneTabs
              ) : (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#64748b",
                    marginBottom: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {i18n.t("Result")}
                </div>
              )}
              <div
                style={{
                  border: "2px dashed #e2e8f0",
                  borderRadius: "10px",
                  backgroundColor: "#f8fafc",
                  // ORİJİNAL panelinin birebir aynısı (bkz. paneBoxSizing) —
                  // eşitlik artık "aynı esneme kuralı" değil, "aynı sabit oran".
                  ...(embed
                    ? { height: "min(300px, 28vh)" }
                    : sideLayout
                      ? sidePaneSizing
                      : paneBoxSizing),
                  overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  padding: 6,
                }}
              >
                {(() => {
                  const isForThisFeature = job?.featureId === selectedFeature.id;
                  const isCompleted = job?.status === "completed" && isForThisFeature;
                  const generatedImages = isCompleted
                    ? job?.result?.details?.generatedImages
                    : null;
                  const isProcessing =
                    isSubmitting ||
                    (job?.status && job.status !== "completed" && job.status !== "failed");

                  // Video modu: hiçbir üretim yokken Result = kutunun kendisi olan video
                  // (imageUrl) — salt önizleme. Yeni üretim başlayınca (isProcessing) eski
                  // video kalkar, aşağıdaki normal spinner/result akışına düşülür.
                  if (isVideoSource && !isProcessing && !isCompleted) {
                    return imageUrl ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 6,
                          overflow: "hidden",
                        }}
                      >
                        {isPlayableVideoExt ? (
                          <video
                            src={imageUrl}
                            autoPlay
                            loop
                            muted
                            playsInline
                            controls
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        ) : (
                          <img
                            src={imageUrl}
                            alt="Result"
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#94a3b8",
                          fontSize: 13,
                        }}
                      >
                        {i18n.t("No image selected")}
                      </div>
                    );
                  }

                  // Only use the multi-image grid for 2+ results. A single generated
                  // image (e.g. generate-from-reference / generate-similar count=1)
                  // falls through to the full-size `result.url` preview below so it
                  // renders fully (objectFit: contain) like the other operations,
                  // instead of being cropped into a small grid card.
                  if (generatedImages?.length > 1) {
                    return (
                      <>
                        <div
                          style={{
                            flex: 1,
                            overflowX: "auto",
                            overflowY: "hidden",
                            display: "flex",
                            gap: 6,
                            padding: "8px 8px 4px",
                            alignItems: "stretch",
                          }}
                        >
                          {generatedImages.map((image, idx) => {
                            const imgKey = `multi-${image.index ?? idx}`;
                            const replayTs = replayKeys[imgKey] || "";
                            const isAnimated =
                              image.url?.endsWith(".webp") || image.url?.endsWith(".gif");
                            const displayUrl =
                              isAnimated && replayTs
                                ? `${image.url}${image.url.includes("?") ? "&" : "?"}__t=${replayTs}`
                                : image.url;
                            return (
                              <div
                                key={idx}
                                style={{
                                  flexShrink: 0,
                                  width: 130,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    borderRadius: 6,
                                    overflow: "hidden",
                                    border: "1px solid #e2e8f0",
                                    backgroundColor: "#e2e8f0",
                                    position: "relative",
                                  }}
                                  onMouseEnter={() => setHoveredImage(`multi-${idx}`)}
                                  onMouseLeave={() => setHoveredImage(null)}
                                >
                                  <img
                                    src={displayUrl}
                                    alt={`Option ${idx + 1}`}
                                    onClick={() => setLightboxUrl(image.url)}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      cursor: "zoom-in",
                                    }}
                                  />
                                  {hoveredImage === `multi-${idx}` && (
                                    <button
                                      onClick={() => setLightboxUrl(image.url)}
                                      title={i18n.t("View fullscreen")}
                                      style={{
                                        position: "absolute",
                                        bottom: 5,
                                        right: 5,
                                        background: "rgba(0,0,0,0.55)",
                                        border: "none",
                                        borderRadius: "50%",
                                        width: 22,
                                        height: 22,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        cursor: "pointer",
                                        backdropFilter: "blur(4px)",
                                        zIndex: 2,
                                      }}
                                    >
                                      <LuZoomIn size={11} />
                                    </button>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleAcceptResult(image.url)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 3,
                                    padding: "3px 6px",
                                    borderRadius: 5,
                                    border: "none",
                                    background: "#22c55e",
                                    color: "#fff",
                                    fontSize: 10,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                >
                                  <LuCheck size={10} /> {i18n.t("Use")}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                        <div
                          style={{
                            padding: "0 8px 5px",
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            onClick={handleDismiss}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              padding: "2px 8px",
                              borderRadius: 5,
                              border: "1px solid #e2e8f0",
                              background: "transparent",
                              color: "#94a3b8",
                              fontSize: 10,
                              cursor: "pointer",
                            }}
                          >
                            <LuX size={10} /> {i18n.t("Dismiss All")}
                          </button>
                        </div>
                      </>
                    );
                  }

                  if (isCompleted && job?.result?.url) {
                    const isMp4 = job.result.url.endsWith(".mp4");
                    const isAnimated =
                      job.result.url.endsWith(".webp") || job.result.url.endsWith(".gif");
                    const replayTs = replayKeys["single"] || "";
                    const displayUrl =
                      isAnimated && replayTs
                        ? `${job.result.url}${job.result.url.includes("?") ? "&" : "?"}__t=${replayTs}`
                        : job.result.url;
                    return (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 6,
                          overflow: "hidden",
                        }}
                      >
                        {isMp4 ? (
                          <video
                            ref={resultVideoRef}
                            src={job.result.url}
                            autoPlay
                            // Honor the Bir Kez Oynat/Döngü choice instead of always
                            // looping — single-play results now actually freeze at
                            // the end, matching gif/webp, and the Replay button
                            // below the image restarts them.
                            loop={loop}
                            muted
                            playsInline
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        ) : (
                          <img
                            src={displayUrl}
                            alt="Result"
                            onClick={() => setLightboxUrl(job.result.url)}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              cursor: "zoom-in",
                            }}
                          />
                        )}
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: "20px 8px 7px",
                            background: "linear-gradient(transparent, rgba(0,0,0,0.55))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 5,
                          }}
                        >
                          <button
                            onClick={() => handleAcceptResult()}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "4px 10px",
                              borderRadius: 6,
                              border: "none",
                              background: "#22c55e",
                              color: "#fff",
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            <LuCheck size={12} /> {i18n.t("Accept")}
                          </button>
                          <button
                            title={i18n.t("Dismiss")}
                            onClick={handleDismiss}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 26,
                              height: 26,
                              borderRadius: "50%",
                              border: "none",
                              background: "rgba(255,255,255,0.18)",
                              color: "#fff",
                              cursor: "pointer",
                              backdropFilter: "blur(4px)",
                            }}
                          >
                            <LuX size={12} />
                          </button>
                          <button
                            title={i18n.t("View fullscreen")}
                            onClick={() => setLightboxUrl(job.result.url)}
                            style={{
                              position: "absolute",
                              bottom: 8,
                              right: 8,
                              background: "rgba(0,0,0,0.55)",
                              border: "none",
                              borderRadius: "50%",
                              width: 28,
                              height: 28,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              cursor: "pointer",
                              backdropFilter: "blur(4px)",
                              zIndex: 3,
                            }}
                          >
                            <LuZoomIn size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  }

                  if (isProcessing) {
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: 1,
                          gap: 6,
                        }}
                      >
                        <LuLoaderCircle
                          size={26}
                          color="#3b82f6"
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                          {i18n.t("Processing")}…
                        </span>
                        {job?.progress != null && job.progress > 0 && (
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>{job.progress}%</span>
                        )}
                      </div>
                    );
                  }

                  if (job?.status === "failed" && isForThisFeature) {
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: 1,
                          gap: 6,
                        }}
                      >
                        <LuCircleX size={26} color="#ef4444" />
                        <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>
                          {i18n.t("Failed")}
                        </span>
                        {job?.error && (
                          <span
                            style={{
                              fontSize: 11,
                              color: "#ef4444",
                              fontWeight: 500,
                              textAlign: "center",
                              maxWidth: 240,
                              lineHeight: 1.35,
                            }}
                          >
                            {job.error}
                          </span>
                        )}
                        <button
                          onClick={handleDismiss}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "3px 10px",
                            borderRadius: 6,
                            border: "1.5px solid #fca5a5",
                            background: "#fff5f5",
                            color: "#ef4444",
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          <LuX size={11} /> {i18n.t("Dismiss")}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        gap: 6,
                      }}
                    >
                      <LuImage size={28} color="#cbd5e1" />
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        {i18n.t("Result will appear here")}
                      </span>
                    </div>
                  );
                })()}
              </div>
              {/* Replay + Preview + Preview&Edit + Convert — ONE row below the
                  image, in normal flow, not overlaid. Was two stacked rows
                  (Replay/Preview, then Preview&Edit/Convert); merged into one
                  wrapping row to reclaim ~40px of fixed-stack height for short
                  viewports. Replay/Preview mirror the isCompleted/single-result
                  calc above (single-result view only — the multi-image grid has
                  its own controls); Preview&Edit/Convert enter Mode B (live job,
                  or the box's already-animated version when reopened) — Convert
                  performs immediately (zero inputs), Preview&Edit preps
                  everything else (prompt/model live in Mode B). */}
              {(() => {
                const isForThisFeature = job?.featureId === selectedFeature.id;
                const isCompleted = job?.status === "completed" && isForThisFeature;
                const generatedImages = isCompleted ? job?.result?.details?.generatedImages : null;
                const resultUrl =
                  isCompleted && !(generatedImages?.length > 1) ? job?.result?.url : null;
                const isMp4 = resultUrl?.endsWith(".mp4");
                const isAnimated = resultUrl?.endsWith(".webp") || resultUrl?.endsWith(".gif");
                const candidate =
                  selectedFeature.id === "animate"
                    ? liveJobToEditVersion(job) ||
                      (currentVersionRow
                        ? versionRowToEditVersion(
                            currentVersionRow,
                            (() => {
                              const i = versions.findIndex((x) => x.id === currentVersionRow.id);
                              return i >= 0 ? i : currentVersionIdx;
                            })()
                          )
                        : null)
                    : null;
                // Reserve the same space as the Original pane's footer (see its
                // comment) instead of collapsing to nothing — otherwise this
                // pane's dashed box grows ~28px taller than Original's whenever
                // there's no result/candidate yet (Mode A empty/processing).
                if (!resultUrl && !candidate) {
                  return <div style={{ marginTop: 6, minHeight: 22 }} />;
                }
                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 6,
                      // nowrap + yatay kaydırma: dört buton birden çıktığında
                      // (Replay+Preview+Preview&Edit+Convert) sarıp ikinci satıra
                      // taşıyordu, o fazlalık da panelin kutusundan çıkıp
                      // ORİJİNAL ile yükseklik eşitliğini bozuyordu. Kutu artık
                      // sabit olduğu için ebat bozulmaz, ama satırın kendisi de
                      // tek satırda kalsın ki alt hizalama kaymasın.
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      marginTop: 6,
                      minHeight: 22,
                    }}
                  >
                    {resultUrl && (isMp4 || isAnimated) && (
                      <button
                        onClick={() => {
                          if (isMp4) {
                            const el = resultVideoRef.current;
                            if (el) {
                              el.currentTime = 0;
                              el.play().catch(() => {});
                            }
                          } else {
                            setReplayKeys((p) => ({ ...p, single: Date.now() }));
                          }
                        }}
                        style={belowImageBtnStyle}
                      >
                        <LuRefreshCw size={12} /> {i18n.t("Replay")}
                      </button>
                    )}
                    {resultUrl && (
                      <button onClick={() => setLightboxUrl(resultUrl)} style={belowImageBtnStyle}>
                        <LuZoomIn size={12} /> {i18n.t("Preview")}
                      </button>
                    )}
                    {candidate && (
                      <button
                        type="button"
                        disabled={isExtending}
                        onClick={() => previewAndEdit(candidate)}
                        style={belowImageBtnStyle}
                      >
                        <LuPencil size={12} /> {i18n.t("PreviewAndEdit")}
                      </button>
                    )}
                    {candidate?.mp4Url && (
                      <button
                        type="button"
                        disabled={isExtending}
                        onClick={() => {
                          enterEditMode(candidate);
                          handleEditConvertFor(candidate);
                        }}
                        style={belowImageBtnStyle}
                      >
                        <LuRefreshCw size={12} />{" "}
                        {i18n.t(candidate.loop ? "ConvertToPlayOnce" : "ConvertToLoop")}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Already-animated box (mp4/webp/gif source, family row resolved):
              the provider/Süre/Döngü/format card below is dead weight (inputs
              are read-only, "Başlat" is locked) — show a compact metadata
              summary instead, permanently (Önizle ve Düzenle is the workflow). */}
          {selectedFeature.id === "animate" && isAlreadyAnimatedBox && (
            <div
              style={{
                background: "#fafbfc",
                border: "1px solid #e9ecef",
                borderRadius: "10px",
                padding: "8px 14px",
                marginBottom: "10px",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8,
                flex: "none",
              }}
            >
              <span style={summaryChipStyle}>{summaryProviderLabel}</span>
              {typeof summaryDurationSec === "number" && (
                <span style={summaryChipStyle}>
                  {i18n.t("AnimEdit.SummaryDuration", { sec: summaryDurationSec.toFixed(1) })}
                </span>
              )}
              <span style={summaryChipStyle}>{i18n.t(loop ? "Loop" : "Play Once")}</span>
              {elementWindow && (
                <span style={summaryChipStyle}>
                  {i18n.t("AnimEdit.SummarySceneWindow", {
                    start: elementWindow.startSec.toFixed(1),
                    end: elementWindow.endSec.toFixed(1),
                  })}
                </span>
              )}
              <span
                style={{
                  ...summaryChipStyle,
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={summaryPromptText || i18n.t("AnimEdit.NoPromptGiven")}
              >
                {summaryPromptText
                  ? i18n.t("AnimEdit.SummaryPrompt", { prompt: summaryPromptText })
                  : i18n.t("AnimEdit.NoPromptGiven")}
              </span>
            </div>
          )}

          {/* Feature-specific controls — ortak kaydırma bölgesi.
              Önizleme panelleri artık sabit ebatta (paneBoxSizing) ve Geçmiş
              paneli / özet kartı flex:none olduğu için ESNEK olan tek bölge bu:
              modal max-height'ine dayandığında burası küçülüp kayıyor, üstteki
              görseller ve alttaki footer yerinde kalıyor.
              Embed'in yükseklik zinciri yok — orada doğal akış korunuyor, yoksa
              minHeight:0 bir yüksekliği olmayan kapsayıcıda içeriği ezerdi. */}
          <div
            lang={sideLayout ? i18n.language : undefined}
            className={embed ? undefined : "railScroll"}
            style={
              embed
                ? undefined
                : sideLayout
                  ? {
                      minHeight: 0,
                      overflowY: "auto",
                      paddingRight: 8,
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }
                  : {
                      // flex: "0 1 auto" — bölge boşluğu doldurmak için
                      // BÜYÜMÜYOR (büyürse kısa formlarda modal gereksiz uzar),
                      // yalnızca gerektiğinde küçülüyor.
                      flex: "0 1 auto",
                      // Taban: sabit parçalar yeri doldurduğunda bölge sıfıra
                      // inip okunamaz hale gelmesin.
                      minHeight: 140,
                      // TAVAN — asıl düzeltme. Eksik olan buydu: tavan yokken
                      // ve atanın yüksekliği indefinite olduğunda (modal
                      // içeriğe göre boyutlandığı için öyle) form sınırsız
                      // büyüyüp üstteki panelleri yukarı itiyordu ve hiçbir
                      // kaydırma çubuğu çıkmıyordu. Bkz. FEATURE_FORM_MAX_HEIGHT.
                      maxHeight: FEATURE_FORM_MAX_HEIGHT,
                      overflowY: "auto",
                      paddingRight: 2,
                    }
            }
          >
            {selectedFeature.id === "remove-text" && renderRegionScope()}

            {selectedFeature.id === "translate" && (
              <>
                <Form.Group style={{ marginBottom: "12px" }}>
                  <Form.Label style={{ fontWeight: 600, color: "#333", fontSize: 13 }}>
                    {i18n.t("Target Language")}
                  </Form.Label>
                  <Form.Control
                    as="select"
                    value={targetLanguage}
                    onChange={(e) => setTargetLanguage(e.target.value)}
                    style={{ borderRadius: "8px", border: "1.5px solid #e2e8f0", fontSize: 13 }}
                    disabled={isSubmitting}
                  >
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {i18n.t(`Language.${lang.labelKey}`)}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                {renderRegionScope()}
              </>
            )}

            {selectedFeature.id === "generate-similar" && (
              <Form.Group style={{ marginBottom: "12px" }}>
                <Form.Label style={{ fontWeight: 600, color: "#333", fontSize: 13 }}>
                  {i18n.t("Count")}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="4"
                  value={count}
                  onChange={(e) => setCount(parseInt(e.target.value))}
                  style={{ borderRadius: "8px", border: "1.5px solid #e2e8f0", fontSize: 13 }}
                  disabled={isSubmitting}
                />
              </Form.Group>
            )}

            {selectedFeature.id === "change-image-text" && (
              <div style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Form.Label style={{ fontWeight: 600, color: "#333", margin: 0, fontSize: 13 }}>
                    {i18n.t("Text Changes")}
                  </Form.Label>
                  <button
                    type="button"
                    onClick={() => {
                      setTextChanges([
                        ...textChanges,
                        { originalText: "", newText: "", location: "" },
                      ]);
                      // Yeni kart, kaydırılabilir form bölgesinin altında
                      // görünmez kalmasın — son input'a odaklanmak onu görünür
                      // alana getiriyor.
                      setFocusLastAddedInput(true);
                    }}
                    disabled={isSubmitting}
                    style={{
                      padding: "3px 10px",
                      border: "1.5px solid #007bff",
                      borderRadius: "6px",
                      backgroundColor: "#fff",
                      color: "#007bff",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    + {i18n.t("Add")}
                  </button>
                </div>
                <div ref={addableListRef}>
                  {textChanges.map((entry, idx) => (
                    <div
                      key={idx}
                      style={{
                        border: "1px solid #e9ecef",
                        borderRadius: "8px",
                        padding: "10px",
                        marginBottom: "8px",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <span style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                          #{idx + 1}
                        </span>
                        {textChanges.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setTextChanges(textChanges.filter((_, i) => i !== idx))}
                            disabled={isSubmitting}
                            style={{
                              border: "none",
                              background: "none",
                              color: "#dc3545",
                              cursor: "pointer",
                              fontSize: "16px",
                              padding: 0,
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
                        <Form.Control
                          type="text"
                          placeholder={i18n.t("OriginalTextPlaceholder")}
                          value={entry.originalText}
                          onChange={(e) => {
                            const u = [...textChanges];
                            u[idx] = { ...u[idx], originalText: e.target.value };
                            setTextChanges(u);
                          }}
                          style={{
                            flex: 1,
                            borderRadius: "6px",
                            border: "1px solid #ddd",
                            fontSize: "13px",
                          }}
                          disabled={isSubmitting}
                        />
                        <button
                          type="button"
                          onClick={() => setTextSelectEntryIdx(idx)}
                          disabled={isSubmitting || !imageUrl}
                          title={i18n.t("SelectText")}
                          style={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "0 10px",
                            border: "1.5px solid #007bff",
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                            color: "#007bff",
                            fontSize: "12px",
                            fontWeight: 600,
                            cursor: isSubmitting || !imageUrl ? "not-allowed" : "pointer",
                            opacity: !imageUrl ? 0.5 : 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <LuScanLine size={14} />
                          {i18n.t("SelectText")}
                        </button>
                      </div>
                      <Form.Control
                        type="text"
                        placeholder={i18n.t("NewTextPlaceholder")}
                        value={entry.newText}
                        onChange={(e) => {
                          const u = [...textChanges];
                          u[idx] = { ...u[idx], newText: e.target.value };
                          setTextChanges(u);
                        }}
                        style={{
                          borderRadius: "6px",
                          border: "1px solid #ddd",
                          marginBottom: "6px",
                          fontSize: "13px",
                        }}
                        disabled={isSubmitting}
                      />
                      <Form.Control
                        as="select"
                        value={entry.location}
                        onChange={(e) => {
                          const u = [...textChanges];
                          u[idx] = { ...u[idx], location: e.target.value, customLocation: "" };
                          setTextChanges(u);
                        }}
                        style={{
                          borderRadius: "6px",
                          border: "1px solid #ddd",
                          marginBottom: entry.location === "custom" ? "6px" : 0,
                          fontSize: "13px",
                        }}
                        disabled={isSubmitting}
                      >
                        <option value="" disabled>
                          {i18n.t("SelectLocation")}
                        </option>
                        {LOCATION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {i18n.t(opt.labelKey)}
                          </option>
                        ))}
                      </Form.Control>
                      {entry.location === "custom" && (
                        <Form.Control
                          type="text"
                          placeholder={i18n.t("EnterCustomLocationPlaceholder")}
                          value={entry.customLocation}
                          onChange={(e) => {
                            const u = [...textChanges];
                            u[idx] = { ...u[idx], customLocation: e.target.value };
                            setTextChanges(u);
                          }}
                          style={{
                            borderRadius: "6px",
                            border: "1px solid #ddd",
                            fontSize: "13px",
                          }}
                          disabled={isSubmitting}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedFeature.id === "resize" && (
              <Form.Group style={{ marginBottom: "12px" }}>
                <Form.Label
                  style={{
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: 13,
                  }}
                >
                  {i18n.t("Aspect Ratio")}
                </Form.Label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "8px" }}>
                  {ASPECT_RATIO_OPTIONS.map((option) => {
                    const isSelected = selectedAspectRatio === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSelectedAspectRatio(option.value)}
                        disabled={isSubmitting}
                        style={{
                          padding: "12px 6px",
                          border: `2px solid ${isSelected ? "#007bff" : "#e9ecef"}`,
                          borderRadius: "10px",
                          backgroundColor: isSelected ? "#f0f7ff" : "#fff",
                          color: isSelected ? "#007bff" : "#555",
                          cursor: isSubmitting ? "not-allowed" : "pointer",
                          fontWeight: 700,
                          fontSize: "12px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          minHeight: "70px",
                        }}
                      >
                        <div
                          style={{
                            ...option.style,
                            border: `2px solid ${isSelected ? "#007bff" : "#aaa"}`,
                            borderRadius: "3px",
                            backgroundColor: isSelected ? "#cce0ff" : "#f0f0f0",
                            flexShrink: 0,
                          }}
                        />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </Form.Group>
            )}

            {selectedFeature.id === "upscale" && (
              <Form.Group style={{ marginBottom: "12px" }}>
                <Form.Label
                  style={{
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: 13,
                  }}
                >
                  {i18n.t("Scale Factor")}
                </Form.Label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                  {[2, 3, 4].map((factor) => {
                    const isSelected = scaleFactor === factor;
                    return (
                      <button
                        key={factor}
                        type="button"
                        onClick={() => setScaleFactor(factor)}
                        disabled={isSubmitting}
                        style={{
                          padding: "14px 8px",
                          border: `2px solid ${isSelected ? "#007bff" : "#e9ecef"}`,
                          borderRadius: "10px",
                          backgroundColor: isSelected ? "#f0f7ff" : "#fff",
                          color: isSelected ? "#007bff" : "#555",
                          cursor: isSubmitting ? "not-allowed" : "pointer",
                          fontWeight: 700,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span style={{ fontSize: "20px", fontWeight: 800 }}>{factor}×</span>
                        <span style={{ fontSize: "11px", color: isSelected ? "#007bff" : "#888" }}>
                          {factor === 2 ? "HD" : factor === 3 ? "Full HD" : "4K"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Form.Group>
            )}

            {selectedFeature.id === "replace-product" &&
              (() => {
                // "Görseli Canlandır" Ayarla adımının birebir form dili: büyük
                // harf bölüm etiketi + "isteğe bağlı" rozeti, 7px etiket-alan
                // aralığı, 12px grup aralığı (kapsayıcı gap), Oynatma kartı
                // gibi başlık+açıklama solda / kontrol sağda kartlar.
                const rpFieldStyle = { display: "flex", flexDirection: "column", gap: 7 };
                const rpLabelStyle = {
                  display: "flex",
                  alignItems: "baseline",
                  gap: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                };
                const rpOptionalStyle = {
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#94a3b8",
                  textTransform: "none",
                  letterSpacing: 0,
                };
                const rpCardStyle = {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "10px 14px",
                };
                const rpCardTitleStyle = { fontSize: 13, fontWeight: 600, color: "#111827" };
                const rpCardHintStyle = { fontSize: 11, lineHeight: 1.45, color: "#6b7280" };
                const rpInputStyle = {
                  borderRadius: 8,
                  border: "1.5px solid #e2e8f0",
                  fontSize: 13,
                  boxShadow: "none",
                };
                const rpSegmentStyle = {
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 2,
                  padding: 3,
                  borderRadius: 10,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  flex: "none",
                };
                const rpSegmentBtnStyle = {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  padding: "0 12px",
                  height: 30,
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  border: "none",
                  cursor: "pointer",
                  color: "#4f46e5",
                  background: "#ffffff",
                  boxShadow: "0 1px 3px rgba(15,23,42,0.10)",
                  margin: 0,
                };
                const rpSegmentGhostStyle = {
                  ...rpSegmentBtnStyle,
                  fontWeight: 600,
                  color: "#64748b",
                  background: "transparent",
                  boxShadow: "none",
                };
                // Referans alanının iki hali (boş: yükle/kitaplık kutuları,
                // dolu: seçili kart) aynı yüksekliği kullanıyor — yoksa görsel
                // seçilince form kısalıyor ve içerikten boylanan modal zıplıyor.
                const rpReferenceMinHeight = 88;
                const rpTileStyle = {
                  flex: "1 1 150px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  minHeight: rpReferenceMinHeight,
                  padding: 12,
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  textAlign: "center",
                  margin: 0,
                };
                const optionalBadge = (
                  <span style={rpOptionalStyle}>{i18n.t("ReplaceProduct.OptionalBadge")}</span>
                );
                return (
                  <>
                    <div
                      style={{
                        ...rpCardStyle,
                        borderColor: targetRegion ? "#bbf7d0" : "#e2e8f0",
                      }}
                    >
                      <span
                        style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}
                      >
                        <span style={rpCardTitleStyle}>
                          {i18n.t("ReplaceProduct.SelectObjectLabel")}
                        </span>
                        <span style={rpCardHintStyle}>
                          {targetRegion ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                color: "#16a34a",
                                fontWeight: 600,
                              }}
                            >
                              <LuCheck size={12} />
                              {i18n.t("ReplaceProduct.RegionSelected")}
                            </span>
                          ) : (
                            i18n.t("ReplaceProduct.SelectObjectHint")
                          )}
                        </span>
                      </span>
                      <span style={rpSegmentStyle}>
                        <button
                          type="button"
                          onClick={() => setRegionSelectorOpen(true)}
                          disabled={!imageUrl || isSubmitting}
                          style={{
                            ...rpSegmentBtnStyle,
                            cursor: !imageUrl || isSubmitting ? "not-allowed" : "pointer",
                          }}
                        >
                          <LuScanLine size={13} />
                          {targetRegion
                            ? i18n.t("ReplaceProduct.RegionSelectedReselect")
                            : i18n.t("ReplaceProduct.SelectObjectButton")}
                        </button>
                        {targetRegion && (
                          <button
                            type="button"
                            onClick={() => setTargetRegion(null)}
                            disabled={isSubmitting}
                            title={i18n.t("Remove")}
                            aria-label={i18n.t("Remove")}
                            style={{ ...rpSegmentGhostStyle, padding: "0 8px", color: "#dc2626" }}
                          >
                            <LuX size={13} />
                          </button>
                        )}
                      </span>
                    </div>

                    <div style={rpFieldStyle}>
                      <span style={rpLabelStyle}>
                        {i18n.t("ReplaceProduct.TargetObjectTitle")}
                        {optionalBadge}
                      </span>
                      <Form.Control
                        type="text"
                        value={targetObject}
                        onChange={(e) => setTargetObject(e.target.value)}
                        disabled={isSubmitting}
                        placeholder={i18n.t("ReplaceProduct.TargetObjectPlaceholder")}
                        title={i18n.t("ReplaceProduct.TargetObjectHint")}
                        style={{ ...rpInputStyle, height: 36 }}
                      />
                      <span style={{ ...rpCardHintStyle, color: "#94a3b8" }}>
                        {i18n.t("ReplaceProduct.TargetObjectHint")}
                      </span>
                    </div>

                    <div
                      style={{
                        ...rpCardStyle,
                        borderColor: adaptStyle ? "#c7d2fe" : "#e2e8f0",
                        transition: "border-color 0.15s",
                      }}
                    >
                      <span
                        style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}
                      >
                        <span style={rpCardTitleStyle}>
                          {i18n.t("ReplaceProduct.AdaptStyleLabel")}
                        </span>
                        <span style={rpCardHintStyle}>
                          {i18n.t("ReplaceProduct.AdaptStyleHint")}
                        </span>
                      </span>
                      <button
                        type="button"
                        className={`app-modal__toggle-switch${
                          adaptStyle ? " app-modal__toggle-switch--on" : ""
                        }`}
                        onClick={() => setAdaptStyle((prev) => !prev)}
                        disabled={isSubmitting}
                        style={{ flex: "none" }}
                      >
                        <span />
                      </button>
                    </div>

                    <div style={rpFieldStyle}>
                      <span style={rpLabelStyle}>
                        {i18n.t("ReplaceProduct.ReferenceImageLabel")}
                      </span>

                      {referenceImageUrl ? (
                        <div
                          style={{
                            ...rpCardStyle,
                            minHeight: rpReferenceMinHeight,
                            boxSizing: "border-box",
                          }}
                        >
                          <span
                            style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}
                          >
                            <img
                              src={referenceImageUrl}
                              alt="reference"
                              onClick={() => setLightboxUrl(referenceImageUrl)}
                              style={{
                                width: 64,
                                height: 64,
                                objectFit: "cover",
                                borderRadius: 8,
                                border: "1px solid #e2e8f0",
                                flexShrink: 0,
                                cursor: "zoom-in",
                              }}
                            />
                            <span
                              style={{
                                ...rpCardTitleStyle,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <LuCheck size={13} style={{ color: "#16a34a", flex: "none" }} />
                              {i18n.t("ReplaceProduct.ReferenceSelected")}
                            </span>
                          </span>
                          <span style={rpSegmentStyle}>
                            <label style={{ ...rpSegmentBtnStyle, color: "#4f46e5" }}>
                              {i18n.t("ReplaceProduct.ChangeUpload")}
                              <input
                                type="file"
                                accept="image/png,image/jpeg"
                                style={{ display: "none" }}
                                disabled={referenceUploading || isSubmitting}
                                onChange={(e) => handleReferenceUpload(e.target.files?.[0])}
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => setReferenceImageUrl("")}
                              disabled={referenceUploading || isSubmitting}
                              style={{ ...rpSegmentGhostStyle, color: "#dc2626" }}
                            >
                              {i18n.t("Remove")}
                            </button>
                          </span>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <label
                            onDragOver={(e) => {
                              e.preventDefault();
                              if (!referenceUploading && !isSubmitting) setRefDragActive(true);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              setRefDragActive(false);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              setRefDragActive(false);
                              if (referenceUploading || isSubmitting) return;
                              const dropped = e.dataTransfer?.files?.[0];
                              if (dropped) handleReferenceUpload(dropped);
                            }}
                            style={{
                              ...rpTileStyle,
                              border: `2px dashed ${refDragActive ? "#a5b4fc" : "#e2e8f0"}`,
                              background: refDragActive ? "#eef2ff" : "#f8fafc",
                              cursor: referenceUploading ? "wait" : "pointer",
                              color: refDragActive ? "#4f46e5" : "#64748b",
                              transition: "border-color 0.15s, background 0.15s",
                            }}
                          >
                            {referenceUploading ? (
                              <>
                                <LuLoaderCircle
                                  size={18}
                                  style={{ animation: "spin 1s linear infinite", color: "#7c3aed" }}
                                />
                                {i18n.t("Uploading")}
                              </>
                            ) : (
                              <>
                                <LuImage size={18} style={{ color: "#7c3aed" }} />
                                {i18n.t("ReplaceProduct.UploadHint")}
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/gif,image/bmp"
                              style={{ display: "none" }}
                              disabled={referenceUploading || isSubmitting}
                              onChange={(e) => handleReferenceUpload(e.target.files?.[0])}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={openLibraryPicker}
                            disabled={referenceUploading || isSubmitting}
                            style={{
                              ...rpTileStyle,
                              border: "2px dashed #e2e8f0",
                              background: "#f8fafc",
                              cursor: referenceUploading || isSubmitting ? "default" : "pointer",
                              color: "#64748b",
                            }}
                          >
                            <LuLibrary size={18} style={{ color: "#7c3aed" }} />
                            {i18n.t("ReplaceProduct.PickFromLibrary")}
                          </button>
                        </div>
                      )}

                      {referenceError && (
                        <span style={{ color: "#dc2626", fontSize: 12 }}>{referenceError}</span>
                      )}
                    </div>

                    <div style={rpFieldStyle}>
                      <span style={rpLabelStyle}>
                        {i18n.t("ReplaceProduct.PromptLabel")}
                        {optionalBadge}
                      </span>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={i18n.t("ReplaceProduct.PromptPlaceholder")}
                        style={{ ...rpInputStyle, resize: "vertical", minHeight: 96 }}
                        disabled={isSubmitting}
                      />
                    </div>
                  </>
                );
              })()}

            {/* replace-background: reference place photo (required) + optional region
          the user draws over the background to swap. Reuses the same
          referenceImageUrl upload + ImageRegionSelector as replace-product. */}
            {selectedFeature.id === "replace-background" && (
              <>
                <Form.Group style={{ marginBottom: "12px" }}>
                  <Form.Label
                    style={{
                      fontWeight: 600,
                      color: "#333",
                      marginBottom: "8px",
                      display: "block",
                      fontSize: 13,
                    }}
                  >
                    {i18n.t("ReplaceBackground.ReferenceImageLabel")}
                  </Form.Label>

                  {referenceImageUrl ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "10px",
                        background: "#f8fafc",
                      }}
                    >
                      <img
                        src={referenceImageUrl}
                        alt="reference"
                        style={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                          {i18n.t("ReplaceBackground.ReferenceSelected")}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <label
                            className="app-modal__btn app-modal__btn--ghost"
                            style={{
                              cursor: "pointer",
                              fontSize: 12,
                              padding: "4px 10px",
                              margin: 0,
                            }}
                          >
                            {i18n.t("ReplaceBackground.ChangeUpload")}
                            <input
                              type="file"
                              accept="image/png,image/jpeg"
                              style={{ display: "none" }}
                              disabled={referenceUploading || isSubmitting}
                              onChange={(e) => handleReferenceUpload(e.target.files?.[0])}
                            />
                          </label>
                          <button
                            type="button"
                            className="app-modal__btn app-modal__btn--ghost"
                            style={{ fontSize: 12, padding: "4px 10px", color: "#dc3545" }}
                            onClick={() => setReferenceImageUrl("")}
                            disabled={referenceUploading || isSubmitting}
                          >
                            {i18n.t("Remove")}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <label
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (!referenceUploading && !isSubmitting) setRefDragActive(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setRefDragActive(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setRefDragActive(false);
                          if (referenceUploading || isSubmitting) return;
                          const dropped = e.dataTransfer?.files?.[0];
                          if (dropped) handleReferenceUpload(dropped);
                        }}
                        style={{
                          flex: "1 1 200px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          minHeight: 96,
                          padding: "16px",
                          border: `2px dashed ${refDragActive ? "#3b82f6" : "#cbd5e1"}`,
                          borderRadius: "10px",
                          background: refDragActive ? "#eff6ff" : "#fff",
                          cursor: referenceUploading ? "wait" : "pointer",
                          color: refDragActive ? "#3b82f6" : "#64748b",
                          fontSize: 12,
                          textAlign: "center",
                          transition: "border-color 0.15s, background 0.15s",
                        }}
                      >
                        {referenceUploading ? (
                          <>
                            <LuLoaderCircle
                              size={20}
                              style={{ animation: "spin 1s linear infinite" }}
                            />
                            {i18n.t("Uploading")}
                          </>
                        ) : (
                          <>
                            <LuImage size={22} />
                            {i18n.t("ReplaceBackground.UploadHint")}
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/bmp"
                          style={{ display: "none" }}
                          disabled={referenceUploading || isSubmitting}
                          onChange={(e) => handleReferenceUpload(e.target.files?.[0])}
                        />
                      </label>
                    </div>
                  )}

                  {referenceError && (
                    <div style={{ marginTop: 8, color: "#dc3545", fontSize: 12 }}>
                      {referenceError}
                    </div>
                  )}
                </Form.Group>

                {/* Optional region: mark part of the background to swap. Omit → whole background. */}
                <Form.Group style={{ marginBottom: "12px" }}>
                  <Form.Label
                    style={{
                      fontWeight: 600,
                      color: "#333",
                      marginBottom: "8px",
                      display: "block",
                      fontSize: 13,
                    }}
                  >
                    {i18n.t("ReplaceBackground.SelectAreaLabel")}
                    <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>
                      ({i18n.t("Optional")})
                    </span>
                  </Form.Label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => setRegionSelectorOpen(true)}
                      disabled={!imageUrl || isSubmitting}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 14px",
                        border: `1.5px solid ${targetRegion ? "#22c55e" : "#e2e8f0"}`,
                        borderRadius: "8px",
                        background: targetRegion ? "#f0fdf4" : "#fff",
                        color: targetRegion ? "#16a34a" : "#475569",
                        cursor: !imageUrl || isSubmitting ? "not-allowed" : "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <LuScanLine size={15} />
                      {targetRegion
                        ? i18n.t("ReplaceBackground.RegionSelectedReselect")
                        : i18n.t("ReplaceBackground.SelectAreaButton")}
                    </button>
                    {targetRegion && (
                      <button
                        type="button"
                        className="app-modal__btn app-modal__btn--ghost"
                        style={{ fontSize: 12, padding: "4px 10px", color: "#dc3545" }}
                        onClick={() => setTargetRegion(null)}
                        disabled={isSubmitting}
                      >
                        {i18n.t("Remove")}
                      </button>
                    )}
                  </div>
                </Form.Group>

                <Form.Group style={{ marginBottom: "12px" }}>
                  <Form.Label style={{ fontWeight: 600, color: "#333", fontSize: 13 }}>
                    {i18n.t("ReplaceBackground.PromptLabel")}
                    <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>
                      ({i18n.t("Optional")})
                    </span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={i18n.t("ReplaceBackground.PromptPlaceholder")}
                    style={{
                      borderRadius: "8px",
                      border: "1.5px solid #e2e8f0",
                      fontSize: "13px",
                      resize: "vertical",
                    }}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              </>
            )}

            {/* remove-object: optional box the user draws over the object to erase.
          Reuses the same ImageRegionSelector + targetRegion as replace-product. */}
            {selectedFeature.id === "remove-object" && (
              <Form.Group style={{ marginBottom: "12px" }}>
                <Form.Label
                  style={{
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "8px",
                    display: "block",
                    fontSize: 13,
                  }}
                >
                  {i18n.t("RemoveObjectSelectAreaLabel")}
                </Form.Label>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => setRegionSelectorOpen(true)}
                    disabled={!imageUrl || isSubmitting}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      border: `1.5px solid ${targetRegion ? "#22c55e" : "#e2e8f0"}`,
                      borderRadius: "8px",
                      background: targetRegion ? "#f0fdf4" : "#fff",
                      color: targetRegion ? "#16a34a" : "#475569",
                      cursor: !imageUrl || isSubmitting ? "not-allowed" : "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <LuScanLine size={15} />
                    {targetRegion
                      ? i18n.t("RemoveObjectAreaReselect")
                      : i18n.t("RemoveObjectSelectAreaButton")}
                  </button>
                  {targetRegion && (
                    <>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: 12,
                          color: "#16a34a",
                        }}
                      >
                        <LuCheck size={14} />
                        {i18n.t("RemoveObjectAreaSelected")}
                      </span>
                      <button
                        type="button"
                        className="app-modal__btn app-modal__btn--ghost"
                        style={{ fontSize: 12, padding: "4px 10px", color: "#dc3545" }}
                        onClick={() => setTargetRegion(null)}
                        disabled={isSubmitting}
                      >
                        {i18n.t("Remove")}
                      </button>
                    </>
                  )}
                </div>
                <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8" }}>
                  {i18n.t("RemoveObjectSelectAreaHint")}
                </div>
              </Form.Group>
            )}

            {/* Prompt — always last. Hidden for the already-animated box: the
              prompt is read-only there and shown as a chip in the summary
              card above instead (see isAlreadyAnimatedBox). For the animate
              feature, the settings toolbar (provider / süre / döngü chips)
              shares this row with the label — right-aligned, wraps under the
              label at narrow/embed widths — instead of its own row above. */}
            {selectedFeature.requiresPrompt &&
              !isAlreadyAnimatedBox &&
              (() => {
                return (
                  <Form.Group style={{ marginBottom: "8px", flex: "none" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Form.Label
                          style={{ fontWeight: 600, color: "#333", fontSize: 13, margin: 0 }}
                        >
                          {i18n.t("Prompt")}
                        </Form.Label>
                        <button
                          type="button"
                          onClick={handleFetchSceneText}
                          disabled={animateInputsLocked || fetchingSceneText}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "#7c3aed",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor:
                              animateInputsLocked || fetchingSceneText ? "default" : "pointer",
                          }}
                        >
                          {fetchingSceneText
                            ? i18n.t("Loading") || "Yükleniyor..."
                            : i18n.t("FetchSceneText") || "Sahne Metnini Getir"}
                        </button>
                      </div>
                      {selectedFeature.id === "animate" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                          }}
                        >
                          <span
                            className="animChip"
                            title={i18n.t("AI Provider")}
                            data-locked={animateInputsLocked || undefined}
                            style={{ ...animChipStyle, opacity: animateInputsLocked ? 0.55 : 1 }}
                          >
                            <LuSparkles size={13} style={{ color: "#7c3aed", flex: "none" }} />
                            {PROVIDER_OPTIONS.find((o) => o.value === provider)?.label || provider}
                            <LuChevronDown size={12} style={{ color: "#94a3b8", flex: "none" }} />
                            <select
                              value={provider}
                              onChange={(e) => setProvider(e.target.value)}
                              disabled={animateInputsLocked}
                              aria-label={i18n.t("AI Provider")}
                              style={{
                                ...chipSelectOverlayStyle,
                                cursor: animateInputsLocked ? "not-allowed" : "pointer",
                              }}
                            >
                              {PROVIDER_OPTIONS.filter((o) => !o.disabled).map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </span>

                          <span aria-hidden="true" style={chipDividerStyle} />

                          <span
                            className="animChip"
                            title={i18n.t("Duration")}
                            data-locked={animateInputsLocked || undefined}
                            style={{ ...animChipStyle, opacity: animateInputsLocked ? 0.55 : 1 }}
                          >
                            <LuTimer size={13} style={{ color: "#0284c7", flex: "none" }} />
                            {duration === null
                              ? i18n.t("Auto")
                              : i18n.t("DurationSecondsShort", { count: duration }) ||
                                `${duration}s`}
                            <LuChevronDown size={12} style={{ color: "#94a3b8", flex: "none" }} />
                            <select
                              value={duration ?? ""}
                              onChange={(e) =>
                                setDuration(e.target.value === "" ? null : Number(e.target.value))
                              }
                              disabled={animateInputsLocked}
                              aria-label={i18n.t("Duration")}
                              style={{
                                ...chipSelectOverlayStyle,
                                cursor: animateInputsLocked ? "not-allowed" : "pointer",
                              }}
                            >
                              <option value="">{i18n.t("Auto")}</option>
                              {supportedDurationsForProvider(provider).map((sec) => (
                                <option key={sec} value={sec}>
                                  {i18n.t("DurationSecondsShort", { count: sec }) || `${sec}s`}
                                </option>
                              ))}
                            </select>
                          </span>

                          <span aria-hidden="true" style={chipDividerStyle} />

                          <span
                            className="animChip"
                            title={i18n.t(loop ? "Loop" : "Play Once")}
                            data-locked={animateInputsLocked || undefined}
                            style={{ ...animChipStyle, opacity: animateInputsLocked ? 0.55 : 1 }}
                          >
                            <LuRepeat
                              size={13}
                              style={{ color: loop ? "#6366f1" : "#94a3b8", flex: "none" }}
                            />
                            <button
                              type="button"
                              role="switch"
                              aria-checked={loop}
                              aria-label={i18n.t(loop ? "Loop" : "Play Once")}
                              className="animLoopSwitch"
                              disabled={animateInputsLocked}
                              onClick={() => {
                                const next = !loop;
                                setLoop(next);
                                localStorage.setItem("animation-loop", String(next));
                              }}
                            />
                          </span>

                          <span aria-hidden="true" style={chipDividerStyle} />

                          <span
                            className="animChip"
                            title={i18n.t("Transparent Background")}
                            data-locked={animateInputsLocked || undefined}
                            style={{ ...animChipStyle, opacity: animateInputsLocked ? 0.55 : 1 }}
                          >
                            <LuScissors
                              size={13}
                              style={{ color: transparent ? "#6366f1" : "#94a3b8", flex: "none" }}
                            />
                            <button
                              type="button"
                              role="switch"
                              aria-checked={transparent}
                              aria-label={i18n.t("Transparent Background")}
                              className="animLoopSwitch"
                              disabled={animateInputsLocked}
                              onClick={() => {
                                const next = !transparent;
                                setTransparent(next);
                                localStorage.setItem("animation-transparent", String(next));
                              }}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={prompt}
                      onChange={(e) => {
                        setPrompt(e.target.value);
                      }}
                      placeholder={i18n.t(
                        selectedFeature.promptPlaceholder || "EnterDescriptionPlaceholder"
                      )}
                      style={{
                        borderRadius: "8px",
                        border: "1.5px solid #e2e8f0",
                        fontSize: "13px",
                        resize: "vertical",
                        // Taller default (was rows=2, ~45px): grows with viewport height
                        // (dialog is calc(100vh - 24px), see styleTag below) up to a cap,
                        // never below 96px. The ORIJINAL/SONUÇ media row below absorbs the
                        // difference via its existing flex-shrink chain (120px pane floor
                        // still holds — see that row's own comment).
                        minHeight: "clamp(96px, 14vh, 130px)",
                      }}
                      disabled={animateInputsLocked}
                    />
                  </Form.Group>
                );
              })()}
          </div>
          {/* /Feature-specific controls */}
        </div>
      )}
    </div>
  );

  const styleTag = (
    <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Geçmiş rayı .modal-content'in içinde (absolute, sol kenar). Başlık/
           gövde/footer genişliği rayın durumundan BAĞIMSIZ, hep aynı:
           min(taban 1000px, kap genişliği − 32px − AÇIK ray). Yani dar ekranda
           içerik baştan açık raya göre boyutlanır → ray açılıp kapanırken
           önizlemeler HİÇ küçülüp büyümez (1024px'te 944↔702px sıçrıyordu) ve
           ray içeriğin üstüne ASLA binmez (kullanıcı kuralı). Bedeli: dar
           ekranda kapalı modal (ortada) daha dar. İçerik SAĞA hizalı; yer
           varken dialog'un sağ kenarı sabit, modal sola büyür; dar ekranda
           sağ kenar gerektiği kadar kayar (aşağıdaki --rail margin kuralı).
           AppModal "Sahneyi gör" ile gövdeyi gizlediğinde (modal-body[hidden])
           kural devre dışı, ray gizlenir. */
        /* Ölçüler ekran (100vw) yerine .modal'ın İÇ genişliğine göre (100cqw):
           AI sohbet paneli yanaşıkken chat.css .modal'a sağdan padding veriyor;
           100vw ile modal sola yapışıyor, sağ kenarı kayıyor, ray içeriği
           örtüyordu. cqw, .modal'ın padding hariç genişliği. */
        .pageModal.imageAnimatorModal--rail {
          container-type: inline-size;
        }
        /* Sağa hizalama margin-left: auto ile (align-self DEĞİL): kapanışta
           .show kalkınca .modal-content blok düzene düşüyor, align-self orada
           çalışmıyor ve içerik rayın altına kayıyordu. flex-shrink: 0 da YOK —
           kolon flex'te yüksekliği kilitleyip uzun formda footer'ı (Başlat)
           modalın dışına itiyordu. */
        .pageModal.imageAnimatorModal--rail .modal-content:not(:has(.modal-body[hidden]))
          > :not(.imgRail) {
          margin-left: auto;
          width: min(var(--img-modal-base-w), 100cqw - 32px - var(--img-rail-open-w));
        }
        /* "Sahneyi gör" (gövde gizli, ray gizli): başlık çubuğu içerik
           genişliğine iner; sağ kenar sabit olduğu için başlık yerinde kalır. */
        .pageModal.imageAnimatorModal--rail:has(.modal-body[hidden])
          .modal-dialog.appModal--constrained {
          max-width: min(var(--img-modal-base-w), 100cqw - 32px - var(--img-rail-open-w)) !important;
        }
        .imageAnimatorModal .modal-content:has(.modal-body[hidden]) > .imgRail {
          display: none !important;
        }

        /* Ray thumbnail listesi — ince, zarif scrollbar (varsayılan geniş gri
           çubuk çirkindi). Firefox + WebKit. */
        .railScroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(148,163,184,0.55) transparent;
        }
        .railScroll::-webkit-scrollbar {
          width: 6px;
        }
        .railScroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .railScroll::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,0.55);
          border-radius: 999px;
        }
        .railScroll::-webkit-scrollbar-thumb:hover {
          background: rgba(100,116,139,0.8);
        }

        /* Geçmiş satırındaki OK normalde GİZLİ; fareyle satır üzerine gelince
           (veya ok klavye-odaklıyken) görünür. ✓ (seçili) bundan etkilenmez. */
        .railRowArrow {
          opacity: 0;
          transition: opacity 0.15s ease;
        }
        .railRow:hover .railRowArrow,
        .railRowArrow:focus-visible {
          opacity: 1;
        }

        /* DEPRECATED */
        @keyframes progressPulse {
          0% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.6;
          }
        }

        .imageAnimatorModal .modal-content {
          border-radius: 16px;
          border: none;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .imageAnimatorModal .modal-body {
          background: #ffffff;
        }

        .imageAnimatorModal .modal-footer {
          background: #f8f9fa;
        }

        /* Tighter vertical chrome — THIS modal only (scoped via the same
           className mechanism the maxWidth prop uses). Stock pageModal spends
           28px top/bottom dialog margin and 16px header/footer paddings;
           at 768px viewports that chrome starves the body. Reclaimed:
           dialog margin 28→12 (x2), header padding 16→10 (x2), footer
           padding 16→10 (x2) = 60px more body height. */
        .imageAnimatorModal .modal-dialog {
          margin: 12px auto;
        }

        /* Geçmiş rayı açılıp kapanırken (--railAnim, ~300ms) modal genişliği
           ve sağ boşluğu rayın width'iyle AYNI süre/eğride değişir. !important
           ŞART: sass/components/_pageModal.scss ".pageModal.fade .modal-dialog"
           transition'ı !important ile transform'a kilitliyor; bu kural olmadan
           max-width anında atlıyor, ray animasyonlu kalıyor ve modal
           kasılıyordu. Giriş kayması (transform) aynen korunur. */
        .pageModal.imageAnimatorModal--railAnim.fade .modal-dialog {
          transition:
            transform 0.3s ease-out,
            max-width 0.25s ease,
            margin-right 0.25s ease !important;
        }

        /* Ray varken dialog sağdan konumlanır: margin-left: auto, margin-right
           JS'te hesaplanan --img-rail-mr (railDialogMr). Kapalıyken modal
           ortada; açıkken sağ kenar sabit, modal sola büyür; dar ekranda
           solda yer bitince sağ kenar gerektiği kadar sağa kayar (geçişli). */
        .pageModal.imageAnimatorModal--rail .modal-dialog {
          margin-left: auto;
          margin-right: var(--img-rail-mr);
        }
        /* Dialog en fazla 100vh → .modal hiç kaymaz; react-bootstrap'in onEnter
           anında (uzun formda) verdiği kaydırma çubuğu payı (paddingLeft ~17px)
           ortalamayı bozuyordu. */
        .pageModal.imageAnimatorModal--rail {
          padding-left: 0 !important;
        }
        /* Ray sol kenarı kaplıyor: modal başlığı/footer'ının sol yuvarlatması
           rayın yanında beyaz bir çentik bırakıyordu. */
        .pageModal.imageAnimatorModal--rail .modal-footer {
          border-bottom-left-radius: 0;
        }
        .pageModal.imageAnimatorModal--rail .app-modal__header {
          border-top-left-radius: 0;
        }

        .pageModal.imageAnimatorModal .app-modal__header {
          padding: 10px 16px;
        }

        .imageAnimatorModal .modal-footer.modal-footer {
          padding: 10px 16px;
        }

        /* Structural height chain — NO magic "chrome height" constant. The
           dialog is capped to viewport minus its own margin (12px above);
           everything below is real flexbox distributing whatever's left, so
           header/footer growing or shrinking can never desync a hardcoded
           number again.
           sass/components/_pageModal.scss forces ".modal.show" descendants
           (.modal-dialog/.modal-content/.modal-body) back to
           "display: block !important" (a global compatibility rule, out of
           scope here) — ".pageModal.imageAnimatorModal.fade" is 3 classes on
           the same ancestor element (the "fade" class react-bootstrap keeps
           through enter AND exit) + 1 for the descendant = higher specificity
           than that rule's 3, so !important here reliably wins. Forcing
           display back to flex only changes how each element lays out its
           own children; it does not change how each is SIZED by its own
           parent, so .modal-body can still be a flex item with a definite
           computed height either way — the actual flex column for the
           modal's own children is bodyContent's inner wrapper div (inline
           style, in the JSX below), same as before. react-bootstrap wraps
           Modal.Body/Modal.Footer each in an unclassed div (visibility
           toggle) — targeted here via :has(). */
        /* :has(.modal-body:not([hidden])) gates all four rules below to the
           EXPANDED state only. AppModal's chevron ("Sahneyi gör") collapses
           by putting a hidden attribute on Modal.Body (+ display:none inline
           on its wrapper div) — without this gate our !importants above
           would keep forcing the wrapper/dialog/content to a full-viewport
           flex column even while collapsed, so the wrapper's inline
           display:none loses to our !important and the user sees a
           full-height empty white modal instead of the dialog shrinking to
           just the header. */
        /* Dikey ortalama + içerik kadar yükseklik.
           Dialog viewport yüksekliğini KAPLIYOR (min-height) ama kendisi bir
           şey çizmiyor; tek çocuğu .modal-content'i justify-content: center ile
           dikeyde ortalıyor. Kısa modallar (prompt'u olmayan feature'lar) artık
           tepeye yapışıp altta boşluk bırakmıyor.
           Yükseklik sınırı bu yüzden dialog'dan CONTENT'e taşındı: dialog'un
           kendisi tam boy, sınırlanması gereken çizilen kutu.
           flex: 0 1 auto — content büyümüyor (yoksa ortalama anlamsız, tam boya
           yayılırdı), ama gerektiğinde küçülüyor; max-height'e dayandığında
           içerideki form bölgesi kaydırılıyor.
           Bootstrap'in centered prop'u KULLANILMADI: o .modal-dialog-centered
           ile display:flex + align-items:center veriyor, buradaki
           flex-direction: column zorunluluğuyla (modal-content'in kolon çocuğu
           olarak boyutlanması için gerekli) çatışıyor. */
        /* .show yerine .fade: kapanışta react-bootstrap .show'u HEMEN
           kaldırıyor, sönme sürerken ortalama düşüp modal tepeye sıçrıyordu.
           .fade açılış/kapanış boyunca durur; özgüllük .show ile aynı. */
        .pageModal.imageAnimatorModal--exiting.fade .modal-dialog {
          transform: none !important;
        }

        .pageModal.imageAnimatorModal.fade:has(.modal-body:not([hidden])) .modal-dialog {
          min-height: calc(100vh - 24px);
          display: flex !important;
          flex-direction: column;
          justify-content: center;
        }

        .pageModal.imageAnimatorModal.fade:has(.modal-body:not([hidden])) .modal-content {
          max-height: calc(100vh - 24px);
          flex: 0 1 auto;
          min-height: 0;
          display: flex !important;
          flex-direction: column;
          overflow: hidden;
        }

        .imageAnimatorModal .modal-content > div:has(> .modal-body:not([hidden])) {
          flex: 1 1 auto;
          min-height: 0;
          /* !important: this wrapper div (AppModal's Sahneyi-gör visibility
             toggle) gets "display: block" as an INLINE style, which beats any
             external rule regardless of specificity unless we go !important
             too. */
          display: flex !important;
          flex-direction: column;
        }

        .pageModal.imageAnimatorModal.fade .modal-body:not([hidden]) {
          flex: 1 1 auto;
          min-height: 0;
          display: block !important;
        }

        .imageAnimatorModal .modal-content > div:has(> .modal-footer) {
          flex: none;
        }


        .imageAnimatorPanel {
          background: #fff;
          border: 1px solid #e9ecef;
          border-radius: 12px;
          padding: 20px;
          margin-top: 12px;
        }

        .imageAnimatorPanel__footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          padding: 16px 0 0;
          border-top: 1px solid #e9ecef;
          margin-top: 16px;
        }

        /* Compact animate settings toolbar chips (ghost) — hover/focus
           states. Locked chips (data-locked) keep their resting look. */
        .animChip:hover:not([data-locked]):not(:disabled) {
          background: #f1f5f9;
        }
        .animChip:focus-within:not([data-locked]) {
          background: #f1f5f9;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        /* Döngü / Bir Kez Oynat pill switch (role=switch) — 32x18, thumb
           slides on toggle. Matches the modal's indigo accent when on. */
        .animLoopSwitch {
          width: 32px;
          height: 18px;
          border-radius: 9px;
          border: none;
          padding: 0;
          background: #d1d5db;
          position: relative;
          flex: none;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }
        .animLoopSwitch[aria-checked="true"] {
          background: #6366f1;
        }
        .animLoopSwitch::after {
          content: "";
          position: absolute;
          top: 2px;
          left: 2px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #fff;
          transition: left 0.15s ease;
        }
        .animLoopSwitch[aria-checked="true"]::after {
          left: 16px;
        }
        .animLoopSwitch:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
        .animLoopSwitch:focus-visible {
          outline: 2px solid #6366f1;
          outline-offset: 2px;
        }
      `}</style>
  );

  // Fullscreen overlay'lar (bölge/metin seçiciler + lightbox) modal kabuğunun
  // DIŞINDA durur ki onun üstüne boyanabilsinler. embed modunda da render
  // edilmeleri şart: aksi halde "Görselden obje seç" / "Metni seç" state'i set
  // eder ama ekranda hiçbir şey açılmaz (görsel ekle sekmesi embed ile mount eder).
  const overlays = (
    <>
      <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      {textSelectEntryIdx !== null && imageUrl && (
        <ImageTextSelector
          imageUrl={imageUrl}
          onClose={() => setTextSelectEntryIdx(null)}
          onExtract={(text) => {
            setTextChanges((prev) => {
              const u = [...prev];
              if (u[textSelectEntryIdx]) {
                u[textSelectEntryIdx] = { ...u[textSelectEntryIdx], originalText: text };
              }
              return u;
            });
            setTextSelectEntryIdx(null);
          }}
        />
      )}

      {targetTextSelectIdx !== null && imageUrl && (
        <ImageTextSelector
          imageUrl={imageUrl}
          onClose={() => setTargetTextSelectIdx(null)}
          onExtract={(text) => {
            setTargetTexts((prev) => {
              const u = [...prev];
              // Only the text is picked from the image — keep whatever
              // location the user already chose for this row.
              if (targetTextSelectIdx < u.length) {
                u[targetTextSelectIdx] = { ...u[targetTextSelectIdx], text };
              }
              return u;
            });
            setTargetTextSelectIdx(null);
          }}
        />
      )}

      {regionSelectorOpen && imageUrl && (
        <ImageRegionSelector
          imageUrl={imageUrl}
          onClose={() => setRegionSelectorOpen(false)}
          onSelect={(region) => {
            setTargetRegion(region);
            setRegionSelectorOpen(false);
          }}
        />
      )}
    </>
  );

  // Kütüphaneden referans görsel seçme görünümü. bodyContent içindeki
  // "Kütüphaneden seç" butonu (ReplaceProduct.PickFromLibrary) her iki modda da
  // görünür olduğu için bu görünüm de her iki modda render edilmeli — yoksa
  // embed modunda butona basınca pickingLibrary true olur ama hiçbir şey açılmaz.
  const libraryPickerView = (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
          {i18n.t("ReplaceProduct.PickFromLibraryTitle")}
        </span>
        <button
          type="button"
          className="app-modal__btn app-modal__btn--ghost"
          style={{ fontSize: 12, padding: "4px 10px" }}
          onClick={() => setPickingLibrary(false)}
        >
          {i18n.t("Cancel")}
        </button>
      </div>
      {libraryLoading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: 40,
            color: "#64748b",
            fontSize: 13,
          }}
        >
          <LuLoaderCircle size={20} style={{ animation: "spin 1s linear infinite" }} />
          {i18n.t("Loading")}
        </div>
      ) : libraryError ? (
        <div style={{ padding: 24, color: "#dc3545", fontSize: 13, textAlign: "center" }}>
          {libraryError}
        </div>
      ) : libraryFiles.length === 0 ? (
        <div style={{ padding: 24, color: "#64748b", fontSize: 13, textAlign: "center" }}>
          {i18n.t("NoResults", { defaultValue: "Görsel bulunamadı" })}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: 10,
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {libraryFiles.map((f, idx) => (
            <button
              key={f.url + idx}
              type="button"
              title={f.name}
              onClick={() => {
                setReferenceImageUrl(f.url);
                setReferenceError(null);
                setPickingLibrary(false);
              }}
              style={{
                padding: 0,
                border: "1.5px solid #e2e8f0",
                borderRadius: 8,
                overflow: "hidden",
                background: "#f8fafc",
                cursor: "pointer",
                aspectRatio: "1 / 1",
              }}
            >
              <img
                src={f.url}
                alt={f.name || "library"}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (embed) {
    return (
      <>
        <div className="imageAnimatorPanel">
          {pickingLibrary ? libraryPickerView : bodyContent}
          {/* Footer, modal dalıyla aynı kuralla gizlenir: picker açıkken gövde
              tamamen başka bir şey gösteriyor, altında "Üret" durması yanlış olur
              (bkz. modal dalındaki footer koşulu). Ray artık gövdeyi kaplamıyor,
              yanında duruyor → versionsOpen footer'ı gizlemez. */}
          {!(
            showHistory ||
            pickingReference ||
            pickingLibrary ||
            pickingExtendLibrary ||
            pickingExtendScene
          ) && (
            <div className="imageAnimatorPanel__footer">
              {modalMode === "edit" ? editFooterContent : footerContent}
            </div>
          )}
        </div>
        {styleTag}
        {overlays}
      </>
    );
  }

  return (
    <>
      <AppModal
        show={visible}
        onClose={handleClose}
        className={`imageAnimatorModal${railShown ? " imageAnimatorModal--rail" : ""}${
          railAnimating ? " imageAnimatorModal--railAnim" : ""
        }${exiting ? " imageAnimatorModal--exiting" : ""}`}
        onExit={() => setExiting(true)}
        onExited={() => setExiting(false)}
        contentClassName={railContentClass}
        size="lg"
        // Modest widen over the 900px .modal-lg default (owner directive) —
        // scoped to THIS modal via AppModal's maxWidth prop (sets
        // --app-modal-max-width + .appModal--constrained on the dialog).
        maxWidth={railW ? (railOpen ? railDialogOpenW : railDialogClosedW) : MODAL_BASE_W}
        // Ray/modal ölçüleri CSS'e: içerik sabit genişliği + sağa sabit dialog
        // margin'i (styleTag) bu değişkenlerden hesaplanır — JS sabitleriyle tek kaynak.
        style={{
          "--img-rail-open-w": `${RAIL_OPEN_W}px`,
          "--img-rail-mr": railDialogMr,
          "--img-modal-base-w": `${MODAL_BASE_W}px`,
        }}
        title={(() => {
          const FeatureIcon = selectedFeature.icon;
          return (
            <>
              {/* The title is the SELECTED FEATURE's own name ("Metin Çevir",
                  "Görseli Canlandır", …) — the same i18n key the Aidea Wizard
                  panel renders for the card that was clicked (see
                  MerlinFeaturesPanel, which maps over this file's
                  IMAGE_EDIT_FEATURES). It used to be a fixed
                  i18n.t("Image Animator") with the feature name demoted to a
                  small grey label, which made every feature except the animator
                  look like it had opened the wrong modal. */}
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FeatureIcon size={16} />
                {i18n.t(selectedFeature.name || "Image Animator")}
                {selectedFeature.description && (
                  <LuInfo
                    size={13}
                    style={{ cursor: "help", opacity: 0.7 }}
                    title={i18n.t(selectedFeature.description)}
                  />
                )}
              </span>
              {(() => {
                const effectiveModelLabel =
                  selectedFeature.id === "remove-text" && editMode === "region"
                    ? "gemini"
                    : selectedFeature.modelLabel;
                return effectiveModelLabel ? (
                  <span
                    style={{
                      marginLeft: 8,
                      padding: "2px 6px",
                      fontSize: 10,
                      fontWeight: 500,
                      color: "#94a3b8",
                      background: "#f1f5f9",
                      border: "1px solid #e2e8f0",
                      borderRadius: 4,
                      letterSpacing: 0.2,
                      textTransform: "lowercase",
                      verticalAlign: "middle",
                    }}
                    title={`AI model: ${effectiveModelLabel}`}
                  >
                    {effectiveModelLabel}
                  </span>
                ) : null;
              })()}
            </>
          );
        })()}
        headerActions={
          <>
            {/* Versiyon/Geçmiş TETİKLEYİCİSİ artık header'da DEĞİL: modalın
                solundaki kalıcı versiyon rayına taşındı (kullanıcı isteği: "üste
                tıkladığımda gelmeyecek, solda gözükecek"). Ray kendi katla okuyla
                genişler/daralır. */}
            {!isVideoSource && (
              <button
                type="button"
                onClick={handleOpenChatEdit}
                title={i18n.t("ImageChatEdit.ButtonTooltip", {
                  defaultValue: "Chatbot ile düzenle",
                })}
                className="app-modal__toggle-btn"
              >
                <LuMessagesSquare size={16} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              title={i18n.t("ImageHistory.Tab") || "Etkinlik Günlüğü"}
              className={`app-modal__toggle-btn${showHistory ? " app-modal__toggle-btn--active" : ""}`}
            >
              <LuScrollText size={16} />
            </button>
          </>
        }
        // No hardcoded "chrome height" here — the scoped styleTag rules bound
        // .modal-body via real flexbox (dialog MAX-height: calc(100vh - margin)
        // → modal-content flex column → .modal-body flex: 1 1 auto), so it can
        // never desync from the header/footer's actual rendered size.
        // Dialog artık sabit yükseklikte DEĞİL: içerik kısa olduğunda modal da
        // kısa kalıyor (eskiden altta boş beyaz alan kalıyordu). Bu yüzden
        // aşağıdaki flexGrow artık "kalan yeri doldur" işini yapmıyor — sabit
        // ebatlı önizleme kutuları (paneBoxSizing) yüksekliği kendisi belirliyor.
        // Root cause (proven by rendered-measurement harness against the REAL
        // AppModal + sass output, not reasoning): react-bootstrap's
        // Modal.Body always carries the base `.modal-body` class, and
        // sass/components/_pageModal.scss has a global
        // `.modal.show .modal-body { display: block !important; }` rule (it
        // exists to force old modals visible) that overrides any
        // `display: flex` on modal-body's OWN children — so the actual flex
        // column for this modal's content is `bodyContent`'s inner wrapper
        // div (inline style below, a plain div the !important rule doesn't
        // match), not modal-body itself. `display: block !important` doesn't
        // stop modal-body from still being sized as a flex ITEM by its own
        // parent (that's a different element/rule), which is all the styleTag
        // needs. Inside bodyContent's flex column, the fixed pieces (Geçmiş
        // accordion, settings card, prompt, summary card — all flex: none)
        // keep their natural height and the flexible media region (Mode B:
        // AnimationEditMode's wrapper, flex: 1, minHeight: 0; Mode A: the
        // ORIJINAL/SONUÇ pane row, flex: 1 1 auto with 120px pane minimums)
        // gets exactly the remaining space — that's what lets the preview
        // media shrink instead of the controls below it clipping.
        // flexGrow toggles whether modal-body STRETCHES to the dialog's full
        // available height (a mode's own content showing) or sizes to its
        // own (short) content instead — the Geçmiş history panel and the
        // picker/history swaps keep flexGrow: 0 so they don't gain dead space
        // below them; flexShrink stays 1 either way so nothing can ever push
        // past the dialog's viewport-bounded height.
        bodyProps={{
          style: {
            padding: "16px 20px",
            overflow: "hidden",
            // Ray artık gövdeyi kaplamıyor (yanında duruyor) → versionsOpen
            // flexGrow'u etkilemez. Yalnız history/picker gövdeyi tamamen
            // değiştirdiğinde (kendi kısa içeriğine sığsın diye) 0.
            flexGrow:
              modalMode === "edit"
                ? 1
                : !showHistory && !pickingReference && !pickingLibrary
                  ? 1
                  : 0,
          },
        }}
        footer={
          showHistory ||
          pickingReference ||
          pickingLibrary ||
          pickingExtendLibrary ||
          pickingExtendScene
            ? null
            : modalMode === "edit"
              ? editFooterContent
              : footerContent
        }
        footerProps={{ style: { display: "flex", gap: "12px" } }}
      >
        {returnTo && (
          <button
            type="button"
            onClick={handleBackToPanel}
            className="app-modal__btn app-modal__btn--ghost"
            style={{
              marginBottom: 12,
              padding: "6px 12px",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <LuArrowLeft size={14} />
            {i18n.t("AnimatorPanel.BackToPanel", { defaultValue: "Back" })}
          </button>
        )}
        {modalMode === "edit" && pickingExtendLibrary ? (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                {i18n.t("AnimEdit.PickFromLibrary")}
              </span>
              <button
                type="button"
                className="app-modal__btn app-modal__btn--ghost"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={() => setPickingExtendLibrary(false)}
              >
                {i18n.t("Cancel")}
              </button>
            </div>
            {extendLibraryLoading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: 40,
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                <LuLoaderCircle size={20} style={{ animation: "spin 1s linear infinite" }} />
                {i18n.t("Loading")}
              </div>
            ) : extendLibraryError ? (
              <div style={{ padding: 24, color: "#dc3545", fontSize: 13, textAlign: "center" }}>
                {extendLibraryError}
              </div>
            ) : extendLibraryFiles.length === 0 ? (
              <div style={{ padding: 24, color: "#64748b", fontSize: 13, textAlign: "center" }}>
                {i18n.t("NoResults", { defaultValue: "Görsel bulunamadı" })}
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 10,
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                {extendLibraryFiles.map((f, idx) => (
                  <button
                    key={f.url + idx}
                    type="button"
                    title={f.name}
                    onClick={() => {
                      setReferenceImage({ url: f.url, name: f.name });
                      setExtendReferenceError(null);
                      setPickingExtendLibrary(false);
                    }}
                    style={{
                      padding: 0,
                      border: "1.5px solid #e2e8f0",
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "#f8fafc",
                      cursor: "pointer",
                      aspectRatio: "1 / 1",
                    }}
                  >
                    <img
                      src={f.url}
                      alt={f.name || "library"}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : modalMode === "edit" && pickingExtendScene ? (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                {i18n.t("AnimEdit.PickFromScene")}
              </span>
              <button
                type="button"
                className="app-modal__btn app-modal__btn--ghost"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={() => setPickingExtendScene(false)}
              >
                {i18n.t("Cancel")}
              </button>
            </div>
            {extendSceneImages.length === 0 ? (
              <div style={{ padding: 24, color: "#64748b", fontSize: 13, textAlign: "center" }}>
                {i18n.t("NoResults", { defaultValue: "Görsel bulunamadı" })}
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: 10,
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                {extendSceneImages.map((f, idx) => (
                  <button
                    key={f.url + idx}
                    type="button"
                    title={f.name}
                    onClick={() => {
                      setReferenceImage({ url: f.url, name: f.name });
                      setExtendReferenceError(null);
                      setPickingExtendScene(false);
                    }}
                    style={{
                      padding: 0,
                      border: "1.5px solid #e2e8f0",
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "#f8fafc",
                      cursor: "pointer",
                      aspectRatio: "1 / 1",
                    }}
                  >
                    <img
                      src={f.url}
                      alt={f.name || "scene"}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : pickingLibrary ? (
          libraryPickerView
        ) : pickingReference ? (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                {i18n.t("ReplaceProduct.PickReferenceTitle")}
              </span>
              <button
                type="button"
                className="app-modal__btn app-modal__btn--ghost"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={() => setPickingReference(false)}
              >
                {i18n.t("Cancel")}
              </button>
            </div>
            <HistoryTab
              mediaType="image"
              jobTypes={["image-animation", "image-editing"]}
              showName
              compactPrompt
              onAddToScene={(url) => {
                if (url) {
                  setReferenceImageUrl(url);
                  setReferenceError(null);
                }
                setPickingReference(false);
              }}
            />
          </div>
        ) : showHistory ? (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                className="app-modal__btn app-modal__btn--ghost"
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <LuArrowLeft size={13} />
                {i18n.t("AnimEdit.BackButton", { defaultValue: "Back" })}
              </button>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
                {i18n.t("AnimEdit.ActivityLogTitle", { defaultValue: "Activity Log" })}
              </span>
            </div>
            <HistoryTab
              mediaType="image"
              jobTypes={["image-animation", "image-editing"]}
              showName
              hideAddButton
              compactPrompt
            />
          </div>
        ) : (
          bodyContent
        )}
      </AppModal>
      {styleTag}
      {overlays}
    </>
  );
}
